// src/App.js - ProStream Transcoder Advanced UI (Enhanced v5.1)
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import "./App.css";

import HlsPlayer from "./components/HlsPlayer";
import VodPlayer from "./components/VodPlayer";

import prostreamLogo from "./ms.png";

// Import icons from react-feather - only use verified ones
import {
  Lock,
  Activity,
  List,
  Circle,
  Video,
  RefreshCw,
  X,
  Radio,
  Monitor,
  Square,
  Play,
  Plus,
  Trash2,
  Database,
  Save,
  Repeat,
  HardDrive,
  Cpu,
  Layers,
  Globe,
  ChevronUp,
  ChevronDown,
  Server,
  Clock,
  Upload,
  Edit3,
  Facebook,
  Youtube,
  Link2,
  Pause,
  RotateCcw,
  Film,
  Menu,
} from 'react-feather';
import { Search } from "lucide-react";


/** =========================
 * Constants & Defaults
 * ========================= */
const DEFAULT_CHANNELS = Array.from({ length: 10 }, (_, i) => `ch${i + 1}`);

const LOGO_OPTIONS = [
  "fwlive.png", "dp.png", "dpw.png", "Fw-small.png", 
  "gurbani.png", "kbdlive.png", "newslogo.png", "news.png",
];

const VIDEO_CODECS = ["h264_nvenc", "libx264", "mpeg2video", "copy"];
const AUDIO_CODECS = ["aac", "mp3", "libopus", "copy"];
const GPU_INDICES = ["0", "1", "2"];
const OVERLAY_CANVAS_WIDTH = 1920;
const OVERLAY_CANVAS_HEIGHT = 1080;

const FONT_OPTIONS = [
  "/home/FTP/loop/fonts/Arial.ttf",
  "/home/FTP/loop/fonts/NotoSans-Regular.ttf",
  "/home/FTP/loop/fonts/Roboto-Regular.ttf",
  "/home/FTP/loop/fonts/FreeSans.ttf",
  "/home/FTP/loop/fonts/LiberationSans-Regular.ttf",
];

// Removed unused UDP_PROFILES constant
// const UDP_PROFILES = [
//   { value: "mpeg2", label: "MPEG-2 (Cable SD 720x576)" },
//   { value: "mpeg4", label: "MPEG-4 (HD 1920×1080)" }
// ];

const DEFAULTS = {};
DEFAULT_CHANNELS.forEach((ch) => {
  DEFAULTS[ch] = {
    channel_name: `Channel-${String(ch).replace("ch", "")}`,
    dashboard_mode: "encoder",
    udp_profile: "mpeg2",
    udp_muxrate: "7000k",
    udp_rate_control: "cbr",
    udp_video_bitrate: "4000k",
    udp_audio_bitrate: "192k",
    udp_minrate: "4000k",
    udp_maxrate: "4000k",
    udp_video_bufsize: "8000k",
    udp_gop_size: "12",
    udp_keyint_min: "12",
    udp_bframes: "2",
    udp_service_name: "Hazoor Sahib",
    udp_service_provider: "MSLiveBox",
    udp_service_id: "1",
    udp_transport_stream_id: "1",
    udp_original_network_id: "1",
    udp_pmt_pid: "4096",
    udp_start_pid: "256",
    udp_video_pid: "256",
    udp_audio_pid: "257",
    udp_pcr_period: "20",
    udp_pat_period: "0.1",
    udp_sdt_period: "0.5",
    udp_pkt_size: "1316",
    udp_ttl: "255",
    udp_buffer_size: "9388608",
    udp_fifo_size: "1000000",
    udp_system_b: false,
    udp_resend_headers: true,
    inputType: "file",
    mode: "server",
    input: "",
    mark_in: "",
    mark_out: "",
    gpu_index: "0",
    video_codec: VIDEO_CODECS[0],
    audio_codec: AUDIO_CODECS[0],
    video_bitrate: "5000k",
    audio_bitrate: "192k",
    encoder_bufsize: "10000k",
    encoder_gop_size: "50",
    encoder_keyint_min: "50",
    encoder_bframes: "0",
    overlay_logo: "",
    overlay_logo_enabled: true,
    logo_x: 1700,
    logo_y: 75,
    logo_width: 0,
    logo_height: 0,
    additional_logos: [],
    facebook_key: "",
    youtube_key: "",
    custom_url: "",
    scroll_text: "",
    scroll_font: FONT_OPTIONS[0],
    scroll_font_size: 48,
    scroll_font_color: "white",
    scroll_bg_color: "black",
    scroll_enable_bg: true,
    scroll_bg_alpha: 0.65,
    scroll_speed: 40,
    scroll_x: 0,
    scroll_y: 1030,
    loop_segment: false,
    youtube_source_kind: "live",
    youtube_anchor_seconds: 0,
    youtube_duration: 0,
    youtube_title: "",
    fallback_enabled: false,
    fallback_media: "",
    save_state: true, // New: Auto-save state
  };
});

const SELECTED_CHANNEL_STORAGE_KEY = "loop_selected_channel";
const FORM_CACHE_STORAGE_KEY = "loop_form_cache_v2";
const STATUS_CACHE_STORAGE_KEY = "loop_status_cache_v2";
const HEALTH_CACHE_STORAGE_KEY = "loop_health_cache_v2";
const LICENSE_CACHE_STORAGE_KEY = "loop_license_cache_v2";
const LICENSE_WATCHDOG_MS = 24 * 60 * 60 * 1000;

function buildDefaultFormState() {
  const next = {};
  DEFAULT_CHANNELS.forEach((ch) => {
    next[ch] = { ...DEFAULTS[ch] };
  });
  return next;
}

function readStoredJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function writeStoredJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function mergeFormState(savedForm) {
  const merged = buildDefaultFormState();
  if (!savedForm || typeof savedForm !== "object") return merged;
  DEFAULT_CHANNELS.forEach((ch) => {
    if (savedForm[ch] && typeof savedForm[ch] === "object") {
      merged[ch] = { ...merged[ch], ...savedForm[ch] };
    }
  });
  return merged;
}

/* --- UI Helper Functions --- */
function fmtLabel(ch) {
  return `CH${String(ch).replace("ch", "")}`;
}
function defaultChannelName(ch) {
  return `Channel-${String(ch).replace("ch", "")}`;
}
function safeBool(v) {
  return !!v;
}

function formatBytes(bytes, decimals = 2) {
  if (bytes == null || Number.isNaN(bytes)) return "--";
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

function playlistItemLabel(item) {
  if (!item) return "";
  if (item.title) return item.title;
  const path = item.path || "";
  if (/^(https?|rtmp|rtmps|udp|srt|rtsp):\/\//i.test(path) || /youtube\.com|youtu\.be/i.test(path)) return path;
  return path.split('/').pop();
}

function isRestreamInput(value) {
  const input = String(value || "").trim();
  return /^(https?|rtmp|rtmps|udp|srt|rtsp):\/\//i.test(input) || /youtube\.com|youtu\.be/i.test(input);
}

function playlistItemProtocol(item) {
  const protocol = String(item?.protocol || (item?.is_remote ? "url" : "file")).toUpperCase();
  if (protocol === "YOUTUBE") return "YOUTUBE";
  if (protocol === "FILE") return "FILE";
  return protocol;
}

function playlistItemTone(item) {
  switch (playlistItemProtocol(item)) {
    case "FILE":
      return "text-slate-300 bg-slate-500/10 border-slate-400/20";
    case "YOUTUBE":
      return "text-rose-300 bg-rose-500/10 border-rose-400/20";
    case "SRT":
      return "text-amber-300 bg-amber-500/10 border-amber-400/20";
    case "UDP":
      return "text-cyan-300 bg-cyan-500/10 border-cyan-400/20";
    case "RTMP":
    case "RTMPS":
      return "text-blue-300 bg-blue-500/10 border-blue-400/20";
    default:
      return "text-violet-300 bg-violet-500/10 border-violet-400/20";
  }
}

function canTrimPlaylistItem(item) {
  return !!item && !item.is_remote && playlistItemProtocol(item) === "FILE";
}

function formatLicenseDateTime(value) {
  if (!value) return "--";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const dd = String(d.getDate()).padStart(2, "0");
  const mon = months[d.getMonth()];
  const yyyy = d.getFullYear();
  let hh = d.getHours();
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ampm = hh >= 12 ? "PM" : "AM";
  hh = hh % 12 || 12;
  return `${dd}-${mon}-${yyyy} ${String(hh).padStart(2, "0")}:${mm}-${ampm}`;
}

function formatClockFromSeconds(seconds = 0) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = String(Math.floor(total / 3600)).padStart(2, "0");
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

function parseClockToSeconds(value) {
  if (!value) return 0;
  const parts = String(value).split(":").map((part) => Number(part) || 0);
  if (parts.length === 3) return (parts[0] * 3600) + (parts[1] * 60) + parts[2];
  if (parts.length === 2) return (parts[0] * 60) + parts[1];
  return parts[0] || 0;
}

function parsePlaylistDuration(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return 0;
  const seconds = parseClockToSeconds(raw);
  return Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : 0;
}

function formatDurationSeconds(value, emptyLabel = "LIVE") {
  const seconds = Number(value);
  return Number.isFinite(seconds) && seconds > 0 ? formatClockFromSeconds(seconds) : emptyLabel;
}

function clampPercent(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.max(0, Math.min(100, num));
}

function parsePosition(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function parseBitrateKbps(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return null;
  const match = raw.match(/^(\d+)([km])?$/);
  if (!match) return null;
  const num = Number(match[1]);
  if (!Number.isFinite(num) || num <= 0) return null;
  const unit = match[2] || "k";
  if (unit === "m") return num * 1000;
  return num;
}

function toKbpsLabel(kbps) {
  const value = Number(kbps);
  if (!Number.isFinite(value) || value <= 0) return "";
  return `${Math.round(value)}k`;
}

function linkUdpBitrates(prevChannel, key, val) {
  const next = { ...prevChannel, [key]: val };
  if (!["udp_video_bitrate", "udp_minrate", "udp_maxrate", "udp_rate_control"].includes(key)) {
    return next;
  }

  let video = parseBitrateKbps(next.udp_video_bitrate);
  let minr = parseBitrateKbps(next.udp_minrate);
  let maxr = parseBitrateKbps(next.udp_maxrate);
  const isCbr = String(next.udp_rate_control || "cbr").toLowerCase() === "cbr";

  if (isCbr) {
    // In CBR mode keep all three fields locked together.
    const anchor =
      (key === "udp_video_bitrate" ? parseBitrateKbps(val) : null) ||
      (key === "udp_minrate" ? parseBitrateKbps(val) : null) ||
      (key === "udp_maxrate" ? parseBitrateKbps(val) : null) ||
      video || minr || maxr;
    if (anchor) {
      next.udp_video_bitrate = toKbpsLabel(anchor);
      next.udp_minrate = toKbpsLabel(anchor);
      next.udp_maxrate = toKbpsLabel(anchor);
    }
    return next;
  }

  // VBR invariant: minrate <= video_bitrate <= maxrate
  if (video == null && (minr != null || maxr != null)) {
    video = minr != null ? minr : maxr;
  }
  if (minr == null && video != null) minr = video;
  if (maxr == null && video != null) maxr = video;
  if (minr != null && maxr != null && minr > maxr) {
    if (key === "udp_minrate") maxr = minr;
    else minr = maxr;
  }
  if (video != null && minr != null && video < minr) video = minr;
  if (video != null && maxr != null && video > maxr) video = maxr;

  if (video != null) next.udp_video_bitrate = toKbpsLabel(video);
  if (minr != null) next.udp_minrate = toKbpsLabel(minr);
  if (maxr != null) next.udp_maxrate = toKbpsLabel(maxr);
  return next;
}

/** Given an array of playlist items and a wall-clock start time string (HH:MM:SS),
 *  returns an array of calculated start-time strings for each clip. */
function computePlaylistStartTimes(items, startClockStr) {
  let acc = parseClockToSeconds(startClockStr || "00:00:00");
  return (items || []).map((item) => {
    const st = item?.day_start || item?.start_clock || formatClockFromSeconds(acc);
    const durStr = item?.duration_str;
    if (durStr && durStr !== "--:--:--") {
      acc += parseClockToSeconds(durStr) || 0;
    }
    return st;
  });
}

function logoFileLabel(path) {
  if (!path) return "Disabled";
  return String(path).split("/").pop() || String(path);
}

function logoPreviewSrc(path) {
  if (!path) return "";
  return `/logo/${encodeURIComponent(logoFileLabel(path))}`;
}

function fallbackFileLabel(item) {
  if (!item) return "";
  const name = item.name || String(item.path || "").split("/").pop() || "";
  if (!name) return "";
  const details = [];
  if (item.type) details.push(item.type);
  if (item.duration_str && item.duration_str !== "00:00:00") details.push(item.duration_str);
  return details.length ? `${name} (${details.join(", ")})` : name;
}

// Removed unused formatTime function
// function formatTime(seconds) {
//   if (!seconds) return "00:00:00";
//   const h = Math.floor(seconds / 3600);
//   const m = Math.floor((seconds % 3600) / 60);
//   const s = Math.floor(seconds % 60);
//   return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
// }

// Professional UI Styles
const UI = {
  bg: "bg-gradient-to-br from-gray-950 via-black to-gray-950 text-gray-100",
  card: "bg-gray-900/80 backdrop-blur-xl border border-gray-800 rounded-2xl shadow-2xl overflow-hidden",
  input: "w-full bg-gray-900/50 border border-gray-700 rounded-xl py-3 px-4 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200",
  label: "text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-2",
  btnPrimary: "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-semibold shadow-lg shadow-blue-500/20 border border-blue-500/20",
  btnDanger: "bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white font-semibold shadow-lg shadow-red-500/20 border border-red-500/20",
  btnSuccess: "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold shadow-lg shadow-emerald-500/20 border border-emerald-500/20",
  btnNeutral: "bg-gray-800/60 hover:bg-gray-700/60 text-gray-300 font-medium border border-gray-700",
  btnActive: "bg-gradient-to-r from-emerald-600 to-green-600 text-white shadow-lg shadow-emerald-500/30 border border-emerald-400/30",
  btnWarning: "bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white font-semibold shadow-lg shadow-amber-500/20 border border-amber-500/20",
  btnPurple: "bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-500 hover:to-violet-500 text-white font-semibold shadow-lg shadow-purple-500/20 border border-purple-500/20",
};

export default function App() {
  const [form, setForm] = useState(() => mergeFormState(readStoredJson(FORM_CACHE_STORAGE_KEY, null)));
  const formRef = useRef(form);
  const [status, setStatus] = useState(() => readStoredJson(STATUS_CACHE_STORAGE_KEY, {}));
  const statusRef = useRef(status);

  const [serverFiles, setServerFiles] = useState([]);
  const [logoOptions, setLogoOptions] = useState(LOGO_OPTIONS);
  const [fallbackOptions, setFallbackOptions] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [playlists, setPlaylists] = useState({});
  const [healthStatus, setHealthStatus] = useState(() => readStoredJson(HEALTH_CACHE_STORAGE_KEY, { status: "unknown" }));
  const [licenseInfo, setLicenseInfo] = useState(() => readStoredJson(LICENSE_CACHE_STORAGE_KEY, null));
  const [fontOptions, setFontOptions] = useState(FONT_OPTIONS);
  const [showTickerModal, setShowTickerModal] = useState(false);
  const [tickerDraft, setTickerDraft] = useState({ text: "", font: FONT_OPTIONS[0], font_size: 48, color: "#ffffff", bg_color: "#000000", enable_bg: true, bg_alpha: 0.65, speed: 40, y: 1030 });
  const [playlistStartClock, setPlaylistStartClock] = useState("06:00:00");

  const [selected, setSelected] = useState(() => {
    try {
      const saved = localStorage.getItem(SELECTED_CHANNEL_STORAGE_KEY);
      return DEFAULT_CHANNELS.includes(saved) ? saved : "ch1";
    } catch {
      return "ch1";
    }
  });
  const [matrixMode, setMatrixMode] = useState(false);
  const [controlRoomMode, setControlRoomMode] = useState(() => {
    try { return localStorage.getItem("loop_control_room_mode") === "1"; } catch { return false; }
  });
  const [showPlaylistManager, setShowPlaylistManager] = useState(false);
  const [showRecordingPanel, setShowRecordingPanel] = useState(false);
  const [recordings, setRecordings] = useState([]);
  const [activeConfigTab, setActiveConfigTab] = useState("source");

  const [hlsKey, setHlsKey] = useState({});
  const bumpHls = useCallback((ch) => {
    setHlsKey((p) => ({ ...p, [ch]: Date.now() }));
  }, []);

  const vodRefs = useRef({});
  const trimModalShellRef = useRef(null);
  const trimModalPrimedRef = useRef(false);
  const [vodKey, setVodKey] = useState({});

  const [passwordInput, setPasswordInput] = useState("");
  const [authenticated, setAuthenticated] = useState(() => {
    const expire = localStorage.getItem("loop_expire");
    return localStorage.getItem("loop_auth") === "1" && expire && Date.now() < Number(expire);
  });

  const [newPlaylistName, setNewPlaylistName] = useState("");
  const [playlistFiles, setPlaylistFiles] = useState([]);
  const [playlistDurations, setPlaylistDurations] = useState([]);
  const [playlistItemStartClocks, setPlaylistItemStartClocks] = useState([]);
  const [playlistLoop, setPlaylistLoop] = useState(true);
  const [playlistShuffle, setPlaylistShuffle] = useState(false);
  const [playlistData, setPlaylistData] = useState({});
  const [playlistUrlInput, setPlaylistUrlInput] = useState("");
  const [playlistUrlDuration, setPlaylistUrlDuration] = useState("");
  const [playlistUrlStartClock, setPlaylistUrlStartClock] = useState("");
  const [playlistUrlContinuous, setPlaylistUrlContinuous] = useState(true);
  const [playlistBusy, setPlaylistBusy] = useState(false);
  const [playlistMarksDraft, setPlaylistMarksDraft] = useState({});
  const [playlistEditorMode, setPlaylistEditorMode] = useState("create");
  const [editingPlaylistName, setEditingPlaylistName] = useState("");
  const [trimModalItem, setTrimModalItem] = useState(null);
  const [trimModalMarks, setTrimModalMarks] = useState({ mark_in: "", mark_out: "" });
  const [selectedLibraryFiles, setSelectedLibraryFiles] = useState(new Set());
  const [playlistPreviewFile, setPlaylistPreviewFile] = useState(null);
  const [playlistMediaSearch, setPlaylistMediaSearch] = useState("");
  const [playlistInspectorName, setPlaylistInspectorName] = useState("");
  const [playlistRundownName, setPlaylistRundownName] = useState("");
  const [playlistManagerMode, setPlaylistManagerMode] = useState("edit");
  const [playlistManagerSelectedName, setPlaylistManagerSelectedName] = useState("");
  const [playlistManagerDropdownOpen, setPlaylistManagerDropdownOpen] = useState(false);
  const [playlistManagerSearch, setPlaylistManagerSearch] = useState("");
  const [playlistItemEditingIndex, setPlaylistItemEditingIndex] = useState(null);
  const [playlistUrlEditorOpen, setPlaylistUrlEditorOpen] = useState(false);
  const [playlistUrlEditIndex, setPlaylistUrlEditIndex] = useState(null);
  const [playlistMediaCollapsed, setPlaylistMediaCollapsed] = useState(() => {
    try { return localStorage.getItem("loop_playlist_media_collapsed") === "1"; } catch { return false; }
  });
  const [playlistFocusMode, setPlaylistFocusMode] = useState(false);
  const [selectedPlaylistName, setSelectedPlaylistName] = useState("");
  const [selectedPlaylistIndex, setSelectedPlaylistIndex] = useState(null);
  const playlistEditorItemsRef = useRef(null);
  const playlistEditorPaneRef = useRef(null);
  const playlistPopoverRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0); // seconds, synced from backend
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [editingChannelName, setEditingChannelName] = useState(false);
  const [channelNameDraft, setChannelNameDraft] = useState("");
  const [overlayCanvasSelection, setOverlayCanvasSelection] = useState({ type: "main", index: null });
  const [overlayCanvasOpen, setOverlayCanvasOpen] = useState(false);
  const [overlaySizeLocks, setOverlaySizeLocks] = useState({});
  const [customDetailOpen, setCustomDetailOpen] = useState({});
  // Removed unused darkMode and setDarkMode
  // const [darkMode, setDarkMode] = useState(true);

  // New: Channel health states
  const [channelHealth, setChannelHealth] = useState(() => {
    const next = {};
    Object.keys(status || {}).forEach((ch) => {
      next[ch] = {
        input: status[ch]?.input_health,
        internal: status[ch]?.internal_health,
        facebook: status[ch]?.facebook_health,
        youtube: status[ch]?.youtube_health,
        custom: status[ch]?.custom_health,
      };
    });
    return next;
  });
  const overlayCanvasRef = useRef(null);
  const overlayDragStateRef = useRef(null);
  const overlayResizeStateRef = useRef(null);
  const channelNameCancelRef = useRef(false);

  /** ---------- Core Logic ---------- */
  const saveTimer = useRef(null);
  const scheduleFormSave = useCallback((nextState, delay = 400) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      axios.post("/api/form/save", nextState).catch(() => {});
    }, delay);
  }, []);

  const loadLogos = useCallback(async () => {
    try {
      const res = await axios.get("/api/logo/list");
      const names = (res.data?.files || [])
        .map((item) => item?.name || item)
        .filter(Boolean);
      setLogoOptions(names.length ? names : LOGO_OPTIONS);
    } catch {
      setLogoOptions(LOGO_OPTIONS);
    }
  }, []);

  const loadFallbackMedia = useCallback(async () => {
    try {
      const res = await axios.get("/api/fallback/list");
      setFallbackOptions(res.data?.files || []);
    } catch {
      setFallbackOptions([]);
    }
  }, []);

  const refreshLicenseInfo = useCallback(async (force = false) => {
    try {
      const res = await axios.get("/api/license/status" + (force ? "?force=true" : ""));
      setLicenseInfo(res.data || null);
      return res.data;
    } catch (e) {
      const fallback = { valid: false, message: e?.message || "Unable to read license status" };
      setLicenseInfo(fallback);
      return fallback;
    }
  }, []);

  const clearLocalAuth = useCallback(() => {
    localStorage.removeItem("loop_auth");
    localStorage.removeItem("loop_expire");
    setAuthenticated(false);
  }, []);

  const loadFonts = useCallback(async () => {
    try {
      const res = await axios.get("/api/fonts/list");
      const names = (res.data?.files || []).map((item) => item.path).filter(Boolean);
      if (names.length) setFontOptions(names);
    } catch {}
  }, []);

  const updateField = useCallback((ch, key, val) => {
    setForm((prev) => {
      if (!prev[ch]) return prev;
      const nextChannel = linkUdpBitrates(prev[ch], key, val);
      const updated = { ...prev, [ch]: nextChannel };
      if (key === "input") {
        updated[ch].mark_in = "";
        updated[ch].mark_out = "";
        setVodKey((old) => ({ ...old, [ch]: Date.now() }));
      }
      formRef.current = updated;
      scheduleFormSave(updated);
      
      return updated;
    });
  }, [scheduleFormSave]);

  const applyUdpProfile = useCallback((ch, profile) => {
    setForm((prev) => {
      if (!prev[ch]) return prev;
      const profileDefaults = profile === "mpeg2"
        ? {
            udp_profile: "mpeg2",
            udp_muxrate: "7000k",
            udp_rate_control: "cbr",
            udp_video_bitrate: "4000k",
            udp_audio_bitrate: "192k",
            udp_minrate: "4000k",
            udp_maxrate: "4000k",
            udp_video_bufsize: "8000k",
            udp_gop_size: "12",
            udp_keyint_min: "12",
            udp_bframes: "2",
            udp_service_name: "Hazoor Sahib",
            udp_service_provider: "MSLiveBox",
            udp_service_id: "1",
            udp_transport_stream_id: "1",
            udp_original_network_id: "1",
            udp_pmt_pid: "4096",
            udp_start_pid: "256",
            udp_video_pid: "256",
            udp_audio_pid: "257",
            udp_pcr_period: "20",
            udp_pat_period: "0.1",
            udp_sdt_period: "0.5",
            udp_pkt_size: "1316",
            udp_ttl: "255",
            udp_buffer_size: "9388608",
            udp_fifo_size: "1000000",
            udp_system_b: false,
            udp_resend_headers: true,
          }
        : {
            udp_profile: "mpeg4",
            udp_muxrate: "8000000",
            udp_rate_control: "cbr",
            udp_video_bitrate: "5000k",
            udp_audio_bitrate: "128k",
            udp_minrate: "5000k",
            udp_maxrate: "5000k",
            udp_video_bufsize: "10000k",
            udp_gop_size: "50",
            udp_keyint_min: "50",
            udp_bframes: "0",
            udp_service_name: "Hazoor Sahib",
            udp_service_provider: "MSLiveBox",
            udp_service_id: "1",
            udp_transport_stream_id: "1",
            udp_original_network_id: "1",
            udp_pmt_pid: "4096",
            udp_pcr_period: "40",
            udp_pat_period: "0.1",
            udp_sdt_period: "0.5",
            udp_pkt_size: "1316",
            udp_ttl: "255",
            udp_buffer_size: "8388608",
            udp_fifo_size: "1000000",
            udp_start_pid: "256",
            udp_video_pid: "256",
            udp_audio_pid: "257",
            udp_system_b: true,
            udp_resend_headers: true,
          };
      const updated = { ...prev, [ch]: { ...prev[ch], ...profileDefaults } };
      formRef.current = updated;
      scheduleFormSave(updated);
      return updated;
    });
  }, [scheduleFormSave]);

  const addAdditionalLogo = (ch) => {
    const cur = formRef.current?.[ch]?.additional_logos || [];
    updateField(ch, "additional_logos", [...cur, { logo: "", enabled: true, x: 0, y: 0, w: 0, h: 0 }]);
  };

  const setMainOverlayLogo = (ch, logoPath) => {
    setForm((prev) => {
      const updated = {
        ...prev,
        [ch]: {
          ...(prev[ch] || {}),
          overlay_logo: logoPath,
          overlay_logo_enabled: !!logoPath,
        },
      };
      formRef.current = updated;
      scheduleFormSave(updated);
      return updated;
    });
  };
  
  const updateAdditionalLogo = (ch, index, key, value) => {
    const list = [...(formRef.current?.[ch]?.additional_logos || [])];
    if (!list[index]) return;
    list[index] = { ...list[index], [key]: value };
    updateField(ch, "additional_logos", list);
  };

  const overlaySizeLockKey = (type, index = null) => `${type}:${index ?? "main"}`;
  const isOverlaySizeLocked = useCallback((type, index = null) => {
    return overlaySizeLocks[`${type}:${index ?? "main"}`] !== false;
  }, [overlaySizeLocks]);
  const toggleOverlaySizeLock = (type, index = null) => {
    const key = overlaySizeLockKey(type, index);
    setOverlaySizeLocks((prev) => ({ ...prev, [key]: prev[key] === false }));
  };

  const setMainLogoSize = (axis, rawValue) => {
    const value = String(rawValue || "").replace(/[^\d]/g, "");
    const currentW = parsePosition(formRef.current?.[ch]?.logo_width, 220);
    const currentH = parsePosition(formRef.current?.[ch]?.logo_height, 80);
    const numeric = Number(value || 0);
    if (!isOverlaySizeLocked("main") || !numeric || !currentW || !currentH) {
      updateField(ch, axis === "w" ? "logo_width" : "logo_height", value);
      return;
    }
    if (axis === "w") {
      updateField(ch, "logo_width", value);
      updateField(ch, "logo_height", Math.round(numeric * (currentH / currentW)));
    } else {
      updateField(ch, "logo_height", value);
      updateField(ch, "logo_width", Math.round(numeric * (currentW / currentH)));
    }
  };

  const setAdditionalLogoSize = (ch, index, axis, rawValue) => {
    const value = String(rawValue || "").replace(/[^\d]/g, "");
    const list = [...(formRef.current?.[ch]?.additional_logos || [])];
    const row = list[index];
    if (!row) return;
    const currentW = parsePosition(row.w, 220);
    const currentH = parsePosition(row.h, 80);
    const numeric = Number(value || 0);
    if (!isOverlaySizeLocked("layer", index) || !numeric || !currentW || !currentH) {
      list[index] = { ...row, [axis === "w" ? "w" : "h"]: value };
      updateField(ch, "additional_logos", list);
      return;
    }
    list[index] = axis === "w"
      ? { ...row, w: value, h: Math.round(numeric * (currentH / currentW)) }
      : { ...row, h: value, w: Math.round(numeric * (currentW / currentH)) };
    updateField(ch, "additional_logos", list);
  };

  const setAdditionalLogoPath = (ch, index, logoPath) => {
    const list = [...(formRef.current?.[ch]?.additional_logos || [])];
    if (!list[index]) return;
    list[index] = {
      ...list[index],
      logo: logoPath,
      enabled: !!logoPath,
    };
    updateField(ch, "additional_logos", list);
  };
  
  const removeAdditionalLogo = (ch, index) => {
    const list = [...(formRef.current?.[ch]?.additional_logos || [])];
    list.splice(index, 1);
    updateField(ch, "additional_logos", list);
  };

  const toggleMainOverlay = (ch) => {
    const current = formRef.current?.[ch] || {};
    if (!current.overlay_logo) return;
    updateField(ch, "overlay_logo_enabled", !current.overlay_logo_enabled);
  };

  const toggleAdditionalOverlay = (ch, index) => {
    const list = [...(formRef.current?.[ch]?.additional_logos || [])];
    if (!list[index]?.logo) return;
    list[index] = { ...list[index], enabled: !list[index].enabled };
    updateField(ch, "additional_logos", list);
  };

  const persistChannelForm = async (channelId) => {
    const nextForm = {
      ...formRef.current,
      [channelId]: {
        ...(formRef.current?.[channelId] || {}),
      },
    };
    formRef.current = nextForm;
    setForm(nextForm);
    scheduleFormSave(nextForm, 0);
    await axios.post("/api/form/save", nextForm);
  };

  const handleLogin = async () => {
    const latestLicense = await refreshLicenseInfo(true);
    if (latestLicense && latestLicense.required !== false && !latestLicense.valid) {
      clearLocalAuth();
      alert(latestLicense.message || "License not valid");
      return;
    }
    if (passwordInput === "admin@123") {
      setAuthenticated(true);
      localStorage.setItem("loop_auth", "1");
      localStorage.setItem("loop_expire", String(Date.now() + 6 * 60 * 60 * 1000));
    } else {
      alert("Incorrect password");
    }
  };
  
  const handleLogout = () => {
    clearLocalAuth();
  };

 const runningProtocolLabel = (p) =>
  ({
    rtmp: "RTMP",
    udp: "UDP",
    udp_profiles: "UDP",
    UDP_PROFILES: "UDP",
    srt: "SRT",
    RTMP: "RTMP",
    SRT: "SRT",
  })[p] || "RTMP";

  const apiPollLock = useRef({ busy: false, tick: 0 });
  const prevRunningRef = useRef({});
  const applyStatusSnapshot = useCallback((incomingStatus, baseForm = null) => {
    const newStatus = incomingStatus || {};
    setStatus(newStatus);
    statusRef.current = newStatus;

    const healthData = {};
    Object.keys(newStatus).forEach((ch) => {
      healthData[ch] = {
        input: newStatus[ch]?.input_health,
        internal: newStatus[ch]?.internal_health,
        facebook: newStatus[ch]?.facebook_health,
        youtube: newStatus[ch]?.youtube_health,
        custom: newStatus[ch]?.custom_health,
      };
    });
    setChannelHealth(healthData);

    const curForm = baseForm || formRef.current;
    if (!curForm || !Object.keys(curForm).length) return;

    let updated = curForm;
    let modified = false;
    DEFAULT_CHANNELS.forEach((ch) => {
      const runningNow = safeBool(newStatus?.[ch]?.internal?.running);
      const previous = prevRunningRef.current?.[ch];
      const runningPrev = safeBool(typeof previous === "object" ? previous.internal : previous);
      const previewNow = safeBool(newStatus?.[ch]?.preview?.running) || safeBool(newStatus?.[ch]?.hls?.running);
      const previewPrev = safeBool(typeof previous === "object" ? previous.preview : false);

      if ((runningNow && !runningPrev) || (previewNow && !previewPrev)) {
        bumpHls(ch);
      }

      // Keep the user's manual source tab selection (server/upload) while idle.
      // Only enforce HLS when the channel becomes live, then restore server when that live run ends.
      let desiredMode = null;
      if (runningNow) {
        desiredMode = "hls";
      } else if (!runningNow && runningPrev && updated[ch]?.mode === "hls") {
        desiredMode = "server";
      }

      if (desiredMode && updated[ch] && updated[ch].mode !== desiredMode) {
        if (!modified) updated = { ...updated };
        updated[ch] = { ...updated[ch], mode: desiredMode };
        modified = true;
      }

      // The server is authoritative once a live path is locked.  Reconcile
      // the cached browser form with that source mode so a live Playout
      // channel cannot reappear as Restreamer after refresh or restart.
      const lockedMode = String(newStatus?.[ch]?.channel_mode || "").trim().toLowerCase();
      if (safeBool(newStatus?.[ch]?.mode_locked) && ["encoder", "restreamer", "playlist"].includes(lockedMode) && updated[ch]) {
        const lockedInputType = lockedMode === "playlist" ? "playlist" : lockedMode === "restreamer" ? "url" : "file";
        if (updated[ch].dashboard_mode !== lockedMode || updated[ch].inputType !== lockedInputType) {
          if (!modified) updated = { ...updated };
          updated[ch] = { ...updated[ch], dashboard_mode: lockedMode, inputType: lockedInputType };
          modified = true;
        }
      }
      prevRunningRef.current[ch] = { internal: runningNow, preview: previewNow };
    });

    if (modified) {
      setForm(updated);
      formRef.current = updated;
    }
  }, [bumpHls]);

  /** ---------- Effects ---------- */
  useEffect(() => {
    let cancelled = false;
    const loadDeferredData = () => {
      window.setTimeout(async () => {
        if (cancelled) return;
        try {
          const [filesRes, fallbackRes, healthRes] = await Promise.all([
            axios.get("/api/video/list").catch(() => ({ data: { files: [] } })),
            axios.get("/api/fallback/list").catch(() => ({ data: { files: [] } })),
            axios.get("/api/health").catch(() => ({ data: { status: "unknown" } })),
          ]);
          if (cancelled) return;
          setServerFiles(filesRes?.data?.files || []);
          setFallbackOptions(fallbackRes?.data?.files || []);
          setHealthStatus(healthRes?.data || { status: "unknown" });
        } catch {}
      }, 0);
    };

    (async () => {
      loadLogos().catch(() => {});
      loadFallbackMedia().catch(() => {});
      loadFonts().catch(() => {});
      refreshLicenseInfo(false).catch(() => {});
      try {
        const [formRes, statusRes, stateRes] = await Promise.all([
          axios.get("/api/form/load"),
          axios.get("/api/channel/status").catch(() => ({ data: {} })),
          axios.get("/api/state").catch(() => null),
        ]);

        if (cancelled) return;

        const merged = mergeFormState(formRes.data);
        setForm(merged);
        formRef.current = merged;
        applyStatusSnapshot(statusRes?.data || {}, merged);

        if (stateRes?.data) {
          console.log("Loaded channel states:", stateRes.data);
        }
        loadDeferredData();
      } catch {
        if (cancelled) return;
        const fallback = buildDefaultFormState();
        setForm(fallback);
        formRef.current = fallback;
        loadDeferredData();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadLogos, loadFallbackMedia, loadFonts, refreshLicenseInfo, applyStatusSnapshot]);

  useEffect(() => {
    const timer = setInterval(() => {
      refreshLicenseInfo(false).catch(() => {});
    }, LICENSE_WATCHDOG_MS);
    return () => clearInterval(timer);
  }, [refreshLicenseInfo]);

  useEffect(() => {
    if (licenseInfo && licenseInfo.required !== false && !licenseInfo.valid) {
      clearLocalAuth();
    }
  }, [licenseInfo, clearLocalAuth]);

  useEffect(() => {
    writeStoredJson(FORM_CACHE_STORAGE_KEY, form);
  }, [form]);

  useEffect(() => {
    writeStoredJson(STATUS_CACHE_STORAGE_KEY, status);
  }, [status]);

  useEffect(() => {
    writeStoredJson(HEALTH_CACHE_STORAGE_KEY, healthStatus);
  }, [healthStatus]);

  useEffect(() => {
    if (licenseInfo) {
      writeStoredJson(LICENSE_CACHE_STORAGE_KEY, licenseInfo);
    }
  }, [licenseInfo]);

  useEffect(() => {
    setEditingChannelName(false);
    setChannelNameDraft("");
  }, [selected]);

  useEffect(() => {
    try {
      localStorage.setItem(SELECTED_CHANNEL_STORAGE_KEY, selected);
    } catch {}
  }, [selected]);

  useEffect(() => {
    const recordFiles = (serverFiles || []).filter((file) =>
      String(file?.name || "").toLowerCase().includes(`record_${selected}`)
    );
    setRecordings(recordFiles);
    const recInfo = status?.[selected]?.record || {};
    setIsRecording(!!recInfo.recording);
    setRecordingTime(recInfo.duration_sec || 0);
  }, [selected, serverFiles, status]);

  useEffect(() => {
    let cancelled = false;
    const pollLock = apiPollLock.current;
    const timer = setInterval(async () => {
      if (pollLock.busy || cancelled || document.hidden) return;
      pollLock.busy = true;
      pollLock.tick += 1;
      try {
        // Load status with health info
        const s = await axios.get("/api/channel/status");
        if (cancelled) return;
        const newStatus = s.data || {};
        applyStatusSnapshot(newStatus);

        if (pollLock.tick % 3 === 1) {
          const [filesRes, fallbackRes] = await Promise.all([
            axios.get("/api/video/list"),
            axios.get("/api/fallback/list").catch(() => ({ data: { files: [] } })),
          ]);
          if (cancelled) return;
          setServerFiles(filesRes.data?.files || []);
          setFallbackOptions(fallbackRes.data?.files || []);
        }

        // Load health status
        if (pollLock.tick % 4 === 1) try {
          const health = await axios.get("/api/health");
          if (cancelled) return;
          setHealthStatus(health.data);
        } catch {}
      } catch (e) {
        console.error("Status update error:", e);
      } finally {
        pollLock.busy = false;
      }
    }, 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
      pollLock.busy = false;
    };
  }, [applyStatusSnapshot]);

  /** ---------- File Management ---------- */
  const uploadFile = async (e, ch) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post("/api/upload", fd, {
        onUploadProgress: (p) => {
          const pct = Math.round((p.loaded * 100) / (p.total || 1));
          setUploadProgress((prev) => ({ ...prev, [ch]: pct }));
        },
      });
      updateField(ch, "input", `/home/FTP/loop/video/${file.name}`);
      // Stay on "upload" mode — do NOT switch back to "server"
      const filesRes = await axios.get("/api/video/list");
      setServerFiles(filesRes.data?.files || []);
      setTimeout(() => setUploadProgress((prev) => ({ ...prev, [ch]: 0 })), 2000);
    } catch { alert("Upload failed"); setUploadProgress((prev) => ({ ...prev, [ch]: 0 })); }
  };

  const uploadFileToLibrary = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post("/api/upload", fd, {
        onUploadProgress: (p) => {
          const pct = Math.round((p.loaded * 100) / (p.total || 1));
          setUploadProgress((prev) => ({ ...prev, __library__: pct }));
        },
      });
      const filesRes = await axios.get("/api/video/list");
      setServerFiles(filesRes.data?.files || []);
      setTimeout(() => setUploadProgress((prev) => ({ ...prev, __library__: 0 })), 2000);
    } catch { alert("Upload failed"); setUploadProgress((prev) => ({ ...prev, __library__: 0 })); }
  };

  const uploadLogo = async (e, ch) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post("/api/upload-logo", fd);
      await loadLogos();
      setMainOverlayLogo(ch, res.data.path);
      alert("Logo uploaded successfully!");
    } catch { alert("Logo upload failed"); }
  };

  const uploadFallbackMedia = async (e, ch) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const progressKey = `${ch}:fallback`;
    e.target.value = "";
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post("/api/fallback/upload", fd, {
        onUploadProgress: (p) => {
          const pct = Math.round((p.loaded * 100) / (p.total || 1));
          setUploadProgress((prev) => ({ ...prev, [progressKey]: pct }));
        },
      });
      await loadFallbackMedia();
      updateField(ch, "fallback_media", res.data.path);
      setTimeout(() => setUploadProgress((prev) => ({ ...prev, [progressKey]: 0 })), 2000);
    } catch {
      alert("Fallback upload failed");
      setUploadProgress((prev) => ({ ...prev, [progressKey]: 0 }));
    }
  };

  const deleteVideo = async (fileName) => {
    if (!window.confirm(`Delete ${fileName}? This action cannot be undone.`)) return;
    try {
      await axios.delete(`/api/video/delete?filename=${encodeURIComponent(fileName)}`);
      const filesRes = await axios.get("/api/video/list");
      setServerFiles(filesRes.data?.files || []);
      setSelectedLibraryFiles((prev) => { const n = new Set(prev); n.delete(fileName); return n; });
    } catch (error) {
      alert("Failed to delete file: " + error.message);
    }
  };

  const deleteSelectedFiles = async () => {
    if (selectedLibraryFiles.size === 0) return;
    if (!window.confirm(`Delete ${selectedLibraryFiles.size} file(s)? This cannot be undone.`)) return;
    try {
      await Promise.all([...selectedLibraryFiles].map((name) =>
        axios.delete(`/api/video/delete?filename=${encodeURIComponent(name)}`)
      ));
      const filesRes = await axios.get("/api/video/list");
      setServerFiles(filesRes.data?.files || []);
      setSelectedLibraryFiles(new Set());
    } catch (error) {
      alert("Failed to delete some files: " + error.message);
    }
  };

  /** ---------- Playlist Management ---------- */
  const loadPlaylists = useCallback(async () => {
    try {
      const res = await axios.get(`/api/playlist/list?channel_id=${selected}`);
      const data = res.data || {};
      setPlaylists(data);
      return data;
    } catch (error) {
      console.error("Failed to load playlists:", error);
      return {};
    }
  }, [selected]);

  const activePlaylistInfo = useMemo(() => {
    const byStatus = status?.[selected]?.playlist;
    if (byStatus?.name && playlists?.[byStatus.name]) {
      return {
        ...playlists[byStatus.name],
        name: byStatus.name,
        current_index: byStatus.current_index ?? 0,
        loop_item: byStatus.loop_item ?? false,
      };
    }
    const fallbackName = playlistData[selected]?.name;
    return fallbackName ? { ...playlists[fallbackName], ...playlistData[selected], name: fallbackName } : null;
  }, [status, selected, playlists, playlistData]);
  const activePlaylistStatusName = status?.[selected]?.playlist?.name || "";

  useEffect(() => {
    if (authenticated) {
      loadPlaylists();
    }
  }, [authenticated, selected, showPlaylistManager, loadPlaylists, activePlaylistStatusName]);

  useEffect(() => {
    try { localStorage.setItem("loop_playlist_media_collapsed", playlistMediaCollapsed ? "1" : "0"); } catch {}
  }, [playlistMediaCollapsed]);

  useEffect(() => {
    if (!showPlaylistManager) return undefined;
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
    const closePopover = (event) => {
      if (playlistPopoverRef.current && !playlistPopoverRef.current.contains(event.target)) setPlaylistManagerDropdownOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setPlaylistManagerDropdownOpen(false);
    };
    document.addEventListener("mousedown", closePopover);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      document.removeEventListener("mousedown", closePopover);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [showPlaylistManager]);

  useEffect(() => {
    try { localStorage.setItem("loop_control_room_mode", controlRoomMode ? "1" : "0"); } catch {}
  }, [controlRoomMode]);

  useEffect(() => {
    if (!playlistFiles.length) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const list = playlistEditorItemsRef.current;
      if (list) list.scrollTop = list.scrollHeight;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [playlistFiles.length]);

  const libraryFileMap = useMemo(() => {
    const map = {};
    serverFiles.forEach((file) => {
      if (!file?.path) return;
      map[file.path] = file;
      map[file.name] = file;
    });
    return map;
  }, [serverFiles]);

  // Drag and drop handlers
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(playlistFiles);
    const durations = Array.from(playlistDurations);
    const startClocks = Array.from(playlistItemStartClocks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    const [reorderedDuration] = durations.splice(result.source.index, 1);
    const [reorderedStartClock] = startClocks.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    durations.splice(result.destination.index, 0, reorderedDuration || 0);
    startClocks.splice(result.destination.index, 0, reorderedStartClock || "");
    
    setPlaylistFiles(items);
    setPlaylistDurations(durations);
    setPlaylistItemStartClocks(startClocks);
  };

  const addToPlaylist = (filePath) => {
    if (!playlistFiles.includes(filePath)) {
      setPlaylistFiles([...playlistFiles, filePath]);
      setPlaylistDurations((prev) => [...prev, 0]);
      setPlaylistItemStartClocks((prev) => [...prev, ""]);
    }
  };

  const removeFromPlaylist = (index) => {
    setPlaylistFiles(playlistFiles.filter((_, i) => i !== index));
    setPlaylistDurations((prev) => prev.filter((_, i) => i !== index));
    setPlaylistItemStartClocks((prev) => prev.filter((_, i) => i !== index));
  };

  const selectPlaylistItem = useCallback((playlistName, index) => {
    setSelectedPlaylistName(playlistName || "");
    setSelectedPlaylistIndex(Number.isInteger(index) ? index : null);
  }, []);

  const resetPlaylistComposer = () => {
    setPlaylistEditorMode("create");
    setEditingPlaylistName("");
    setPlaylistInspectorName("");
    setPlaylistRundownName("");
    setNewPlaylistName("");
    setPlaylistFiles([]);
    setPlaylistDurations([]);
    setPlaylistItemStartClocks([]);
    setPlaylistLoop(true);
    setPlaylistShuffle(false);
    setPlaylistUrlInput("");
    setPlaylistUrlDuration("");
    setPlaylistUrlStartClock("");
    setPlaylistUrlContinuous(true);
    setPlaylistManagerSelectedName("");
    setPlaylistManagerMode("edit");
  };

  const openPlaylistEditor = async (name) => {
    let pl = playlists?.[name];
    if (!pl) {
      const latest = await loadPlaylists();
      pl = latest?.[name];
    }
    if (!pl) return;
    setShowPlaylistManager(true);
    setPlaylistEditorMode("edit");
    setEditingPlaylistName(name);
    setNewPlaylistName(name);
    setPlaylistFiles([...(pl.files || [])]);
    setPlaylistDurations((pl.files || []).map((_, index) => parsePlaylistDuration(pl.durations?.[index])));
    setPlaylistItemStartClocks((pl.files || []).map((_, index) => String(pl.start_times?.[index] || pl.items?.[index]?.day_start || "")));
    setPlaylistStartClock(pl.start_clock || "00:00:00");
    setPlaylistLoop(pl.loop_mode !== false);
    setPlaylistShuffle(!!pl.shuffle);
    setPlaylistInspectorName("");
    setPlaylistRundownName("");
    setPlaylistManagerSelectedName(name);
    setPlaylistManagerMode("edit");
    setPlaylistManagerDropdownOpen(false);
    window.requestAnimationFrame(() => {
      playlistEditorPaneRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const openPlaylistViewer = (name) => {
    const pl = playlists?.[name];
    if (!pl) return;
    setPlaylistEditorMode("edit");
    setEditingPlaylistName(name);
    setNewPlaylistName(name);
    setPlaylistFiles([...(pl.files || [])]);
    setPlaylistDurations((pl.files || []).map((_, index) => parsePlaylistDuration(pl.durations?.[index])));
    setPlaylistItemStartClocks((pl.files || []).map((_, index) => String(pl.start_times?.[index] || pl.items?.[index]?.day_start || "")));
    setPlaylistStartClock(pl.start_clock || "00:00:00");
    setPlaylistLoop(pl.loop_mode !== false);
    setPlaylistShuffle(!!pl.shuffle);
    setPlaylistManagerSelectedName(name);
    setPlaylistManagerMode("view");
    setPlaylistManagerDropdownOpen(false);
    setPlaylistInspectorName("");
    setPlaylistRundownName("");
  };

  const createPlaylist = async () => {
    if (!newPlaylistName.trim()) {
      alert("Please enter a playlist name");
      return;
    }
    if (playlistFiles.length === 0) {
      alert("Please add files to the playlist");
      return;
    }

    try {
      const playlistName = newPlaylistName.trim();
      const fd = new FormData();
      fd.append("channel_id", selected);
      fd.append("playlist_name", playlistName);
      fd.append("files", JSON.stringify(playlistFiles));
      fd.append("durations", JSON.stringify(playlistDurations.map((duration) => parsePlaylistDuration(duration))));
      fd.append("start_times", JSON.stringify(playlistItemStartClocks.map((value) => String(value || "").trim())));
      fd.append("loop_mode", playlistLoop);
      fd.append("shuffle", playlistShuffle);
      fd.append("start_clock", playlistStartClock || "");

      // Add trim marks if available
      const f = formRef.current?.[selected];
      if (f?.mark_in || f?.mark_out) {
        fd.append("mark_in", f.mark_in || "");
        fd.append("mark_out", f.mark_out || "");
      }

      await axios.post("/api/playlist/save", fd);
      await axios.post("/api/playlist/timing/save", { channel_id: selected, playlist_name: playlistName, start_clock: playlistStartClock || "" }).catch(() => {});
      if (playlistEditorMode === "edit" && editingPlaylistName && editingPlaylistName !== playlistName) {
        const deleteFd = new FormData();
        deleteFd.append("channel_id", selected);
        deleteFd.append("playlist_name", editingPlaylistName);
        await axios.post("/api/playlist/delete", deleteFd);
      }

      resetPlaylistComposer();
      await loadPlaylists();
      alert(`Playlist ${playlistEditorMode === "edit" ? "updated" : "created"} successfully!`);
    } catch (error) {
      alert(`Failed to ${playlistEditorMode === "edit" ? "update" : "create"} playlist: ` + error.message);
    }
  };

  const deletePlaylist = async (name) => {
    if (!window.confirm(`Delete playlist "${name}"?`)) return;
    try {
      const fd = new FormData();
      fd.append("channel_id", selected);
      fd.append("playlist_name", name);
      await axios.post("/api/playlist/delete", fd);
      await loadPlaylists();
      alert("Playlist deleted successfully");
    } catch (error) {
      alert("Failed to delete playlist: " + error.message);
    }
  };

  const startPlaylist = async (playlistName, loopSegment = false) => {
    try {
      setPlaylistBusy(true);
      const f = formRef.current?.[selected];
      const fd = new FormData();
      fd.append("channel_id", selected);
      fd.append("playlist_name", playlistName);
      fd.append("loop_segment", loopSegment);
      fd.append("start_clock", playlistStartClock || playlists?.[playlistName]?.start_clock || "");
      
      // Preserve current channel settings
      if (f) {
        fd.append("save_state_flag", f.save_state !== false);
      }

      await axios.post("/api/playlist/start", fd);
      const loadedPlaylists = await loadPlaylists();
      bumpHls(selected);
      updateField(selected, "dashboard_mode", "playlist");
      updateField(selected, "mode", "hls");
      setPlaylistData(prev => ({
        ...prev,
        [selected]: { 
          name: playlistName, 
          loopSegment,
          files: loadedPlaylists[playlistName]?.files || [],
          items: loadedPlaylists[playlistName]?.items || [],
        }
      }));
      setCurrentFileIndex(0);
      alert(`Playlist "${playlistName}" started!`);
      setShowPlaylistManager(false);
    } catch (error) {
      const backendError = error?.response?.data?.error;
      alert("Failed to start playlist: " + (backendError || error.message));
    } finally {
      setPlaylistBusy(false);
    }
  };

  const stopPlaylist = async () => {
    try {
      setPlaylistBusy(true);
      const fd = new FormData();
      fd.append("channel_id", selected);
      fd.append("dest", "internal");
      await axios.post("/api/channel/stop", fd);
      await loadPlaylists();
      setPlaylistData(prev => {
        const newData = { ...prev };
        delete newData[selected];
        return newData;
      });
      setCurrentFileIndex(0);
      alert("Playlist stopped");
    } catch (error) {
      alert("Failed to stop playlist: " + error.message);
    } finally {
      setPlaylistBusy(false);
    }
  };

  const playPlaylistItem = async (playlistName, index, loopItem = false) => {
    try {
      setPlaylistBusy(true);
      selectPlaylistItem(playlistName, index);
      await axios.post("/api/playlist/item/play", {
        channel_id: selected,
        playlist_name: playlistName,
        index,
        loop_item: loopItem,
      });
      await loadPlaylists();
      setCurrentFileIndex(index);
    } catch (error) {
      const backendError = error?.response?.data?.error;
      alert("Failed to play playlist item: " + (backendError || error.message));
    } finally {
      setPlaylistBusy(false);
    }
  };

  const pausePlaylist = async () => {
    try {
      setPlaylistBusy(true);
      await axios.post("/api/playlist/pause", { channel_id: selected });
    } catch (error) {
      alert("Failed to pause playlist: " + (error?.response?.data?.error || error.message));
    } finally {
      setPlaylistBusy(false);
    }
  };

  const resumePlaylist = async () => {
    try {
      setPlaylistBusy(true);
      await axios.post("/api/playlist/resume", { channel_id: selected });
    } catch (error) {
      alert("Failed to resume playlist: " + (error?.response?.data?.error || error.message));
    } finally {
      setPlaylistBusy(false);
    }
  };

  const stopPlaylistItem = async () => {
    try {
      setPlaylistBusy(true);
      await axios.post("/api/playlist/item/stop", { channel_id: selected });
      await loadPlaylists();
      setPlaylistData(prev => {
        const newData = { ...prev };
        delete newData[selected];
        return newData;
      });
      setSelectedPlaylistName("");
      setSelectedPlaylistIndex(null);
    } catch (error) {
      const backendError = error?.response?.data?.error;
      alert("Failed to stop playlist item: " + (backendError || error.message));
    } finally {
      setPlaylistBusy(false);
    }
  };

  const openTrimModal = (itemOrPath) => {
    const item = typeof itemOrPath === "string"
      ? {
          path: itemOrPath,
          title: itemOrPath.split("/").pop(),
          is_remote: /^(https?|rtmp|rtmps|udp|srt|rtsp):\/\//i.test(itemOrPath) || /youtube\.com|youtu\.be/i.test(itemOrPath),
          protocol: playlistItemProtocol({ path: itemOrPath }),
        }
      : itemOrPath;
    if (!canTrimPlaylistItem(item)) return;
    const draft = playlistMarksDraft[item.path] || {};
    setTrimModalItem(item);
    setTrimModalMarks({
      mark_in: draft.mark_in || item.mark_in || "",
      mark_out: draft.mark_out || item.mark_out || "",
    });
    trimModalPrimedRef.current = false;
  };

  const closeTrimModal = useCallback(() => {
    setTrimModalItem(null);
    setTrimModalMarks({ mark_in: "", mark_out: "" });
  }, []);

  const captureTrimModalMark = (type) => {
    const video = vodRefs.current.trimModal;
    if (!video || Number.isNaN(video.currentTime)) {
      alert("Trim preview is not ready yet");
      return;
    }
    const value = formatClockFromSeconds(video.currentTime);
    setTrimModalMarks((prev) => ({ ...prev, [type === "in" ? "mark_in" : "mark_out"]: value }));
  };

  const primeTrimModalPreview = () => {
    const video = vodRefs.current.trimModal;
    if (!video || trimModalPrimedRef.current) return;
    trimModalPrimedRef.current = true;
    const preferredStart = parseClockToSeconds(trimModalMarks.mark_in || trimModalItem?.mark_in || "");
    const previewStart = Math.max(0, preferredStart || 0.04);
    try {
      if (Number.isFinite(video.duration) && previewStart < video.duration) {
        video.currentTime = previewStart;
      } else if (!Number.isFinite(video.duration)) {
        video.currentTime = previewStart;
      }
    } catch {}
    try {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    } catch {}
  };

  const saveTrimModalMarks = useCallback(async () => {
    if (!trimModalItem?.path) return;
    const fd = new FormData();
    fd.append("channel_id", selected);
    fd.append("input", trimModalItem.path);
    fd.append("mark_in", trimModalMarks.mark_in || "");
    fd.append("mark_out", trimModalMarks.mark_out || "");
    try {
      await axios.post("/api/mark/save", fd);
      setPlaylistMarksDraft((prev) => ({
        ...prev,
        [trimModalItem.path]: {
          mark_in: trimModalMarks.mark_in || "",
          mark_out: trimModalMarks.mark_out || "",
        },
      }));
      await loadPlaylists();
      closeTrimModal();
    } catch (error) {
      alert("Failed to save clip marks: " + (error.response?.data?.error || error.message));
    }
  }, [closeTrimModal, loadPlaylists, selected, trimModalItem?.path, trimModalMarks.mark_in, trimModalMarks.mark_out]);

  const playSelectedPlaylistItem = async () => {
    if (!activePlaylistName || !activePlaylistItems.length) return;
    const targetIndex = selectedPlaylistName === activePlaylistName && Number.isInteger(selectedPlaylistIndex)
      ? selectedPlaylistIndex
      : currentFileIndex;
    if (targetIndex == null || targetIndex < 0 || targetIndex >= activePlaylistItems.length) return;
    await playPlaylistItem(activePlaylistName, targetIndex, false);
  };

  const restorePlaylistLoop = async () => {
    if (!activePlaylistName || !activePlaylistItems.length) return;
    const targetIndex = selectedPlaylistName === activePlaylistName && Number.isInteger(selectedPlaylistIndex)
      ? selectedPlaylistIndex
      : currentFileIndex;
    try {
      setPlaylistBusy(true);
      await axios.post("/api/playlist/loop-mode", {
        channel_id: selected,
        playlist_name: activePlaylistName,
        index: targetIndex,
        loop_mode: true,
      });
      await playPlaylistItem(activePlaylistName, targetIndex, false);
    } catch (error) {
      const backendError = error?.response?.data?.error;
      alert("Failed to restore playlist loop: " + (backendError || error.message));
    } finally {
      setPlaylistBusy(false);
    }
  };

  const playPlaylistOffset = async (offset) => {
    if (!activePlaylistName || !activePlaylistItems.length) return;
    const baseIndex = selectedPlaylistName === activePlaylistName && Number.isInteger(selectedPlaylistIndex)
      ? selectedPlaylistIndex
      : currentFileIndex;
    const nextIndex = activePlaylistInfo?.loop_mode
      ? (baseIndex + offset + activePlaylistItems.length) % activePlaylistItems.length
      : baseIndex + offset;
    if (nextIndex < 0 || nextIndex >= activePlaylistItems.length) {
      alert("No clip available at that position");
      return;
    }
    selectPlaylistItem(activePlaylistName, nextIndex);
    await playPlaylistItem(activePlaylistName, nextIndex, false);
  };

  const cuePlaylistSelection = () => {
    if (!activePlaylistItems.length) return;
    const baseIndex = selectedPlaylistName === activePlaylistName && Number.isInteger(selectedPlaylistIndex)
      ? selectedPlaylistIndex
      : currentFileIndex;
    const nextIndex = activePlaylistInfo?.loop_mode
      ? (baseIndex + 1 + activePlaylistItems.length) % activePlaylistItems.length
      : Math.min(baseIndex + 1, activePlaylistItems.length - 1);
    selectPlaylistItem(activePlaylistName, nextIndex);
  };

  const jumpToPlaylistItem = async () => {
    if (!activePlaylistItems.length) return;
    const defaultIndex = selectedPlaylistName === activePlaylistName && Number.isInteger(selectedPlaylistIndex)
      ? selectedPlaylistIndex + 1
      : currentFileIndex + 1;
    const answer = window.prompt(`Jump to clip number (1-${activePlaylistItems.length})`, String(defaultIndex));
    if (!answer) return;
    const targetIndex = Number(answer) - 1;
    if (!Number.isInteger(targetIndex) || targetIndex < 0 || targetIndex >= activePlaylistItems.length) {
      alert("Invalid clip number");
      return;
    }
    selectPlaylistItem(activePlaylistName, targetIndex);
    await playPlaylistItem(activePlaylistName, targetIndex, false);
  };

  const liveDestinations = ["internal", "custom", "facebook", "youtube"];

  const hasLiveOutputForChannel = (channelId) => {
    const channelStatus = status?.[channelId] || {};
    return liveDestinations.some((dest) => safeBool(channelStatus?.[dest]?.running));
  };

  const getEffectiveChannelMode = (channelId) => {
    const channelStatus = status?.[channelId] || {};
    const channelForm = form?.[channelId] || {};
    const reportedMode = String(channelStatus?.channel_mode || "").trim().toLowerCase();
    // A mode in a status snapshot only locks the UI while a path is actually
    // live.  Once stopped, prefer the saved source selection over stale
    // playlist/restreamer data from an earlier response.
    if (reportedMode && reportedMode !== "idle" &&
        (safeBool(channelStatus?.mode_locked) || hasLiveOutputForChannel(channelId))) {
      return reportedMode;
    }
    if (channelStatus?.playlist?.name) return "playlist";
    if (!hasLiveOutputForChannel(channelId)) return "idle";
    if (channelForm.dashboard_mode) return channelForm.dashboard_mode;
    if (channelForm.inputType === "playlist") return "playlist";
    if (channelForm.inputType === "url" || isRestreamInput(channelForm.input)) return "restreamer";
    return "encoder";
  };

  const isChannelLiveLocked = (channelId) => {
    const channelStatus = status?.[channelId] || {};
    const effectiveMode = getEffectiveChannelMode(channelId);
    return !!channelStatus?.mode_locked || (effectiveMode !== "idle" && hasLiveOutputForChannel(channelId));
  };

  const setDashboardModeForChannel = (channelId, mode) => {
    const locked = isChannelLiveLocked(channelId);
    const liveChannelMode = getEffectiveChannelMode(channelId);
    if (locked && liveChannelMode !== "idle" && liveChannelMode !== mode) {
      alert(`This channel is locked in ${liveChannelMode.toUpperCase()} mode while it is on air.`);
      return;
    }
    // The operating mode describes the source path.  Keep the output
    // destinations independent: starting a UDP/RTMP destination must never
    // silently turn an Encoder or Playout channel into Restreamer mode.
    const inputType = mode === "playlist" ? "playlist" : mode === "restreamer" ? "url" : "file";
    setForm((prev) => {
      const updated = {
        ...prev,
        [channelId]: {
          ...(prev[channelId] || {}),
          dashboard_mode: mode,
          inputType,
        },
      };
      formRef.current = updated;
      scheduleFormSave(updated, 150);
      return updated;
    });
  };

  const editUrlPlaylistItem = (index) => {
    const file = playlistFiles[index] || "";
    const duration = parsePlaylistDuration(playlistDurations[index]);
    setPlaylistUrlEditIndex(index);
    setPlaylistUrlInput(file);
    setPlaylistUrlStartClock(playlistItemStartClocks[index] || playlistEditorEndTimes[index - 1] || playlistStartClock);
    setPlaylistUrlDuration(duration > 0 ? formatDurationSeconds(duration, "") : "");
    setPlaylistUrlContinuous(duration <= 0);
    setPlaylistUrlEditorOpen(true);
  };

  const addUrlToPlaylist = () => {
    if (!playlistUrlInput.trim()) {
      alert("Please enter a URL");
      return;
    }
    const duration = parsePlaylistDuration(playlistUrlDuration);
    if (!playlistUrlContinuous && duration <= 0) {
      alert("Enter a valid duration such as 00:05:00 or 300 seconds");
      return;
    }
    if (Number.isInteger(playlistUrlEditIndex)) {
      setPlaylistFiles((prev) => prev.map((value, index) => index === playlistUrlEditIndex ? playlistUrlInput.trim() : value));
      setPlaylistDurations((prev) => prev.map((value, index) => index === playlistUrlEditIndex ? (playlistUrlContinuous ? 0 : duration) : value));
      setPlaylistItemStartClocks((prev) => prev.map((value, index) => index === playlistUrlEditIndex ? playlistUrlStartClock.trim() : value));
    } else {
      setPlaylistFiles(prev => [...prev, playlistUrlInput.trim()]);
      setPlaylistDurations((prev) => [...prev, playlistUrlContinuous ? 0 : duration]);
      setPlaylistItemStartClocks((prev) => [...prev, playlistUrlStartClock.trim()]);
    }
    setPlaylistUrlInput("");
    setPlaylistUrlDuration("");
    setPlaylistUrlStartClock("");
    setPlaylistUrlContinuous(true);
    setPlaylistUrlEditIndex(null);
    setPlaylistUrlEditorOpen(false);
  };

  /** ---------- Recording ---------- */
  const startRecording = async (withTrim = false) => {
    try {
      const f = formRef.current?.[selected];
      if (!f?.input && !safeBool(status?.[selected]?.internal?.running)) {
        alert("Please start encoding first or select a source file");
        return;
      }

      const fd = new FormData();
      fd.append("channel_id", selected);
      
      // Recording options
      fd.append("record_with_trim", withTrim);
      
      if (withTrim && f?.input) {
        fd.append("mark_in", f?.mark_in || "");
        fd.append("mark_out", f?.mark_out || "");
        fd.append("input", f.input);
      }

      
await axios.post("/api/channel/record/start", fd);
setShowRecordingPanel(false);

// Refresh status + recordings
const s = await axios.get("/api/channel/status");
setStatus(s.data || {});
statusRef.current = s.data || {};

    } catch (error) {
      alert("Recording failed: " + error.message);
    }
  };

  const stopRecording = async () => {
    try {
      const fd = new FormData();
      fd.append("channel_id", selected);
      
await axios.post("/api/channel/record/stop", fd);

// Refresh status + recordings
const s = await axios.get("/api/channel/status");
setStatus(s.data || {});
statusRef.current = s.data || {};

const filesRes = await axios.get("/api/video/list");
const recordFiles = filesRes.data?.files?.filter(f =>
  f.name.toLowerCase().includes(`record_${selected}`)
) || [];
setRecordings(recordFiles);
setRecordingTime(0);

    } catch (error) {
      alert("Failed to stop recording: " + error.message);
    }
  };

  const deleteRecording = async (fileName) => {
    if (!window.confirm(`Delete recording "${fileName}"?`)) return;
    try {
      await axios.delete(`/api/video/delete?filename=${encodeURIComponent(fileName)}`);
      setRecordings(prev => prev.filter(r => r.name !== fileName));
      alert("Recording deleted");
    } catch (error) {
      alert("Failed to delete recording: " + error.message);
    }
  };

  /** ---------- Mark Management ---------- */
  const captureMark = (ch, type) => {
    const vid = vodRefs.current[ch];
    if (vid && !isNaN(vid.currentTime)) {
      const t = Math.floor(vid.currentTime);
      const h = String(Math.floor(t / 3600)).padStart(2, "0");
      const m = String(Math.floor((t % 3600) / 60)).padStart(2, "0");
      const s = String(t % 60).padStart(2, "0");
      const val = `${h}:${m}:${s}`;
      updateField(ch, type === "in" ? "mark_in" : "mark_out", val);
      alert(`${type === "in" ? "IN" : "OUT"} mark set to ${val}`);
    } else {
      alert("No video loaded or video not ready");
    }
  };

  const saveMarks = async (ch) => {
    const f = formRef.current?.[ch];
    if (!f?.input) return alert("No file selected");
    const data = new FormData();
    data.append("channel_id", ch);
    data.append("input", f.input);
    data.append("mark_in", f.mark_in || "");
    data.append("mark_out", f.mark_out || "");
    try { 
      await axios.post("/api/mark/save", data); 
      alert("Marks saved successfully!");
    } catch { alert("Failed to save marks"); }
  };

  const loadMarksForInput = async (ch, absPath) => {
    try {
      const d = new FormData();
      d.append("input", absPath);
      const res = await axios.post("/api/mark/load", d);
      const { mark_in = "", mark_out = "" } = res.data || {};
      updateField(ch, "mark_in", mark_in);
      updateField(ch, "mark_out", mark_out);
    } catch {}
  };

  /** ---------- Stream Control ---------- */
  const updateScroll = async (ch) => {
  const f = formRef.current?.[ch];
  if (!f) return;
  try {
    await axios.post("/api/update-scroll", {
      channel_id: ch,
      text: f.scroll_text || "",
      scroll_font: f.scroll_font,
      scroll_font_size: f.scroll_font_size,
      scroll_font_color: f.scroll_font_color,
      scroll_bg_color: f.scroll_bg_color,
      scroll_enable_bg: f.scroll_enable_bg,
      scroll_bg_alpha: f.scroll_bg_alpha,
      scroll_speed: f.scroll_speed,
      scroll_y: f.scroll_y,
    });
    alert("Scroll updated live");
  } catch (e) {
    console.error(e);
    alert("Scroll update failed");
  }
};

  const openTickerEditor = () => {
    const cur = formRef.current?.[selected] || {};
    setTickerDraft({
      text: cur.scroll_text || "",
      font: cur.scroll_font || fontOptions[0] || FONT_OPTIONS[0],
      font_size: Number(cur.scroll_font_size || 48),
      color: cur.scroll_font_color || "#ffffff",
      bg_color: cur.scroll_bg_color || "#000000",
      enable_bg: cur.scroll_enable_bg !== false,
      bg_alpha: Number(cur.scroll_bg_alpha || 0.65),
      speed: Number(cur.scroll_speed || 40),
      y: Number(cur.scroll_y || 1030),
    });
    setShowTickerModal(true);
  };

  const saveTickerEditor = async () => {
    try {
      await axios.post("/api/update-ticker", { channel_id: selected, ...tickerDraft });
      updateField(selected, "scroll_text", tickerDraft.text);
      updateField(selected, "scroll_font", tickerDraft.font);
      updateField(selected, "scroll_font_size", tickerDraft.font_size);
      updateField(selected, "scroll_font_color", tickerDraft.color);
      updateField(selected, "scroll_bg_color", tickerDraft.bg_color);
      updateField(selected, "scroll_enable_bg", tickerDraft.enable_bg);
      updateField(selected, "scroll_bg_alpha", tickerDraft.bg_alpha);
      updateField(selected, "scroll_speed", tickerDraft.speed);
      updateField(selected, "scroll_y", tickerDraft.y);
      setShowTickerModal(false);
      alert("Ticker updated live");
    } catch (e) {
      alert("Ticker update failed: " + (e?.response?.data?.error || e.message));
    }
  };

  const startInternal = async (ch, options = {}) => {
    const { silent = false } = options;
    let f = formRef.current?.[ch];
    if (!f?.input) return alert("Select a source first");
    
    try {
      await persistChannelForm(ch);
      f = formRef.current?.[ch];
      const sourceMode = f.inputType === "url" || isRestreamInput(f.input) ? "restreamer" : "encoder";
      const payload = new FormData();
      payload.append("channel_id", ch);
      payload.append("dest", "internal");
      payload.append("input", f.input.replace(/\s+/g, ""));
      payload.append("dashboard_mode", sourceMode);
      payload.append("inputType", sourceMode === "restreamer" ? "url" : "file");
      payload.append("save_state_flag", f.save_state !== false);
      
      // Add all encoder settings
      Object.entries(f).forEach(([k, v]) => {
        if (k === "input" || k === "inputType" || k === "mode" || k === "save_state") return;
        if (k === "additional_logos") payload.append(k, JSON.stringify(v || []));
        else if (v !== undefined && v !== null) payload.append(k, v);
      });
      
      await axios.post("/api/channel/start", payload);
      bumpHls(ch);
      updateField(ch, "dashboard_mode", sourceMode);
      updateField(ch, "inputType", sourceMode === "restreamer" ? "url" : "file");
      updateField(ch, "mode", "hls");
      if (!silent) alert("Encoder started successfully!");
    } catch (error) {
      console.error("Start encoder failed:", error);
      if (!silent) alert("Failed to start encoder: " + (error.response?.data?.error || error.message));
      else throw error;
    }
  };

  const inspectYoutubeSource = async (ch) => {
    const channelForm = formRef.current?.[ch] || {};
    if (!/youtube\.com|youtu\.be/i.test(channelForm.input || "")) {
      alert("Enter a YouTube link first");
      return;
    }
    try {
      const response = await axios.post("/api/youtube/info", { url: channelForm.input });
      const info = response.data || {};
      const sourceKind = info.source_kind === "video" ? "video" : "live";
      updateField(ch, "youtube_source_kind", sourceKind);
      updateField(ch, "youtube_duration", Number(info.duration || 0));
      updateField(ch, "youtube_title", info.title || "");
      if (sourceKind === "live") updateField(ch, "youtube_anchor_seconds", 0);
    } catch (error) {
      alert("Could not inspect the YouTube link: " + (error?.response?.data?.error || error.message));
    }
  };

  const toggleStream = async (ch, dest) => {
    const st = statusRef.current?.[ch]?.[dest];
    const running = safeBool(st?.running);
    let f = formRef.current?.[ch];
    
    if (dest === "internal" && !f?.input) {
      alert("Select a source first");
      return;
    }
    
    try {
      const payload = new FormData();
      payload.append("channel_id", ch);
      payload.append("dest", dest);
      payload.append("save_state_flag", f?.save_state !== false);
      
      if (running) {
        await axios.post("/api/channel/stop", payload);
        alert(`${dest} stream stopped`);
      } else {
        await persistChannelForm(ch);
        f = formRef.current?.[ch];
        const internalRunning = safeBool(statusRef.current?.[ch]?.internal?.running);
        if (dest !== "internal" && !internalRunning) {
          if (!f?.input) {
            alert("Select and start a source first");
            return;
          }
          await startInternal(ch, { silent: true });
          await new Promise((resolve) => setTimeout(resolve, 1200));
        }
        if (dest === "internal") {
          // For internal streams, send all form data
          payload.append("input", f.input.replace(/\s+/g, ""));
          const sourceMode = f.inputType === "url" || isRestreamInput(f.input) ? "restreamer" : "encoder";
          payload.append("dashboard_mode", sourceMode);
          payload.append("inputType", sourceMode === "restreamer" ? "url" : "file");
          
          Object.entries(f).forEach(([k, v]) => {
            if (k === "input" || k === "inputType" || k === "mode" || k === "save_state") return;
            if (k === "additional_logos") payload.append(k, JSON.stringify(v || []));
            else if (!["facebook_key", "youtube_key", "custom_url"].includes(k) && v !== undefined && v !== null) {
              payload.append(k, v);
            }
          });
        } else {
          // For external streams
          if (dest === "facebook") payload.append("facebook_key", f.facebook_key || "");
          else if (dest === "youtube") payload.append("youtube_key", f.youtube_key || "");
          else if (dest === "custom") {
            payload.append("custom_url", f.custom_url || "");
            if (f.custom_url?.toLowerCase().startsWith("udp://")) {
              [
                "udp_profile",
                "udp_muxrate",
                "udp_rate_control",
                "udp_video_bitrate",
                "udp_audio_bitrate",
                "udp_minrate",
                "udp_maxrate",
                "udp_video_bufsize",
                "udp_gop_size",
                "udp_keyint_min",
                "udp_bframes",
                "udp_service_name",
                "udp_service_provider",
                "udp_service_id",
                "udp_transport_stream_id",
                "udp_original_network_id",
                "udp_pmt_pid",
                "udp_start_pid",
                "udp_video_pid",
                "udp_audio_pid",
                "udp_pcr_period",
                "udp_pat_period",
                "udp_sdt_period",
                "udp_pkt_size",
                "udp_ttl",
                "udp_buffer_size",
                "udp_fifo_size",
                "udp_system_b",
                "udp_resend_headers",
              ].forEach((key) => payload.append(key, f[key] ?? ""));
            }
          }
        }
        
      await axios.post("/api/channel/start", payload);
        // Destination selection is not a source-mode change.  In particular,
        // a Playlist's multicast destination must leave the Playout dashboard
        // selected, and a local file encoder must stay Encoder.
        alert(`${dest} stream started`);
      }
    } catch (error) {
      alert("Action failed: " + error.message);
    }
  };

  const forceStop = async (ch) => {
    if (!window.confirm("Force stop all streams for this channel?")) return;
    try {
      await axios.post("/api/channel/force-stop", { channel_id: ch });
      await axios.post("/api/channel/cleanup", { channel_id: ch });
      alert("Channel force stopped and cleaned");
    } catch { alert("Force stop failed"); }
  };

  const restartSystem = async () => {
    if (!window.confirm("Restart the backend service?")) return;
    try { 
      await axios.post("/api/system/restart"); 
      alert("System restart initiated");
    } catch { alert("Restart failed"); }
  };

  const clearChannel = async (ch) => {
    if (!window.confirm("Clear all settings for this channel?")) return;
    try {
      await axios.post("/api/channel/clear-settings", { channel_id: ch });
      setForm((prev) => {
        const updated = { ...prev, [ch]: { ...DEFAULTS[ch] } };
        formRef.current = updated;
        return updated;
      });
      alert("Channel settings cleared");
    } catch {
      alert("Failed to clear settings");
    }
  };

  // Removed unused checkInputHealth function
  // const checkInputHealth = async (ch) => {
  //   try {
  //     const res = await axios.get(`/api/health/input/${ch}`);
  //     return res.data;
  //   } catch (error) {
  //     console.error("Health check failed:", error);
  //     return null;
  //   }
  // };

  /** ---------- UI State ---------- */
  const ch = selected;
  const f = form[ch] || DEFAULTS[ch];
  const st = status?.[ch] || {};
  const systemStats = healthStatus?.system || {};
  const hasSystemStats = ["cpu_percent", "memory_percent", "disk_percent"].some((key) => systemStats[key] !== undefined && systemStats[key] !== null);
  const systemMeters = [
    {
      key: "cpu",
      label: "CPU",
      icon: Cpu,
      percent: clampPercent(systemStats.cpu_percent),
      detail: hasSystemStats ? `${Math.round(clampPercent(systemStats.cpu_percent))}%` : "Loading",
      tone: "var(--meter-cpu)",
      sub: "processor"
    },
    {
      key: "memory",
      label: "RAM",
      icon: Server,
      percent: clampPercent(systemStats.memory_percent),
      detail: systemStats.memory_total ? `${formatBytes(systemStats.memory_used)} / ${formatBytes(systemStats.memory_total)}` : (hasSystemStats ? `${Math.round(clampPercent(systemStats.memory_percent))}%` : "Loading"),
      tone: "var(--meter-memory)",
      sub: "memory"
    },
    {
      key: "disk",
      label: "Disk",
      icon: HardDrive,
      percent: clampPercent(systemStats.disk_percent),
      detail: systemStats.disk_total ? `${formatBytes(systemStats.disk_used)} / ${formatBytes(systemStats.disk_total)}` : (hasSystemStats ? `${Math.round(clampPercent(systemStats.disk_percent))}%` : "Loading"),
      tone: "var(--meter-disk)",
      sub: "storage"
    }
  ];
  
  const activeCount = useMemo(() => {
    return Object.values(status || {}).reduce((acc, chObj) => {
      return acc + Object.values(chObj || {}).filter((d) => d?.running).length;
    }, 0);
  }, [status]);

  const isPlaylistActive = useMemo(() => {
    return activePlaylistInfo?.name || false;
  }, [activePlaylistInfo]);

  // Scheduled start-time strings for each clip, computed from playlistStartClock.
  const playlistEditorStartTimes = useMemo(() => {
    let acc = parseClockToSeconds(playlistStartClock || "00:00:00");
    return playlistFiles.map((file, index) => {
      const manualStart = parseClockToSeconds(playlistItemStartClocks[index]);
      if (playlistItemStartClocks[index]) acc = manualStart;
      const st = formatClockFromSeconds(acc);
      const meta = libraryFileMap[file] || libraryFileMap[String(file).split("/").pop()] || null;
      const isRemote = /^(https?|rtmp|rtmps|udp|srt|rtsp):\/\//i.test(file) || /youtube\.com|youtu\.be/i.test(file);
      const duration = isRemote
        ? parsePlaylistDuration(playlistDurations[index])
        : parseClockToSeconds(meta?.duration_str) || 0;
      acc += duration;
      return st;
    });
  }, [playlistFiles, playlistDurations, playlistItemStartClocks, playlistStartClock, libraryFileMap]);

  const playlistEditorEndTimes = useMemo(() => {
    let acc = parseClockToSeconds(playlistStartClock || "00:00:00");
    return playlistFiles.map((file, index) => {
      const manualStart = parseClockToSeconds(playlistItemStartClocks[index]);
      if (playlistItemStartClocks[index]) acc = manualStart;
      const meta = libraryFileMap[file] || libraryFileMap[String(file).split("/").pop()] || null;
      const isRemote = /^(https?|rtmp|rtmps|udp|srt|rtsp):\/\//i.test(file) || /youtube\.com|youtu\.be/i.test(file);
      const duration = isRemote
        ? parsePlaylistDuration(playlistDurations[index])
        : parseClockToSeconds(meta?.duration_str) || 0;
      acc += duration;
      return formatClockFromSeconds(acc);
    });
  }, [playlistFiles, playlistDurations, playlistItemStartClocks, playlistStartClock, libraryFileMap]);

  const playlistEditorTotalDuration = useMemo(() => {
    return playlistFiles.reduce((total, file, index) => {
      const meta = libraryFileMap[file] || libraryFileMap[String(file).split("/").pop()] || null;
      const isRemote = /^(https?|rtmp|rtmps|udp|srt|rtsp):\/\//i.test(file) || /youtube\.com|youtu\.be/i.test(file);
      const configured = parsePlaylistDuration(playlistDurations[index]);
      return total + (configured || (!isRemote ? parseClockToSeconds(meta?.duration_str) : 0));
    }, 0);
  }, [playlistFiles, playlistDurations, libraryFileMap]);

  const activePlaylistStartTimes = useMemo(
    () => computePlaylistStartTimes(activePlaylistInfo?.items, playlistStartClock),
    [activePlaylistInfo, playlistStartClock]
  );

  const playlistInspectorStartTimes = useMemo(
    () => computePlaylistStartTimes(playlists?.[playlistInspectorName]?.items, playlistStartClock),
    [playlists, playlistInspectorName, playlistStartClock]
  );

  const allPlaylistStartTimes = useMemo(() => {
    const result = {};
    Object.entries(playlists || {}).forEach(([name, pl]) => {
      result[name] = computePlaylistStartTimes(pl.items || [], playlistStartClock);
    });
    return result;
  }, [playlists, playlistStartClock]);

  const activePlaylistName = activePlaylistInfo?.name || "";
  const activePlaylistItems = activePlaylistInfo?.items || [];
  const activePlaylistCount = activePlaylistItems.length || activePlaylistInfo?.files?.length || 0;
  const activeLoopItem = !!activePlaylistInfo?.loop_item;
  const playlistInspector = playlistInspectorName ? playlists?.[playlistInspectorName] : null;
  const playlistPreviewFilename = playlistPreviewFile?.name || playlistPreviewFile?.path?.split("/").pop() || "";
  const playlistPreviewSrc = playlistPreviewFilename ? `/vod/${encodeURIComponent(playlistPreviewFilename)}` : "";
  const filteredPlaylistFiles = useMemo(() => {
    const query = playlistMediaSearch.trim().toLowerCase();
    return query ? serverFiles.filter((file) => String(file.name || "").toLowerCase().includes(query)) : serverFiles;
  }, [serverFiles, playlistMediaSearch]);
  const trimModalFilename = trimModalItem?.path ? encodeURIComponent((trimModalItem.path || "").split("/").pop()) : "";
  const trimModalSrc = trimModalFilename ? `/vod/${trimModalFilename}` : "";
  const liveMode = getEffectiveChannelMode(ch) || (isPlaylistActive ? "playlist" : "idle");
  const channelModeLocked = isChannelLiveLocked(ch);
  const currentDashboardMode = useMemo(() => {
    if (channelModeLocked && liveMode !== "idle") return liveMode;
    if (isPlaylistActive) return "playlist";
    return f.dashboard_mode || (f.inputType === "playlist" ? "playlist" : f.inputType === "url" ? "restreamer" : "encoder");
  }, [channelModeLocked, liveMode, isPlaylistActive, f.dashboard_mode, f.inputType]);
  const currentPlaylistItem = activePlaylistItems[currentFileIndex] || activePlaylistInfo?.now_playing || null;
  const nextPlaylistItem = activePlaylistInfo?.next_item || activePlaylistItems[currentFileIndex + 1] || (activePlaylistInfo?.loop_mode && activePlaylistItems.length ? activePlaylistItems[(currentFileIndex + 1) % activePlaylistItems.length] : null);
  const cuePlaylistItem = activePlaylistInfo?.cue_item || activePlaylistItems[currentFileIndex + 2] || (activePlaylistInfo?.loop_mode && activePlaylistItems.length > 1 ? activePlaylistItems[(currentFileIndex + 2) % activePlaylistItems.length] : null);
  const channelDisplayName = (f.channel_name || "").trim() || defaultChannelName(ch);
  const beginChannelNameEdit = () => {
    channelNameCancelRef.current = false;
    setChannelNameDraft(channelDisplayName);
    setEditingChannelName(true);
  };
  const saveChannelNameEdit = () => {
    const nextName = channelNameDraft.trim() || defaultChannelName(ch);
    updateField(ch, "channel_name", nextName);
    setEditingChannelName(false);
  };
  const handleChannelNameBlur = () => {
    if (channelNameCancelRef.current) {
      channelNameCancelRef.current = false;
      return;
    }
    saveChannelNameEdit();
  };
  const selectedPlaylistItem = selectedPlaylistName === activePlaylistName && Number.isInteger(selectedPlaylistIndex)
    ? activePlaylistItems[selectedPlaylistIndex] || null
    : null;
  const sourceInputType = currentDashboardMode === "playlist" ? "playlist" : currentDashboardMode === "restreamer" ? "url" : f.inputType;
  const isYoutubeSource = /youtube\.com|youtu\.be/i.test(f.input || "");
  const isRecordedYoutube = isYoutubeSource && f.youtube_source_kind === "video";
  const youtubeDuration = Math.max(0, Number(f.youtube_duration || 0));
  const youtubeAnchor = Math.min(Math.max(0, Number(f.youtube_anchor_seconds || 0)), youtubeDuration || 0);
  const formatYoutubeTime = (seconds) => {
    const total = Math.max(0, Math.floor(Number(seconds || 0)));
    const h = String(Math.floor(total / 3600)).padStart(2, "0");
    const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
    const s = String(total % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };
  const showSourceSection = true;
  const showEncoderSection = currentDashboardMode !== "playlist";
  const showGraphicsSection = true;
  const showDestinationsSection = true;
  const overlayCanvasPreviewHeight = 250;
  const overlayCanvasPreviewWidth = Math.round((OVERLAY_CANVAS_WIDTH / OVERLAY_CANVAS_HEIGHT) * overlayCanvasPreviewHeight);
  const overlayCanvasScale = overlayCanvasPreviewWidth / OVERLAY_CANVAS_WIDTH;
  const overlayLayers = useMemo(() => {
    const base = [];
    base.push({
      key: "main",
      type: "main",
      index: null,
      label: "Main",
      logo: f.overlay_logo || "",
      enabled: !!f.overlay_logo && f.overlay_logo_enabled !== false,
      x: parsePosition(f.logo_x, 1700),
      y: parsePosition(f.logo_y, 75),
      w: parsePosition(f.logo_width, 0),
      h: parsePosition(f.logo_height, 0),
    });
    (f.additional_logos || []).forEach((row, idx) => {
      base.push({
        key: `layer-${idx}`,
        type: "layer",
        index: idx,
        label: `Layer ${idx + 1}`,
        logo: row.logo || "",
        enabled: !!row.logo && row.enabled !== false,
        x: parsePosition(row.x, 0),
        y: parsePosition(row.y, 0),
        w: parsePosition(row.w, 0),
        h: parsePosition(row.h, 0),
      });
    });
    return base;
  }, [f.overlay_logo, f.overlay_logo_enabled, f.logo_x, f.logo_y, f.logo_width, f.logo_height, f.additional_logos]);

  const applyOverlayCanvasPosition = useCallback((target, nextX, nextY) => {
    const clampedX = Math.max(0, Math.min(OVERLAY_CANVAS_WIDTH, Math.round(nextX)));
    const clampedY = Math.max(0, Math.min(OVERLAY_CANVAS_HEIGHT, Math.round(nextY)));
    if (target.type === "main") {
      updateField(ch, "logo_x", clampedX);
      updateField(ch, "logo_y", clampedY);
      return;
    }
    if (Number.isInteger(target.index)) {
      const list = [...(formRef.current?.[ch]?.additional_logos || [])];
      if (!list[target.index]) return;
      list[target.index] = { ...list[target.index], x: clampedX, y: clampedY };
      updateField(ch, "additional_logos", list);
    }
  }, [ch, updateField]);

  const applyOverlayCanvasSize = useCallback((target, nextW, nextH) => {
    let clampedW = Math.max(24, Math.min(OVERLAY_CANVAS_WIDTH, Math.round(nextW)));
    let clampedH = Math.max(24, Math.min(OVERLAY_CANVAS_HEIGHT, Math.round(nextH)));
    if (isOverlaySizeLocked(target.type, target.index)) {
      const ratio = target.startW && target.startH ? target.startH / target.startW : 80 / 220;
      clampedH = Math.max(24, Math.min(OVERLAY_CANVAS_HEIGHT, Math.round(clampedW * ratio)));
    }
    if (target.type === "main") {
      updateField(ch, "logo_width", clampedW);
      updateField(ch, "logo_height", clampedH);
      return;
    }
    if (Number.isInteger(target.index)) {
      const list = [...(formRef.current?.[ch]?.additional_logos || [])];
      if (!list[target.index]) return;
      list[target.index] = { ...list[target.index], w: clampedW, h: clampedH };
      updateField(ch, "additional_logos", list);
    }
  }, [ch, updateField, isOverlaySizeLocked]);

  const startOverlayDrag = useCallback((event, target) => {
    event.preventDefault();
    event.stopPropagation();
    setOverlayCanvasSelection(target);
    const source = target.type === "main"
      ? { x: parsePosition(f.logo_x, 1700), y: parsePosition(f.logo_y, 75) }
      : {
          x: parsePosition(f.additional_logos?.[target.index]?.x, 0),
          y: parsePosition(f.additional_logos?.[target.index]?.y, 0),
        };
    overlayDragStateRef.current = {
      target,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: source.x,
      startY: source.y,
    };
  }, [f.logo_x, f.logo_y, f.additional_logos]);

  const startOverlayResize = useCallback((event, target) => {
    event.preventDefault();
    event.stopPropagation();
    setOverlayCanvasSelection(target);
    const source = target.type === "main"
      ? { w: parsePosition(f.logo_width, 220), h: parsePosition(f.logo_height, 80) }
      : {
          w: parsePosition(f.additional_logos?.[target.index]?.w, 220),
          h: parsePosition(f.additional_logos?.[target.index]?.h, 80),
        };
    overlayResizeStateRef.current = {
      target: { ...target, startW: source.w > 0 ? source.w : 220, startH: source.h > 0 ? source.h : 80 },
      startClientX: event.clientX,
      startClientY: event.clientY,
      startW: source.w > 0 ? source.w : 220,
      startH: source.h > 0 ? source.h : 80,
    };
  }, [f.logo_width, f.logo_height, f.additional_logos]);

  useEffect(() => {
    const onMove = (event) => {
      const drag = overlayDragStateRef.current;
      if (drag) {
        const deltaX = (event.clientX - drag.startClientX) / overlayCanvasScale;
        const deltaY = (event.clientY - drag.startClientY) / overlayCanvasScale;
        const nextX = drag.startX + deltaX;
        const nextY = drag.startY + deltaY;
        applyOverlayCanvasPosition(drag.target, nextX, nextY);
      }
      const resize = overlayResizeStateRef.current;
      if (resize) {
        const deltaX = (event.clientX - resize.startClientX) / overlayCanvasScale;
        const deltaY = (event.clientY - resize.startClientY) / overlayCanvasScale;
        applyOverlayCanvasSize(resize.target, resize.startW + deltaX, resize.startH + deltaY);
      }
    };
    const onUp = () => {
      const wasDragging = overlayDragStateRef.current || overlayResizeStateRef.current;
      overlayDragStateRef.current = null;
      overlayResizeStateRef.current = null;
      if (wasDragging && formRef.current) {
        // Flush form save immediately (no debounce)
        scheduleFormSave(formRef.current, 0);
        // Also push overlay changes directly to the live API
        const currentForm = formRef.current[ch];
        if (currentForm) {
          axios.post("/api/channel/overlay/live", {
            channel_id: ch,
            overlay_logo: currentForm.overlay_logo || "",
            overlay_logo_enabled: currentForm.overlay_logo_enabled !== false,
            logo_x: currentForm.logo_x ?? 10,
            logo_y: currentForm.logo_y ?? 10,
            logo_width: currentForm.logo_width ?? 0,
            logo_height: currentForm.logo_height ?? 0,
            additional_logos: currentForm.additional_logos || [],
          }).catch(() => {});
        }
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [applyOverlayCanvasPosition, applyOverlayCanvasSize, overlayCanvasScale, ch, scheduleFormSave]);

  useEffect(() => {
    if (activePlaylistInfo?.current_index != null) {
      setCurrentFileIndex(activePlaylistInfo.current_index);
    }
  }, [activePlaylistInfo]);

  useEffect(() => {
    if (!activePlaylistName) {
      setSelectedPlaylistName("");
      setSelectedPlaylistIndex(null);
      return;
    }
    if (selectedPlaylistName !== activePlaylistName || !Number.isInteger(selectedPlaylistIndex)) {
      setSelectedPlaylistName(activePlaylistName);
      setSelectedPlaylistIndex(activePlaylistInfo?.current_index ?? 0);
    }
  }, [activePlaylistName, activePlaylistInfo?.current_index, selectedPlaylistIndex, selectedPlaylistName]);

  useEffect(() => {
    const drafts = {};
    Object.values(playlists || {}).forEach((pl) => {
      (pl?.items || []).forEach((item) => {
        if (item?.path) {
          drafts[item.path] = {
            mark_in: item.mark_in || "",
            mark_out: item.mark_out || "",
          };
        }
      });
    });
    setPlaylistMarksDraft((prev) => ({ ...drafts, ...prev }));
  }, [playlists]);

  useEffect(() => {
    if (!trimModalItem) return;
    const timer = requestAnimationFrame(() => {
      trimModalShellRef.current?.focus?.();
    });
    return () => {
      trimModalPrimedRef.current = false;
      cancelAnimationFrame(timer);
    };
  }, [trimModalItem, trimModalSrc]);

  useEffect(() => {
    if (!trimModalItem) return undefined;
    const handleTrimHotkeys = (event) => {
      const targetTag = event.target?.tagName;
      // Allow hotkeys when the seekbar (input[type=range]) is focused, but
      // block them for text inputs and textareas so normal typing still works.
      if (targetTag === "TEXTAREA") return;
      if (targetTag === "INPUT" && event.target?.type !== "range") return;
      const key = String(event.key || "").toLowerCase();
      const code = String(event.code || "");
      if (key === "i" || code === "KeyI") {
        event.preventDefault();
        event.stopPropagation();
        captureTrimModalMark("in");
      } else if (key === "o" || code === "KeyO") {
        event.preventDefault();
        event.stopPropagation();
        captureTrimModalMark("out");
      } else if (event.key === "Enter" || code === "Enter" || code === "NumpadEnter") {
        event.preventDefault();
        event.stopPropagation();
        saveTrimModalMarks();
      }
    };
    window.addEventListener("keydown", handleTrimHotkeys, true);
    return () => window.removeEventListener("keydown", handleTrimHotkeys, true);
  }, [saveTrimModalMarks, trimModalItem, trimModalMarks.mark_in, trimModalMarks.mark_out]);

  // Format recording time
  const formatRecordingTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Get health indicator
  const getHealthIndicator = (channel, dest = 'input') => {
    const health = channelHealth[channel]?.[dest];
    if (!health) return null;
    
    return {
      healthy: health.healthy,
      message: health.message,
      lastCheck: health.last_check
    };
  };

  /** =========================
   * Login Screen
   * ========================= */
  if (licenseInfo && licenseInfo.required !== false && !licenseInfo.valid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 to-black flex items-center justify-center p-4">
        <div className="w-full max-w-2xl bg-gray-900/90 border border-red-500/30 rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center gap-3 mb-4"><Lock className="h-8 w-8 text-red-400" /><div><h2 className="text-2xl font-bold text-white">LoopLayer License Expired or Invalid</h2><p className="text-sm text-gray-400">License is controlled remotely from the LoopLayer license server.</p></div></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-6">
            <div className="bg-black/40 rounded-xl p-3 border border-gray-800"><div className="text-gray-500 text-xs uppercase">MAC ID</div><div className="font-mono text-cyan-300">{licenseInfo.mac_address || licenseInfo.instance_id || "--"}</div></div>
            <div className="bg-black/40 rounded-xl p-3 border border-gray-800"><div className="text-gray-500 text-xs uppercase">Hostname</div><div className="font-mono text-gray-200">{licenseInfo.hostname || "--"}</div></div>
            <div className="bg-black/40 rounded-xl p-3 border border-gray-800"><div className="text-gray-500 text-xs uppercase">Days Left</div><div className="font-mono text-amber-300">{licenseInfo.days_left ?? "--"}</div></div>
            <div className="bg-black/40 rounded-xl p-3 border border-gray-800"><div className="text-gray-500 text-xs uppercase">Expires</div><div className="font-mono text-gray-200">{formatLicenseDateTime(licenseInfo.license_expires_at)}</div></div>
            <div className="bg-black/40 rounded-xl p-3 border border-gray-800 md:col-span-2"><div className="text-gray-500 text-xs uppercase">Message</div><div className="text-red-300">{licenseInfo.message || "License not valid"}</div></div>
          </div>
          <button onClick={() => refreshLicenseInfo(true)} className="px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold">Refresh License</button>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 to-black flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-900/20 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-900/20 blur-[120px] rounded-full pointer-events-none" />

        <div className="relative z-10 w-full max-w-md bg-gray-900/80 backdrop-blur-xl border border-gray-800 rounded-2xl p-8 shadow-2xl">
          <div className="flex justify-center mb-6">
            <img src={prostreamLogo} alt="ProStream" className="h-24 object-contain" />
          </div>
          <h2 className="text-2xl font-bold text-center text-white mb-1">ProStream Transcoder v5.1</h2>
          <p className="text-gray-400 text-center text-sm mb-8">Professional Streaming Platform with Auto-Restore</p>

          <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
            <div className="mb-6 space-y-2">
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                <Lock className="h-3 w-3" /> Administrator Password
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  className={`${UI.input} pl-10`}
                  placeholder="Enter administrator password"
                  autoFocus
                />
                <Lock className="absolute left-3 top-3.5 h-4 w-4 text-gray-500" />
              </div>
              <p className="text-xs text-gray-500 mt-2">For Password Call +91-7508003477</p>
            </div>

            <button type="submit" className={`w-full py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${UI.btnPrimary}`}>
              <Lock className="h-4 w-4" /> Unlock Dashboard
            </button>
          </form>
        </div>
      </div>
    );
  }

  /** =========================
   * Main Dashboard
   * ========================= */
  return (
    <div className={`min-h-screen flex flex-col ${UI.bg}`}>
      <style>{`
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: rgba(30, 41, 59, 0.3); }
        ::-webkit-scrollbar-thumb { background: rgba(100, 116, 139, 0.5); border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(100, 116, 139, 0.7); }
        .animate-pulse-slow { animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .5; } }
        .glass-effect { backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); }
        .gradient-border { border: 2px solid transparent; background: linear-gradient(black, black) padding-box, linear-gradient(45deg, #3b82f6, #8b5cf6) border-box; }
        @media (max-width: 768px) {
          .mobile-stack { flex-direction: column !important; }
          .mobile-full { width: 100% !important; }
          .mobile-hidden { display: none !important; }
        }
      `}</style>

      {/* --- Header --- */}
      <header className="sticky top-0 z-50 bg-gray-900/90 backdrop-blur-xl border-b border-gray-800 shadow-xl">
        <div className="app-header-inner max-w-[1920px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between mobile-stack">
          <div className="app-brand flex items-center gap-4">
            <img src={prostreamLogo} alt="ProStream" className="h-8 sm:h-10 object-contain" />
            <div>
              <h1 className="font-bold text-lg sm:text-xl leading-tight bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                ProStream <span className="text-white">Transcoder v5.1</span>
              </h1>
              <div className="text-xs font-mono text-gray-400 flex items-center gap-2">
                <span>Auto-Restore Enabled</span>
                <span className="w-1 h-1 rounded-full bg-gray-600" />
                <span className={activeCount > 0 ? "text-emerald-400 animate-pulse flex items-center gap-1" : "text-gray-500"}>
                  <Activity className="h-3 w-3" /> {activeCount} Active
                </span>
              </div>
            </div>
          </div>

          <div className="app-header-actions flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
            {isPlaylistActive && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-emerald-500/20 rounded-lg border border-emerald-500/30">
                <List className="h-3.5 w-3.5 text-emerald-400" />
                <span className="text-xs text-emerald-300">{activePlaylistName}</span>
                <span className="text-xs text-gray-400">
                  Clip {Math.min(currentFileIndex + 1, Math.max(activePlaylistCount, 1))}/{activePlaylistCount}
                </span>
              </div>
            )}
            {isRecording && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 rounded-lg border border-red-500/30 animate-pulse">
                <Circle className="h-5 w-5 text-red-400 fill-current" />
                <span className="text-xs text-red-300 font-mono">{formatRecordingTime(recordingTime)}</span>
              </div>
            )}
            <button onClick={() => refreshLicenseInfo(true)} className="px-3 py-2 rounded-xl text-xs font-semibold bg-emerald-600/15 text-emerald-300 border border-emerald-500/20" title={`${licenseInfo?.message || "License"} | Expires: ${formatLicenseDateTime(licenseInfo?.license_expires_at)}`}>LIC {licenseInfo?.days_left ?? "--"}d</button>
            <button onClick={openTickerEditor} className={`px-3 sm:px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition ${UI.btnPurple}`}><Edit3 className="h-4 w-4" /> <span className="mobile-hidden">Ticker</span></button>
            <button onClick={() => setShowPlaylistManager(true)} className={`px-3 sm:px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition ${UI.btnNeutral}`}>
              <List className="h-4 w-4" /> <span className="mobile-hidden">Playlists</span>
            </button>
            <button onClick={() => setShowRecordingPanel(true)} className={`px-3 sm:px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition ${UI.btnDanger}`}>
              <Video className="h-4 w-4" /> <span className="mobile-hidden">Record</span>
            </button>
            <button onClick={restartSystem} className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition ${UI.btnWarning}`}>
              <RefreshCw className="h-4 w-4" /> Restart
            </button>
            <button onClick={handleLogout} className="ml-2 p-2.5 rounded-xl hover:bg-gray-800 text-gray-400 hover:text-white transition border border-gray-700">
              <Lock className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Channel Navigation Bar */}
        <div className="bg-gray-900/50 border-t border-gray-800">
          <div className="max-w-[1920px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4 mobile-stack">
            {/* Channel Selector */}
            <div className="flex items-center gap-2 overflow-x-auto pt-2 pb-3 w-full">
              {DEFAULT_CHANNELS.map((c) => {
                const isActive = c === selected;
                const isRunning = hasLiveOutputForChannel(c);
                const isLocked = isChannelLiveLocked(c);
                const inputHealth = getHealthIndicator(c, 'input');
                
                return (
                  <button
                    key={c}
                    onClick={() => setSelected(c)}
                    className={`
                      relative px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 whitespace-nowrap
                      flex items-center gap-2 border-2 flex-shrink-0
                      ${isActive 
                        ? "gradient-border bg-black text-white shadow-lg shadow-blue-500/30" 
                        : "bg-gray-800/50 text-gray-400 border-gray-700 hover:border-gray-600 hover:text-gray-200 hover:bg-gray-800"
                      }
                    `}
                  >
                    <Radio className="h-3.5 w-3.5" />
                    {fmtLabel(c)}
                    {isLocked && (
                      <Lock className="h-3 w-3 text-amber-300" />
                    )}
                    {isRunning && (
                      <span className="absolute top-1 right-1 h-2.5 w-2.5 rounded-full bg-emerald-400 live-glow-dot" />
                    )}
                    {inputHealth && !inputHealth.healthy && (
                      <span className="absolute top-1 left-1 h-2.5 w-2.5 rounded-full bg-red-400 shadow-[0_0_10px_rgba(239,68,68,0.8)]" />
                    )}
                  </button>
                );
              })}
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3 w-full justify-between mt-2 sm:mt-0">
              <button 
                onClick={() => setMatrixMode(!matrixMode)}
                className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors flex items-center gap-2 ${matrixMode ? "bg-blue-500/20 text-blue-300 border-blue-500/30" : "bg-transparent text-gray-500 border-gray-700"}`}
              >
                <Monitor className="h-4 w-4" />
                {matrixMode ? "Hide Matrix" : "Show Matrix"}
              </button>
              
              <div className="text-xs text-gray-500 font-mono flex items-center gap-2">
                <span className="text-gray-400">{selected.toUpperCase()}</span>
                <span className="w-1 h-1 rounded-full bg-gray-600" />
                {safeBool(st?.internal?.running) ? 
                  <span className="live-glow-text flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 live-glow-dot" />
                    LIVE  |  {st?.internal?.duration || "00:00:00"}
                  </span> : 
                  <span className="text-amber-500 flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-amber-500" />
                    IDLE
                  </span>}
              </div>
            </div>
          </div>
        </div>
      </header>

      {showTickerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-gray-900/90 backdrop-blur-xl border border-gray-800 rounded-2xl shadow-2xl overflow-hidden w-full max-w-5xl max-h-[92vh]">
            <div className="px-6 py-4 border-b border-gray-800 flex justify-between items-center bg-gradient-to-r from-gray-900 to-transparent">
              <h3 className="text-lg font-bold text-white flex items-center gap-2"><Edit3 className="h-5 w-5 text-purple-400" /> Ticker Canvas Editor</h3>
              <button onClick={() => setShowTickerModal(false)} className="p-2 hover:bg-gray-800 rounded-lg"><X className="h-5 w-5 text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="rounded-xl border border-gray-800 bg-black p-4"><div className="text-xs text-gray-500 mb-2">Preview</div><div className="relative h-24 rounded-lg overflow-hidden bg-gradient-to-r from-gray-950 to-gray-900 border border-gray-800"><div style={{ position:'absolute', left:0, right:0, bottom: Math.max(0, Math.min(70, (Number(tickerDraft.y || 1030) / 1080) * 70)), background: tickerDraft.enable_bg ? tickerDraft.bg_color : 'transparent', opacity: tickerDraft.enable_bg ? tickerDraft.bg_alpha : 1, padding:'6px 12px' }}><span style={{ color: tickerDraft.color, fontSize: Math.max(14, Number(tickerDraft.font_size || 48) / 2.8), fontFamily:'sans-serif' }}>{tickerDraft.text || 'Type ticker text here...'}</span></div></div></div>
              <textarea value={tickerDraft.text} onChange={(e)=>setTickerDraft(p=>({...p,text:e.target.value}))} className={UI.input + " min-h-[140px] text-lg"} placeholder="Type Punjabi/English ticker text here" />
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <select value={tickerDraft.font} onChange={(e)=>setTickerDraft(p=>({...p,font:e.target.value}))} className={UI.input}>{fontOptions.map(fp=><option key={fp} value={fp}>{String(fp).split('/').pop()}</option>)}</select>
                <input type="number" value={tickerDraft.font_size} onChange={(e)=>setTickerDraft(p=>({...p,font_size:e.target.value}))} className={UI.input} placeholder="Font size" />
                <input type="color" value={/^#/.test(tickerDraft.color) ? tickerDraft.color : '#ffffff'} onChange={(e)=>setTickerDraft(p=>({...p,color:e.target.value}))} className="w-full h-12 rounded-xl bg-gray-900 border border-gray-700" />
                <input type="color" value={/^#/.test(tickerDraft.bg_color) ? tickerDraft.bg_color : '#000000'} onChange={(e)=>setTickerDraft(p=>({...p,bg_color:e.target.value}))} className="w-full h-12 rounded-xl bg-gray-900 border border-gray-700" />
                <input type="number" value={tickerDraft.speed} onChange={(e)=>setTickerDraft(p=>({...p,speed:e.target.value}))} className={UI.input} placeholder="Speed" />
                <input type="number" value={tickerDraft.y} onChange={(e)=>setTickerDraft(p=>({...p,y:e.target.value}))} className={UI.input} placeholder="Y position" />
                <input type="number" step="0.05" min="0" max="1" value={tickerDraft.bg_alpha} onChange={(e)=>setTickerDraft(p=>({...p,bg_alpha:e.target.value}))} className={UI.input} placeholder="BG alpha" />
                <label className="flex items-center gap-2 rounded-xl border border-gray-700 bg-gray-900/50 px-4 text-sm text-gray-300"><input type="checkbox" checked={tickerDraft.enable_bg} onChange={(e)=>setTickerDraft(p=>({...p,enable_bg:e.target.checked}))} /> Background</label>
              </div>
              <div className="flex justify-end gap-3"><button onClick={()=>setShowTickerModal(false)} className="px-4 py-3 rounded-xl bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700">Cancel</button><button onClick={saveTickerEditor} className="px-4 py-3 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-semibold">Update Live Ticker</button></div>
            </div>
          </div>
        </div>
      )}

      {/* --- Recording Modal --- */}
      {showRecordingPanel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className={`${UI.card} w-full max-w-2xl max-h-[90vh] overflow-hidden`}>
            <div className="px-6 py-4 border-b border-gray-800 flex justify-between items-center bg-gradient-to-r from-gray-900 to-transparent">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Circle className="h-5 w-5 text-red-400 fill-current" /> Recording Panel
              </h3>
              <button onClick={() => setShowRecordingPanel(false)} className="p-2 hover:bg-gray-800 rounded-lg">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto space-y-6">
              <div className="space-y-4">
                <div className="text-sm text-gray-300">
                  Record the current stream to a file. The recording will appear in the server browser and can be used in playlists.
                </div>
                
                {/* Recording Status */}
                {isRecording ? (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Circle className="h-5 w-5 text-red-400 fill-current animate-pulse" />
                        <div>
                          <div className="font-bold text-white">Recording in Progress</div>
                          <div className="text-sm text-red-300 font-mono">{formatRecordingTime(recordingTime)}</div>
                        </div>
                      </div>
                      <button 
                        onClick={stopRecording}
                        className={`px-4 py-2 rounded-xl flex items-center gap-2 ${UI.btnDanger}`}
                      >
                        <Square className="h-4 w-4" /> Stop Recording
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-gray-900/30 p-4 rounded-xl border border-gray-800">
                      <div className="font-medium text-gray-300 mb-2">Recording Options</div>
                      
                      <div className="space-y-3">
                        <button 
                          onClick={() => startRecording(false)}
                          className={`w-full py-3 rounded-xl flex items-center justify-center gap-2 ${UI.btnDanger}`}
                        >
                          <Circle className="h-4 w-4 fill-current" /> Start Recording (Full Stream)
                        </button>
                        
                        {f.input && (f.mark_in || f.mark_out) && (
                          <div className="space-y-2">
                            <button 
                              onClick={() => startRecording(true)}
                              className={`w-full py-3 rounded-xl flex items-center justify-center gap-2 ${UI.btnWarning}`}
                            >
                              <Circle className="h-4 w-4 fill-current" /> Record with Trim Marks
                            </button>
                            <div className="text-xs text-gray-500">
                              Using marks: IN: {f.mark_in || "00:00:00"} - OUT: {f.mark_out || "End"}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Recent Recordings */}
                    {recordings.length > 0 && (
                      <div className="border-t border-gray-800 pt-4">
                        <div className="font-medium text-gray-300 mb-3">Recent Recordings</div>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {recordings.slice(0, 10).map((rec, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-gray-800/30 p-2 rounded-lg hover:bg-gray-800/50 group">
                              <div className="flex items-center gap-3 min-w-0">
                                <Film className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                <div className="min-w-0">
                                  <div className="text-sm font-medium truncate">{rec.name}</div>
                                  <div className="text-xs text-gray-500 flex items-center gap-2">
                                    <span>{rec.duration_str}</span>
                                    <span> | </span>
                                    <span>{formatBytes(rec.size)}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <button 
                                  onClick={() => updateField(selected, "input", rec.path)}
                                  className="p-1.5 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 opacity-0 group-hover:opacity-100 transition"
                                  title="Use this file"
                                >
                                  <Play className="h-3.5 w-3.5" />
                                </button>
                                <button 
                                  onClick={() => addToPlaylist(rec.path)}
                                  className="p-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 opacity-0 group-hover:opacity-100 transition"
                                  title="Add to playlist"
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                </button>
                                <button 
                                  onClick={() => deleteRecording(rec.name)}
                                  className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 opacity-0 group-hover:opacity-100 transition"
                                  title="Delete"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- Clip Trim Modal --- */}
      {trimModalItem && (
      <div
        ref={trimModalShellRef}
        tabIndex={-1}
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 outline-none"
      >
          <div className={`${UI.card} w-full max-w-4xl max-h-[92vh] overflow-hidden`}>
            <div className="px-6 py-4 border-b border-gray-800 flex justify-between items-center bg-gradient-to-r from-gray-900 to-transparent">
              <div>
                <h3 className="text-lg font-bold text-white">Trim Clip</h3>
                <div className="text-xs text-gray-500 mt-1">{playlistItemLabel(trimModalItem)}</div>
              </div>
              <button onClick={closeTrimModal} className="p-2 hover:bg-gray-800 rounded-lg">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <div className="p-5 grid grid-cols-1 xl:grid-cols-[1.2fr_0.8fr] gap-5">
              <div className="space-y-3">
                <div className="rounded-2xl overflow-hidden border border-gray-800 bg-black">
                  <video
                    key={trimModalSrc}
                    ref={(node) => { vodRefs.current.trimModal = node; }}
                    controls
                    autoPlay
                    preload="auto"
                    playsInline
                    muted
                    onLoadedMetadata={primeTrimModalPreview}
                    onClick={() => trimModalShellRef.current?.focus?.()}
                    onKeyDownCapture={(event) => { const k=String(event.key||'').toLowerCase(); if(k==='i'){event.preventDefault(); captureTrimModalMark('in');} if(k==='o'){event.preventDefault(); captureTrimModalMark('out');} }}
                    tabIndex={0}
                    className="w-full aspect-video bg-black"
                    src={trimModalSrc}
                  />
                </div>
                <div className="text-xs text-gray-500">Cue the clip in the preview, then set in and out from the live frame.</div>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-1">Mark In</div>
                    <input value={trimModalMarks.mark_in} onChange={(e) => setTrimModalMarks((prev) => ({ ...prev, mark_in: e.target.value }))} className={`${UI.input} text-center font-mono`} placeholder="00:00:00" />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-1">Mark Out</div>
                    <input value={trimModalMarks.mark_out} onChange={(e) => setTrimModalMarks((prev) => ({ ...prev, mark_out: e.target.value }))} className={`${UI.input} text-center font-mono`} placeholder="00:00:00" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => captureTrimModalMark("in")} className="px-4 py-3 rounded-xl bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/20">SET IN</button>
                  <button onClick={() => captureTrimModalMark("out")} className="px-4 py-3 rounded-xl bg-violet-600/20 hover:bg-violet-600/30 text-violet-300 border border-violet-500/20">SET OUT</button>
                </div>
                <div className="rounded-2xl border border-gray-800 bg-gray-900/40 p-4 text-sm text-gray-300 space-y-2">
                  <div>Saved marks are used automatically in playlist playout.</div>
                  <div>Double-click any local clip row to reopen this trim window.</div>
                  <div className="text-cyan-300">Keyboard: <span className="font-mono">I</span> set in, <span className="font-mono">O</span> set out, <span className="font-mono">Enter</span> save trim.</div>
                </div>
                <div className="flex items-center justify-end gap-3">
                  <button onClick={closeTrimModal} className="px-4 py-3 rounded-xl bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700">Cancel</button>
                  <button onClick={saveTrimModalMarks} className={`px-4 py-3 rounded-xl ${UI.btnPrimary}`}>
                    <span className="inline-flex items-center gap-2"><Save className="h-4 w-4" /> Save Trim</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- Playlist Manager Modal --- */}
      {showPlaylistManager && (
        <div className="playlist-modal-backdrop fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-2 sm:p-4">
          <div className={`${UI.card} playlist-manager-shell playlist-modal w-[96vw] max-w-[1800px] h-[92vh] max-h-[94vh] flex flex-col overflow-hidden`}>
            <div className="playlist-modal-header px-4 sm:px-5 border-b border-gray-800 flex justify-between items-center shrink-0">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <List className="h-5 w-5 text-blue-400" /> Playlist Manager
                {isPlaylistActive && (
                  <span className="ml-2 text-xs bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded-full">
                    Active: {activePlaylistName}
                  </span>
                )}
              </h3>
              <button onClick={() => setShowPlaylistManager(false)} className="p-2 hover:bg-gray-800 rounded-lg">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            <div className="playlist-manager-toolbar shrink-0 border-b border-gray-800 bg-gray-950/90 px-3 py-2">
              <div className="playlist-toolbar-line">
                <div ref={playlistPopoverRef} className="relative shrink-0">
                  <button
                    type="button"
                    onClick={() => setPlaylistManagerDropdownOpen((open) => !open)}
                    className="playlist-toolbar-control min-w-[180px] justify-between"
                    title="Select an existing playlist or create a new one"
                  >
                    <span className="truncate">Playlist: {playlistManagerSelectedName || "New playlist"}</span>
                    <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" />
                  </button>
                  {playlistManagerDropdownOpen && (
                    <div className="playlist-selector-popover absolute left-0 top-[calc(100%+8px)] z-[1100] w-[400px] rounded-xl border border-gray-700 bg-gray-950 p-3 shadow-2xl">
                      <div className="mb-2 text-[11px] font-semibold tracking-[0.16em] text-gray-500">SELECT PLAYLIST</div>
                      <input
                        autoFocus
                        value={playlistManagerSearch}
                        onChange={(event) => setPlaylistManagerSearch(event.target.value)}
                        placeholder="Search playlists..."
                        className="mb-2 h-9 w-full rounded-lg border border-gray-800 bg-gray-900 px-3 text-xs text-white outline-none focus:border-blue-500"
                      />
                      <div className="playlist-selector-list max-h-[360px] overflow-y-auto playlist-rundown-scroller">
                        {Object.entries(playlists || {})
                          .filter(([name]) => !playlistManagerSearch.trim() || name.toLowerCase().includes(playlistManagerSearch.trim().toLowerCase()))
                          .map(([name, pl]) => (
                            <button
                              type="button"
                              key={name}
                              onClick={() => openPlaylistViewer(name)}
                              className={`playlist-selector-row flex w-full items-center justify-between gap-3 rounded-lg px-3 text-left hover:bg-gray-800 ${playlistManagerSelectedName === name ? "bg-blue-500/10 text-blue-200" : "text-gray-300"}`}
                            >
                              <span className="min-w-0 truncate">{name}</span>
                              <span className="shrink-0 text-[11px] text-gray-500">{(pl.items || []).length} items · {formatDurationSeconds((pl.items || []).reduce((total, item) => total + parsePlaylistDuration(item.duration || item.duration_str), 0), "--:--:--")} · {pl.loop_mode ? "Loop" : "Once"}</span>
                            </button>
                          ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => { resetPlaylistComposer(); setPlaylistManagerDropdownOpen(false); }}
                        className="mt-2 flex h-10 w-full items-center gap-2 rounded-lg border-t border-gray-800 px-3 pt-2 text-left text-sm text-blue-300 hover:bg-blue-500/10"
                      >
                        <Plus className="h-4 w-4" /> Create New Playlist
                      </button>
                    </div>
                  )}
                </div>
                <input
                  value={newPlaylistName}
                  onChange={(event) => { setNewPlaylistName(event.target.value); setPlaylistManagerMode("edit"); }}
                  disabled={playlistManagerMode === "view"}
                  placeholder="Playlist name"
                  className="playlist-toolbar-input playlist-name-control"
                />
                <input
                  value={playlistStartClock}
                  onChange={(event) => setPlaylistStartClock(event.target.value)}
                  disabled={playlistManagerMode === "view"}
                  className="playlist-toolbar-input w-28 font-mono"
                  placeholder="HH:MM:SS"
                  title="Playlist start time"
                />
                <button
                  type="button"
                  onClick={() => { const now = new Date(); setPlaylistStartClock(`${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`); }}
                  disabled={playlistManagerMode === "view"}
                  className="playlist-toolbar-control text-emerald-300"
                >Now</button>
                <button type="button" disabled={playlistManagerMode === "view"} onClick={() => setPlaylistLoop((value) => !value)} className={`playlist-toggle ${playlistLoop ? "is-on" : ""}`}>Loop {playlistLoop ? "ON" : "OFF"}</button>
                <button type="button" disabled={playlistManagerMode === "view"} onClick={() => setPlaylistShuffle((value) => !value)} className={`playlist-toggle ${playlistShuffle ? "is-on" : ""}`}>Shuffle {playlistShuffle ? "ON" : "OFF"}</button>
                <span className="shrink-0 text-[11px] text-gray-500">{playlistFiles.length} items · {formatDurationSeconds(playlistEditorTotalDuration, "00:00:00")}</span>
                <button type="button" onClick={() => setPlaylistMediaCollapsed((value) => !value)} className="playlist-toolbar-control shrink-0">{playlistMediaCollapsed ? "☰ Media" : "‹ Media"}</button>
                <button type="button" onClick={() => setPlaylistFocusMode((value) => !value)} className="playlist-toolbar-control shrink-0">{playlistFocusMode ? "Exit Focus" : "Focus Playlist"}</button>
                {playlistManagerMode === "view" ? (
                  <button type="button" onClick={() => setPlaylistManagerMode("edit")} className="playlist-toolbar-primary shrink-0">Edit Playlist</button>
                ) : (
                  <button type="button" onClick={createPlaylist} disabled={!newPlaylistName.trim() || !playlistFiles.length || playlistBusy} className="playlist-toolbar-primary shrink-0"><Save className="h-3.5 w-3.5" /> Save</button>
                )}
                {playlistManagerSelectedName && <button type="button" onClick={() => startPlaylist(playlistManagerSelectedName, false)} disabled={playlistBusy} className="playlist-toolbar-control shrink-0 text-emerald-300"><Play className="h-3.5 w-3.5" /> Start</button>}
                {isPlaylistActive && <button type="button" onClick={stopPlaylist} disabled={playlistBusy} className="playlist-toolbar-danger shrink-0"><Square className="h-3.5 w-3.5" /> Stop</button>}
                <button type="button" onClick={() => { setPlaylistUrlEditIndex(null); setPlaylistUrlStartClock(playlistEditorEndTimes[playlistFiles.length - 1] || playlistStartClock); setPlaylistUrlEditorOpen(true); }} disabled={playlistManagerMode === "view"} className="playlist-toolbar-control shrink-0 text-cyan-300"><Plus className="h-3.5 w-3.5" /> URL</button>
              </div>
            </div>
            
            <div className={`playlist-manager-body flex-1 min-h-0 overflow-hidden p-3 sm:p-4 ${playlistFocusMode ? "playlist-manager-focus" : ""} ${playlistMediaCollapsed ? "is-media-collapsed" : ""}`}>
              <div className={`grid grid-cols-1 ${playlistMediaCollapsed ? "lg:grid-cols-[52px_minmax(0,1fr)]" : "lg:grid-cols-[280px_minmax(0,1fr)]"} gap-4 h-full min-h-0 playlist-manager-columns`}>
                {/* Create Playlist / Editor */}
                <div ref={playlistEditorPaneRef} className="flex flex-col space-y-3 lg:order-2 min-h-0 overflow-y-auto pr-2 playlist-editor-pane playlist-manager-pane">
                  <h4 className="playlist-editor-heading font-bold text-gray-300 flex items-center gap-2">
                    <Plus className="h-4 w-4" /> {playlistEditorMode === "edit" ? `Edit Playlist: ${editingPlaylistName}` : "Create New Playlist"}
                  </h4>
                  
                  <div className="playlist-editor-content flex min-h-0 flex-1 flex-col space-y-3">
                    <div className="playlist-editor-name-row flex gap-2">
                      <input
                        type="text"
                        placeholder="Playlist Name"
                        value={newPlaylistName}
                        onChange={(e) => setNewPlaylistName(e.target.value)}
                        disabled={playlistManagerMode === "view"}
                        className={`${UI.input} flex-1`}
                      />
                      <button
                        onClick={createPlaylist}
                        disabled={playlistManagerMode === "view" || !newPlaylistName.trim() || playlistFiles.length === 0}
                        className={`px-4 py-3 rounded-xl ${(!newPlaylistName.trim() || playlistFiles.length === 0) ? 'opacity-50 cursor-not-allowed' : UI.btnPrimary}`}
                        title={playlistEditorMode === "edit" ? "Update Playlist" : "Create Playlist"}
                      >
                        <Save className="h-4 w-4" />
                      </button>
                      {playlistEditorMode === "edit" && (
                        <button
                          onClick={resetPlaylistComposer}
                          className="px-4 py-3 rounded-xl bg-gray-800/80 hover:bg-gray-700 text-gray-300 border border-gray-700"
                          title="Cancel editing"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    
                    <div className="playlist-editor-settings space-y-3 bg-gray-900/30 p-4 rounded-xl">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs text-gray-500 uppercase tracking-wider">Playlist Start Time</label>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                const d = new Date();
                                setPlaylistStartClock(
                                  `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}:${String(d.getSeconds()).padStart(2,"0")}`
                                );
                              }}
                              className="text-xs px-2.5 py-1 rounded-lg bg-emerald-700/30 hover:bg-emerald-700/50 border border-emerald-600/30 text-emerald-300 flex items-center gap-1.5"
                              title="Set start time to right now"
                            >
                              <Clock className="h-3 w-3" /> Live Start (Now)
                            </button>
                          </div>
                        </div>
                        <input value={playlistStartClock} onChange={(e)=>setPlaylistStartClock(e.target.value)} className={`${UI.input} mt-1 font-mono`} placeholder="HH:MM:SS" />
                        <div className="text-[11px] text-gray-500 mt-1">Each clip&rsquo;s scheduled start time is calculated from this. Click &ldquo;Live Start (Now)&rdquo; to use the current clock.</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="playlistLoop"
                          checked={playlistLoop}
                          onChange={(e) => setPlaylistLoop(e.target.checked)}
                          className="rounded border-gray-600"
                        />
                        <label htmlFor="playlistLoop" className="text-sm text-gray-300">Loop playlist continuously</label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="playlistShuffle"
                          checked={playlistShuffle}
                          onChange={(e) => setPlaylistShuffle(e.target.checked)}
                          className="rounded border-gray-600"
                        />
                        <label htmlFor="playlistShuffle" className="text-sm text-gray-300">Shuffle playback order</label>
                      </div>
                    </div>
                    
                    {/* URL Input for Playlist */}
                    <div className="playlist-editor-url-legacy space-y-2">
                      <div className="text-sm font-medium text-gray-300">Add URL to Playlist</div>
                      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_150px_auto] gap-2">
                        <input
                          type="text"
                          value={playlistUrlInput}
                          onChange={(e) => setPlaylistUrlInput(e.target.value)}
                          placeholder="file path, http://, udp://, rtmp://, srt://, youtube.com/..."
                          className={`${UI.input} flex-1`}
                        />
                        <input
                          type="text"
                          value={playlistUrlDuration}
                          onChange={(e) => setPlaylistUrlDuration(e.target.value)}
                          placeholder="Duration HH:MM:SS"
                          className={`${UI.input} font-mono text-xs`}
                          title="Optional live URL playout duration"
                        />
                        <button
                          onClick={addUrlToPlaylist}
                          className="px-4 py-3 rounded-xl bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/30"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="text-[11px] text-gray-500">For live URLs, the next item is taken only after the source is ready and this duration has elapsed. Leave empty for continuous live input.</div>
                    </div>
                    
                    {/* Drag & Drop Playlist Items */}
                    <div className="border border-gray-800 rounded-xl p-3 flex flex-1 flex-col min-h-0 playlist-editor-rundown">
                    <div className="playlist-rundown-titlebar flex items-center justify-between mb-2">
                      <div>
                        <h5 className="text-sm font-semibold text-gray-200 flex items-center gap-2">
                          <List className="h-4 w-4" /> Playlist Items <span className="text-xs font-normal text-gray-500">{playlistFiles.length} Items · {formatDurationSeconds(playlistEditorTotalDuration, "00:00:00")}</span>
                        </h5>
                        <div className="text-[11px] text-gray-500">Start {playlistStartClock || "--:--:--"} · End {playlistEditorEndTimes[playlistFiles.length - 1] || playlistStartClock || "--:--:--"}</div>
                      </div>
                      {playlistFiles.length > 0 && (
                        <button
                          onClick={() => { setPlaylistFiles([]); setPlaylistDurations([]); }}
                          disabled={playlistManagerMode === "view"}
                          className="text-xs text-red-400 hover:text-red-300"
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                    <div className="playlist-rundown-table-head" aria-hidden="true"><span>#</span><span>Drag</span><span>Type</span><span>Name / Source</span><span>Start</span><span>Duration</span><span>End</span><span>Info</span><span>Trim</span><span>Actions</span></div>
                      <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="playlist-items">
                          {(provided) => (
                            <div
                              {...provided.droppableProps}
                              ref={(node) => {
                                provided.innerRef(node);
                                playlistEditorItemsRef.current = node;
                              }}
                              className="playlist-rundown-table flex-1 min-h-0 overflow-y-auto playlist-rundown-scroller"
                            >
                              {playlistFiles.map((file, idx) => {
                                const meta = libraryFileMap[file] || libraryFileMap[String(file).split("/").pop()] || null;
                                const isRemote = /^(https?|rtmp|rtmps|udp|srt|rtsp):\/\//i.test(file) || /youtube\.com|youtu\.be/i.test(file);
                                return (
                                <Draggable key={`file-${idx}`} draggableId={`file-${idx}`} index={idx} isDragDisabled={playlistManagerMode === "view"}>
                                  {(provided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      onDoubleClick={() => !isRemote && openTrimModal({ path: file, title: meta?.name || String(file).split('/').pop(), is_remote: false, mark_in: playlistMarksDraft[file]?.mark_in || "", mark_out: playlistMarksDraft[file]?.mark_out || "" })}
                                      onClick={() => setPlaylistItemEditingIndex((current) => current === idx ? null : idx)}
                                      className={`playlist-rundown-row group cursor-pointer ${playlistItemEditingIndex === idx ? "is-editing" : ""}`}
                                    >
                                      <span className="playlist-row-index">{String(idx + 1).padStart(2, "0")}</span>
                                      <div {...provided.dragHandleProps} className={`playlist-row-drag ${playlistManagerMode === "view" ? "cursor-default" : "cursor-move"}`}>
                                          <Menu className="h-4 w-4 text-gray-500" />
                                      </div>
                                      <span className={`playlist-row-type ${isRemote ? "is-url" : ""}`}>{isRemote ? "URL" : "FILE"}</span>
                                      <span className="playlist-row-name" title={String(file)}>{isRemote ? file : (meta?.name || file.split('/').pop())}</span>
                                      <span className="playlist-row-time">{playlistEditorStartTimes[idx] || "--:--:--"}</span>
                                      <span className="playlist-row-time">{isRemote ? (playlistDurations[idx] ? formatDurationSeconds(parsePlaylistDuration(playlistDurations[idx])) : "LIVE") : (playlistDurations[idx] ? formatDurationSeconds(parsePlaylistDuration(playlistDurations[idx])) : (meta?.duration_str || "--:--:--"))}</span>
                                      <span className="playlist-row-time">{playlistEditorEndTimes[idx] || "--:--:--"}</span>
                                      <span className="playlist-row-info">{isRemote ? "Live source" : (meta?.resolution || "—")}</span>
                                      <button type="button" onClick={(event) => { event.stopPropagation(); if (!isRemote) openTrimModal({ path: file, title: meta?.name || String(file).split('/').pop(), is_remote: false, mark_in: playlistMarksDraft[file]?.mark_in || "", mark_out: playlistMarksDraft[file]?.mark_out || "" }); }} disabled={isRemote || playlistManagerMode === "view"} className="playlist-row-trim">{isRemote ? "—" : "Trim"}</button>
                                      <div className="playlist-row-actions">
                                        {isRemote && (
                                          <button
                                            onClick={(event) => { event.stopPropagation(); editUrlPlaylistItem(idx); }}
                                            disabled={playlistManagerMode === "view"}
                                            type="button"
                                            className="p-2 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 transition"
                                            title={`Edit URL item ${idx + 1}`}
                                          >
                                            <Edit3 className="h-3.5 w-3.5" />
                                          </button>
                                        )}
                                        <button 
                                          onClick={(event) => { event.stopPropagation(); removeFromPlaylist(idx); }}
                                          disabled={playlistManagerMode === "view"}
                                          type="button"
                                          className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition"
                                          title={`Remove item ${idx + 1}`}
                                        >
                                          <X className="h-3.5 w-3.5" />
                                        </button>
                                      </div>
                                      {playlistItemEditingIndex === idx && playlistManagerMode === "edit" && (
                                        <div className="playlist-row-editor" onClick={(event) => event.stopPropagation()}>
                                          <label>Start <input value={playlistItemStartClocks[idx] || ""} onChange={(event) => setPlaylistItemStartClocks((prev) => { const next = [...prev]; next[idx] = event.target.value; return next; })} placeholder="Auto" /></label>
                                          <label>Duration <input value={playlistDurations[idx] ? formatDurationSeconds(parsePlaylistDuration(playlistDurations[idx]), "") : ""} onChange={(event) => setPlaylistDurations((prev) => { const next = [...prev]; next[idx] = event.target.value; return next; })} placeholder={isRemote ? "LIVE / HH:MM:SS" : (meta?.duration_str || "HH:MM:SS")} /></label>
                                          <span>{isRemote ? "Live source switches only after its video pad is ready." : "Double-click or Trim opens the trim editor."}</span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </Draggable>
                              )})}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                      
                      {playlistFiles.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <List className="h-12 w-12 mx-auto mb-3 opacity-20" />
                          <p>Drag files here or use "Add to Playlist" buttons</p>
                          <p className="text-xs mt-1">Double-click a local clip to open trim preview</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Available Files & Existing Playlists */}
                <div className={`space-y-6 min-w-0 lg:order-1 min-h-0 overflow-y-auto pr-2 playlist-library-pane playlist-manager-pane ${playlistMediaCollapsed ? "playlist-library-collapsed" : ""}`}>
                  <button type="button" onClick={() => setPlaylistMediaCollapsed(false)} className="playlist-media-rail" title="Expand Media Library"><span>☰</span><span>Media</span></button>
                  <div className="playlist-library-content">
                  <div className="grid grid-cols-1 gap-3 items-start">
                  {/* Available Files */}
                  <div className="min-w-0">
                    <h4 className="playlist-media-title font-bold text-gray-300 flex items-center gap-2 mb-3">
                      <Database className="h-4 w-4" /> Available Files ({serverFiles.length})
                    </h4>
                    <div className="playlist-media-search relative mb-3">
                      <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-600" />
                      <input
                        value={playlistMediaSearch}
                        onChange={(event) => setPlaylistMediaSearch(event.target.value)}
                        placeholder="Search media files..."
                        className="h-9 w-full rounded-lg border border-gray-800 bg-gray-950/70 pl-9 pr-3 text-xs text-white outline-none focus:border-blue-500/60"
                        aria-label="Search available media files"
                      />
                    </div>

                    {/* Upload to Library */}
                    <label className="playlist-media-upload flex items-center gap-2 cursor-pointer px-3 py-2 mb-3 rounded-lg border border-blue-500/25 bg-blue-500/10 hover:bg-blue-500/15 transition text-xs text-blue-200">
                      <Upload className="h-4 w-4 flex-shrink-0 text-blue-400" />
                      <span>Upload video to library</span>
                      <input type="file" className="hidden" accept="video/*" onChange={uploadFileToLibrary} />
                    </label>
                    {uploadProgress.__library__ > 0 && (
                      <div className="mb-3">
                        <div className="w-full bg-gray-800 rounded-full h-1.5 overflow-hidden">
                          <div className="bg-gradient-to-r from-blue-500 to-indigo-500 h-full transition-all duration-200" style={{ width: `${uploadProgress.__library__}%` }} />
                        </div>
                        <div className="text-xs text-blue-400 mt-1 text-center">Uploading: {uploadProgress.__library__}%</div>
                      </div>
                    )}

                    {/* Multi-select toolbar */}
                    {selectedLibraryFiles.size > 0 && (
                      <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-red-500/10 rounded-xl border border-red-500/20">
                        <span className="text-xs text-red-300 font-medium">{selectedLibraryFiles.size} selected</span>
                        <button
                          onClick={() => setSelectedLibraryFiles(new Set())}
                          className="text-xs text-gray-400 hover:text-gray-300 underline"
                        >
                          Clear
                        </button>
                        <button
                          onClick={deleteSelectedFiles}
                          className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs font-medium transition"
                        >
                          <Trash2 className="h-3 w-3" /> Delete Selected
                        </button>
                      </div>
                    )}

                    <div className="playlist-media-preview mb-3 grid grid-cols-1 gap-2 rounded-xl border border-gray-800 bg-black/20 p-2">
                      <div className="relative aspect-video min-h-[150px] overflow-hidden rounded-xl border border-gray-800 bg-black">
                        {playlistPreviewSrc ? (
                          <video
                            key={playlistPreviewSrc}
                            src={playlistPreviewSrc}
                            controls
                            preload="metadata"
                            playsInline
                            className="h-full w-full object-contain"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center text-xs text-gray-600">
                            <Film className="mr-2 h-5 w-5" /> Select media to preview
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="playlist-media-file-list border border-gray-800 rounded-xl p-2 overflow-y-auto playlist-rundown-scroller pr-1">
                      {/* Select-all row */}
                      {serverFiles.length > 0 && (
                        <div className="flex items-center gap-2 px-2 pb-2 mb-1 border-b border-gray-800/60">
                          <input
                            type="checkbox"
                            className="rounded accent-blue-500 cursor-pointer"
                            checked={selectedLibraryFiles.size === serverFiles.length && serverFiles.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedLibraryFiles(new Set(serverFiles.map((f) => f.name)));
                              } else {
                                setSelectedLibraryFiles(new Set());
                              }
                            }}
                          />
                          <span className="text-xs text-gray-500">Select all</span>
                        </div>
                      )}
                      {filteredPlaylistFiles.map((file, idx) => (
                        <div
                          key={idx}
                          onClick={() => setPlaylistPreviewFile(file)}
                          className={`flex cursor-pointer items-center justify-between p-2 rounded group ${playlistPreviewFile?.path === file.path ? "bg-blue-500/10 ring-1 ring-blue-500/30" : "hover:bg-gray-800/30"}`}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <input
                              type="checkbox"
                              className="rounded accent-blue-500 cursor-pointer flex-shrink-0"
                              checked={selectedLibraryFiles.has(file.name)}
                              onChange={(e) => {
                                setSelectedLibraryFiles((prev) => {
                                  const next = new Set(prev);
                                  e.target.checked ? next.add(file.name) : next.delete(file.name);
                                  return next;
                                });
                              }}
                            />
                            <Film className="h-4 w-4 text-gray-500 flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate">{file.name}</div>
                              <div className="text-xs text-gray-500 flex items-center gap-2">
                                <span>{file.duration_str}</span>
                                <span> | </span>
                                <span>{formatBytes(file.size)}</span>
                                <span> | </span>
                                <span>{file.resolution}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button 
                              onClick={(event) => {
                                event.stopPropagation();
                                updateField(selected, "input", file.path);
                                loadMarksForInput(selected, file.path);
                              }}
                              className="p-1.5 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 opacity-0 group-hover:opacity-100 transition"
                              title="Use this file"
                            >
                              <Play className="h-3.5 w-3.5" />
                            </button>
                            <button 
                              onClick={(event) => { event.stopPropagation(); addToPlaylist(file.path); }}
                              disabled={playlistManagerMode === "view" || playlistFiles.includes(file.path)}
                              className="p-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition"
                              title="Add to playlist"
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                            <button 
                              onClick={(event) => { event.stopPropagation(); deleteVideo(file.name); }}
                              className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 opacity-0 group-hover:opacity-100 transition"
                              title="Delete file"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                      {filteredPlaylistFiles.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <Film className="h-12 w-12 mx-auto mb-3 opacity-20" />
                          <p>{playlistMediaSearch.trim() ? "No matching video files" : "No video files found"}</p>
                          <p className="text-xs mt-1">Upload videos to get started</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Existing Playlists (legacy location; use the compact toolbar selector) */}
                  <div className="playlist-existing-legacy min-w-0">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold text-gray-300 flex items-center gap-2">
                        <List className="h-4 w-4" /> Existing Playlists ({Object.keys(playlists).length})
                      </h4>
                      <button
                        onClick={loadPlaylists}
                        className="text-xs text-gray-400 hover:text-gray-300 flex items-center gap-1"
                      >
                        <RefreshCw className="h-3 w-3" /> Refresh
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-4 max-h-[20rem] overflow-y-auto pr-1 playlist-rundown-scroller">
                      {Object.keys(playlists).map((name) => {
                        const pl = playlists[name];
                        const playlistItems = pl.items || [];
                        const isActivePlayout = activePlaylistName === name;
                        return (
                          <div
                            key={name}
                            className={`rounded-2xl border p-4 transition-all min-h-[208px] flex flex-col ${
                              pl.active
                                ? "border-emerald-500/40 bg-emerald-500/10 shadow-lg shadow-emerald-900/20"
                                : "border-gray-800 bg-gray-900/40"
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3 mb-3 mobile-stack">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <div className="font-semibold text-gray-100">{name}</div>
                                  {pl.active && (
                                      <span className="text-[11px] uppercase tracking-[0.2em] bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded-full border border-emerald-500/30">
                                        On Air
                                      </span>
                                    )}
                                </div>
                                <div className="text-xs text-gray-500 mt-2 flex items-center gap-2 flex-wrap">
                                  <span className="inline-flex items-center gap-1">
                                    <Film className="h-3 w-3" /> {playlistItems.length} clips
                                  </span>
                                  <span className={pl.loop_mode ? "text-emerald-400" : "text-amber-400"}>
                                    {pl.loop_mode ? "Playlist Loop On" : "Playlist Loop Off"}
                                  </span>
                                  {pl.loop_item && <span className="text-violet-300">Item Loop On</span>}
                                  {pl.shuffle && <span className="text-blue-300">Shuffle</span>}
                                  {pl.mark_in && <span className="text-cyan-300">Trim {pl.mark_in} - {pl.mark_out || "End"}</span>}
                                </div>
                              </div>
                            </div>
                            <div className="rounded-2xl border border-gray-800/80 bg-black/20 px-4 py-3 mb-4">
                              <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div>
                                  <div className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-400">
                                    Rundown Summary
                                  </div>
                                  <div className="text-sm text-gray-300 mt-1">
                                    {playlistItems[0] ? playlistItemLabel(playlistItems[0]) : "No clips"}{playlistItems.length > 1 ? ` +${playlistItems.length - 1} more` : ""}
                                  </div>
                                </div>
                                <button
                                  onClick={() => setPlaylistRundownName((current) => current === name ? "" : name)}
                                  disabled={playlistBusy}
                                  className={`px-3 py-2 rounded-xl border transition disabled:opacity-50 ${playlistRundownName === name ? "bg-blue-600/20 text-blue-300 border-blue-500/30" : "bg-gray-800/80 hover:bg-gray-700 text-gray-200 border-gray-700"}`}
                                  title="Load this rundown inline"
                                >
                                  <span className="inline-flex items-center gap-2"><List className="h-4 w-4" /> {playlistRundownName === name ? "Hide rundown" : "Load rundown"}</span>
                                </button>
                              </div>
                              {playlistItems.length > 0 && (
                                <div className="mt-3 space-y-2 max-h-[6.5rem] overflow-y-auto pr-1 playlist-rundown-scroller">
                                  {playlistItems.slice(0, 4).map((item, idx) => (
                                    <div key={`${name}-summary-${idx}`} className="flex items-center justify-between gap-3 text-xs text-gray-400">
                                      <div className="flex items-center gap-2 min-w-0">
                                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg border border-gray-700 bg-gray-800/70 text-[10px] font-semibold text-gray-300">
                                          {String(idx + 1).padStart(2, "0")}
                                        </span>
                                        <span className="truncate">{playlistItemLabel(item)}</span>
                                      </div>
                                      <div className="flex items-center gap-2 shrink-0">
                                        <span className="text-emerald-400/80 font-mono">{item.day_start || (allPlaylistStartTimes[name] || [])[idx] || "--:--:--"} → {item.day_end || item.day_start || "--:--:--"}</span>
                                        <span>{item.duration_str || "--:--:--"}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            {playlistRundownName === name && (
                              <div className="mb-4 rounded-2xl border border-blue-500/20 bg-blue-500/5 p-3">
                                <div className="flex items-center justify-between gap-2 mb-2">
                                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-300">Loaded Rundown</div>
                                  <span className="text-[11px] text-gray-500">{playlistItems.length} items</span>
                                </div>
                                <div className="max-h-[14rem] overflow-y-auto space-y-1.5 playlist-rundown-scroller pr-1">
                                  {playlistItems.map((item, idx) => (
                                    <div key={`${name}-loaded-${idx}`} className="flex items-center gap-3 rounded-xl border border-gray-800/80 bg-gray-950/40 px-3 py-2 text-xs">
                                      <span className="w-6 shrink-0 text-center font-mono text-gray-500">{String(idx + 1).padStart(2, "0")}</span>
                                      <span className="min-w-0 flex-1 truncate text-gray-200">{playlistItemLabel(item)}</span>
                                      <span className="shrink-0 font-mono text-emerald-400/80">{item.day_start || (allPlaylistStartTimes[name] || [])[idx] || "--:--:--"} → {item.day_end || item.day_start || "--:--:--"}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className="mt-auto flex items-center gap-2 flex-wrap justify-end">
                              <button 
                                onClick={() => openPlaylistEditor(name)}
                                disabled={playlistBusy}
                                className="px-3 py-2 rounded-xl bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 border border-cyan-500/20 transition disabled:opacity-50"
                                title="Edit existing playlist"
                              >
                                <span className="inline-flex items-center gap-2"><List className="h-4 w-4" /> Edit Playlist</span>
                              </button>
                              <button 
                                onClick={() => startPlaylist(name, false)}
                                disabled={playlistBusy}
                                className="px-3 py-2 rounded-xl bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/20 transition disabled:opacity-50"
                                title="Start playlist"
                              >
                                <span className="inline-flex items-center gap-2"><Play className="h-4 w-4" /> Start</span>
                              </button>
                              <button 
                                onClick={() => startPlaylist(name, true)}
                                disabled={playlistBusy}
                                className="px-3 py-2 rounded-xl bg-violet-600/20 hover:bg-violet-600/30 text-violet-300 border border-violet-500/20 transition disabled:opacity-50"
                                title="Start playlist with loop mode"
                              >
                                <span className="inline-flex items-center gap-2"><Repeat className="h-4 w-4" /> Loop</span>
                              </button>
                              {isActivePlayout && (
                                <button 
                                  onClick={stopPlaylistItem}
                                  disabled={playlistBusy}
                                  className="px-3 py-2 rounded-xl bg-red-600/20 hover:bg-red-600/30 text-red-300 border border-red-500/20 transition disabled:opacity-50"
                                  title="Stop current playlist playout"
                                >
                                  <span className="inline-flex items-center gap-2"><Square className="h-4 w-4" /> Stop</span>
                                </button>
                              )}
                              <button 
                                onClick={() => deletePlaylist(name)}
                                disabled={playlistBusy}
                                className="px-3 py-2 rounded-xl bg-red-600/20 hover:bg-red-600/30 text-red-300 border border-red-500/20 transition disabled:opacity-50"
                                title="Delete playlist"
                              >
                                <span className="inline-flex items-center gap-2"><Trash2 className="h-4 w-4" /> Delete</span>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {Object.keys(playlists).length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <List className="h-12 w-12 mx-auto mb-3 opacity-20" />
                          <p>No playlists created yet</p>
                          <p className="text-xs mt-1">Create a playlist using the form on the left</p>
                        </div>
                      )}
                    </div>
                  </div>
                  </div>
                  </div>
                </div>
              </div>
              
              {playlistUrlEditorOpen && (
                <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/60 p-4">
                  <div className="w-full max-w-xl rounded-2xl border border-gray-700 bg-gray-950 p-5 shadow-2xl">
                    <div className="mb-4 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold text-white">Add URL / Live Source</div>
                        <div className="text-xs text-gray-500">The source is handed off only after GStreamer reports a ready video pad.</div>
                      </div>
                      <button type="button" onClick={() => setPlaylistUrlEditorOpen(false)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-800 hover:text-white"><X className="h-4 w-4" /></button>
                    </div>
                    <input
                      autoFocus
                      type="text"
                      value={playlistUrlInput}
                      onChange={(event) => setPlaylistUrlInput(event.target.value)}
                      placeholder="http://, https://, udp://, rtmp://, srt:// or file path"
                      className={`${UI.input} mb-3`}
                    />
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <label className="text-xs text-gray-500">Start Time
                        <input value={playlistUrlStartClock} onChange={(event) => setPlaylistUrlStartClock(event.target.value)} placeholder="HH:MM:SS" className={`${UI.input} mt-1 font-mono`} />
                      </label>
                      <label className="text-xs text-gray-500">Duration
                        <input value={playlistUrlDuration} onChange={(event) => setPlaylistUrlDuration(event.target.value)} disabled={playlistUrlContinuous} placeholder="HH:MM:SS" className={`${UI.input} mt-1 font-mono disabled:opacity-40`} />
                      </label>
                    </div>
                    <label className="mt-3 flex items-center gap-2 text-sm text-gray-300">
                      <input type="checkbox" checked={playlistUrlContinuous} onChange={(event) => setPlaylistUrlContinuous(event.target.checked)} className="accent-cyan-500" />
                      Continuous source until manually switched
                    </label>
                    <div className="mt-5 flex justify-end gap-2">
                      <button type="button" onClick={() => setPlaylistUrlEditorOpen(false)} className="playlist-toolbar-control">Cancel</button>
                      <button type="button" onClick={addUrlToPlaylist} className="playlist-toolbar-primary"><Plus className="h-3.5 w-3.5" /> Add to Playlist</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Active Playlist Control */}
              {isPlaylistActive && (
                <div className="playlist-manager-active-control border-t border-gray-800 pt-4 mt-4 space-y-4">
                  <div className="flex items-start justify-between gap-4 mobile-stack">
                    <div>
                      <h4 className="font-bold text-gray-300 flex items-center gap-2">
                        <Activity className="h-4 w-4 text-emerald-400" /> Active Playlist
                      </h4>
                      <p className="text-sm text-gray-500">
                        {activePlaylistName}  |  Playlist Loop: {activePlaylistInfo?.loop_mode ? "On" : "Off"}  |  Item Loop: {activeLoopItem ? "On" : "Off"}  |  Clip {Math.min(currentFileIndex + 1, Math.max(activePlaylistCount, 1))} of {Math.max(activePlaylistCount, 1)}
                      </p>
                    </div>
                    <button 
                      onClick={stopPlaylist}
                      className={`px-4 py-2 rounded-xl flex items-center gap-2 ${UI.btnDanger} mt-2 sm:mt-0`}
                    >
                      <Square className="h-4 w-4" /> Stop Playlist
                    </button>
                  </div>
                  {activePlaylistItems.length > 0 && (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                      {activePlaylistItems.map((item, idx) => {
                        const isCurrent = currentFileIndex === idx;
                        return (
                          <div
                            key={`active-${idx}`}
                            className={`rounded-2xl border px-4 py-3 flex items-start justify-between gap-3 ${
                              isCurrent ? "border-emerald-500/40 bg-emerald-500/10" : "border-gray-800 bg-gray-900/40"
                            }`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${playlistItemTone(item)}`}>
                                  {playlistItemProtocol(item)}
                                </span>
                                <span className={`text-sm font-medium truncate ${isCurrent ? "text-emerald-100" : "text-gray-100"}`}>
                                  {playlistItemLabel(item)}
                                </span>
                              </div>
                              <div className="mt-1 text-xs text-gray-500 flex items-center gap-3 flex-wrap">
                                <span className="text-emerald-400/80 font-mono">{item.day_start || activePlaylistStartTimes[idx] || "--:--:--"} → {item.day_end || item.day_start || "--:--:--"}</span>
                                <span>{item.duration_str || "--:--:--"}</span>
                                <span>{item.resolution || "Unknown"}</span>
                                {!item.exists && <span className="text-red-300">Missing source</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap justify-end">
                              <button
                                onClick={() => (isCurrent ? stopPlaylistItem() : playPlaylistItem(activePlaylistName, idx, false))}
                                disabled={playlistBusy || !item.exists}
                                className={`px-3 py-2 rounded-xl border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                                  isCurrent
                                    ? "bg-red-600/20 text-red-300 border-red-500/20 hover:bg-red-600/30"
                                    : "bg-emerald-600/20 text-emerald-300 border-emerald-500/20 hover:bg-emerald-600/30"
                                }`}
                              >
                                <span className="inline-flex items-center gap-2">
                                  {isCurrent ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                                  {isCurrent ? "Stop" : "Play"}
                                </span>
                              </button>
                              <button
                                onClick={() => playPlaylistItem(activePlaylistName, idx, true)}
                                disabled={playlistBusy || !item.exists}
                                className="px-3 py-2 rounded-xl border border-violet-500/20 bg-violet-600/20 text-violet-300 hover:bg-violet-600/30 transition disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                <span className="inline-flex items-center gap-2">
                                  <Repeat className="h-4 w-4" /> Loop
                                </span>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showPlaylistManager && playlistInspector && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/55 backdrop-blur-sm p-4">
          <div className={`${UI.card} w-full max-w-4xl max-h-[88vh] overflow-hidden playlist-inspector-panel`}>
            <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between bg-gradient-to-r from-gray-900 to-transparent">
              <div>
                <div className="text-lg font-bold text-white">{playlistInspectorName}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {(playlistInspector.items || []).length} clips | {playlistInspector.loop_mode ? "Playlist Loop On" : "Play Once"} | {playlistInspector.shuffle ? "Shuffle" : "In Order"}
                </div>
              </div>
              <button onClick={() => setPlaylistInspectorName("")} className="p-2 hover:bg-gray-800 rounded-lg">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <div className="px-5 py-4 border-b border-gray-800/80 bg-gray-950/40 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-gray-300">
                {activePlaylistName === playlistInspectorName
                  ? `Live clip control enabled. ${selectedPlaylistName === playlistInspectorName && Number.isInteger(selectedPlaylistIndex) ? `Selected clip: ${selectedPlaylistIndex + 1}` : ""}`
                  : "Open, inspect, trim, and start this rundown from here."}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => openPlaylistEditor(playlistInspectorName)}
                  className="px-3 py-2 rounded-xl bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 border border-cyan-500/20 transition"
                >
                  <span className="inline-flex items-center gap-2"><List className="h-4 w-4" /> Edit</span>
                </button>
                <button
                  onClick={() => startPlaylist(playlistInspectorName, false)}
                  disabled={playlistBusy}
                  className="px-3 py-2 rounded-xl bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/20 transition disabled:opacity-50"
                >
                  <span className="inline-flex items-center gap-2"><Play className="h-4 w-4" /> Start</span>
                </button>
                <button
                  onClick={() => startPlaylist(playlistInspectorName, true)}
                  disabled={playlistBusy}
                  className="px-3 py-2 rounded-xl bg-violet-600/20 hover:bg-violet-600/30 text-violet-300 border border-violet-500/20 transition disabled:opacity-50"
                >
                  <span className="inline-flex items-center gap-2"><Repeat className="h-4 w-4" /> Loop</span>
                </button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto max-h-[68vh] space-y-3 playlist-rundown-scroller">
              {(playlistInspector.items || []).map((item, idx) => (
                <div
                  key={`inspect-${item.path}-${idx}`}
                  onClick={() => selectPlaylistItem(playlistInspectorName, idx)}
                  onDoubleClick={() => openTrimModal(item)}
                  className={`rounded-2xl border px-4 py-3 transition cursor-pointer ${
                    selectedPlaylistName === playlistInspectorName && selectedPlaylistIndex === idx
                      ? "border-cyan-500/40 bg-cyan-500/10"
                      : activePlaylistName === playlistInspectorName && currentFileIndex === idx
                        ? "border-emerald-500/40 bg-emerald-500/10"
                        : "border-gray-800 bg-gray-900/40 hover:bg-gray-800/40"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] px-2 py-1 rounded-full border border-gray-700 bg-gray-800/70 text-gray-300">
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <span className={`text-[11px] px-2 py-1 rounded-full border ${playlistItemTone(item)}`}>{playlistItemProtocol(item)}</span>
                        <span className="text-sm font-semibold text-white truncate">{playlistItemLabel(item)}</span>
                        {selectedPlaylistName === playlistInspectorName && selectedPlaylistIndex === idx && (
                          <span className="text-[11px] px-2 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/20 text-cyan-200">
                            Selected
                          </span>
                        )}
                        {activePlaylistName === playlistInspectorName && currentFileIndex === idx && (
                          <span className="text-[11px] px-2 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/20 text-emerald-200">
                            On Air
                          </span>
                        )}
                      </div>
                      <div className="mt-2 text-xs text-gray-500 flex items-center gap-3 flex-wrap">
                        <span className="text-emerald-400/80 font-mono">{item.day_start || playlistInspectorStartTimes[idx] || "--:--:--"} → {item.day_end || item.day_start || "--:--:--"}</span>
                        <span>{item.duration_str || "--:--:--"}</span>
                        <span>{item.resolution || "Unknown"}</span>
                        <span>{formatBytes(item.size)}</span>
                        {!item.is_remote && <span className="text-cyan-300">Double-click to trim</span>}
                        {item.trim_enabled && <span className="text-emerald-300">Trim {item.mark_in || "00:00:00"} - {item.mark_out || "END"}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          if (activePlaylistName === playlistInspectorName && currentFileIndex === idx) {
                            stopPlaylistItem();
                          } else {
                            playPlaylistItem(playlistInspectorName, idx, false);
                          }
                        }}
                        disabled={playlistBusy || !item.exists}
                        className={`px-3 py-2 rounded-xl border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                          activePlaylistName === playlistInspectorName && currentFileIndex === idx
                            ? "bg-red-600/20 text-red-300 border-red-500/20 hover:bg-red-600/30"
                            : "bg-emerald-600/20 text-emerald-300 border-emerald-500/20 hover:bg-emerald-600/30"
                        }`}
                      >
                        <span className="inline-flex items-center gap-2">
                          {activePlaylistName === playlistInspectorName && currentFileIndex === idx ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                          {activePlaylistName === playlistInspectorName && currentFileIndex === idx ? "Stop" : "Play"}
                        </span>
                      </button>
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          playPlaylistItem(playlistInspectorName, idx, true);
                        }}
                        disabled={playlistBusy || !item.exists}
                        className="px-3 py-2 rounded-xl border border-violet-500/20 bg-violet-600/20 text-violet-300 hover:bg-violet-600/30 transition disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <span className="inline-flex items-center gap-2"><Repeat className="h-4 w-4" /> Loop</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* --- Main Content --- */}
      <main className={`flex-1 p-3 sm:p-4 max-w-[1920px] mx-auto w-full dashboard-main ${controlRoomMode ? "control-room-mode" : ""}`}>
        <div className="dashboard-operator-bar">
          <div className="dashboard-operator-identity">
            <span className="dashboard-channel-chip">{fmtLabel(ch)}</span>
            <span className="dashboard-mode-chip">{currentDashboardMode === "playlist" ? "PLAYOUT" : currentDashboardMode.toUpperCase()}</span>
            <span className={`dashboard-live-chip ${safeBool(st?.internal?.running) ? "is-live" : ""}`}>
              <span className="status-dot" /> {safeBool(st?.internal?.running) ? `LIVE ${st?.internal?.duration || "00:00:00"}` : "STOPPED"}
            </span>
            {channelModeLocked && <span className="dashboard-lock-chip"><Lock className="h-3 w-3" /> LOCKED</span>}
          </div>
          <div className="dashboard-health-summary">
            <span className={getHealthIndicator(ch, "input")?.healthy ? "is-healthy" : ""}>INPUT {getHealthIndicator(ch, "input")?.healthy ? "✓" : "—"}</span>
            <span className={safeBool(st?.internal?.running) ? "is-healthy" : ""}>ENCODER {safeBool(st?.internal?.running) ? "✓" : "—"}</span>
            <span className={safeBool(st?.custom?.running) ? "is-healthy" : ""}>OUTPUT {safeBool(st?.custom?.running) ? "✓" : "—"}</span>
          </div>
          <div className="dashboard-operator-actions">
            {currentDashboardMode === "playlist" && <button type="button" className="dashboard-operator-button" onClick={() => setShowPlaylistManager(true)}><List className="h-3.5 w-3.5" /> Playlist</button>}
            <button type="button" className="dashboard-operator-button" onClick={() => setControlRoomMode((value) => !value)}>{controlRoomMode ? "Exit Control Room" : "Control Room"}</button>
            <button type="button" className="dashboard-operator-button dashboard-operator-stop" onClick={() => forceStop(ch)}><Square className="h-3.5 w-3.5" /> Stop</button>
          </div>
        </div>

        <div className="dashboard-columns">
        
        {/* LEFT COLUMN: Visuals */}
        <div className="space-y-4 preview-column">
          
          {/* Matrix Grid */}
          {matrixMode && (
            <div className={`${UI.card} p-3`}>
              <div className="px-4 py-3 border-b border-gray-800 mb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Monitor className="h-4 w-4 text-blue-400" />
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Multi-View Matrix</span>
                  </div>
                  <span className="text-xs text-gray-500">{DEFAULT_CHANNELS.length} channels</span>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-2">
                {DEFAULT_CHANNELS.map((c) => {
                  const running = hasLiveOutputForChannel(c);
                  const locked = isChannelLiveLocked(c);
                  const isSel = c === selected;
                  const inputHealth = getHealthIndicator(c, 'input');
                  
                  return (
                    <button
                      key={c}
                      onClick={() => setSelected(c)}
                      className={`relative group aspect-video rounded-xl overflow-hidden border-2 transition-all duration-300 ${isSel ? "gradient-border scale-[1.02]" : "border-gray-800 opacity-80 hover:opacity-100 hover:border-gray-700 hover:scale-[1.01]"}`}
                    >
                      <div className="absolute inset-0 bg-black">
                        <HlsPlayer channel={c} key={hlsKey[c] || c} />
                      </div>
                      <div className="absolute top-2 left-2 bg-black/70 backdrop-blur px-2 py-1 rounded text-xs font-bold text-white flex items-center gap-1.5 border border-white/10">
                        {fmtLabel(c)}
                        <span className={`w-2 h-2 rounded-full ${running ? "bg-emerald-500 animate-pulse" : "bg-gray-600"}`} />
                        {locked && <Lock className="h-3 w-3 text-amber-300" />}
                        {inputHealth && !inputHealth.healthy && (
                          <span className="w-2 h-2 rounded-full bg-red-500" />
                        )}
                      </div>
                      {running && (
                        <div className="absolute top-2 right-2 bg-emerald-500/20 text-emerald-300 text-[10px] px-1.5 py-0.5 rounded border border-emerald-500/30">
                          LIVE
                        </div>
                      )}
                      {inputHealth && !inputHealth.healthy && (
                        <div className="absolute bottom-2 left-2 bg-red-500/20 text-red-300 text-[10px] px-1.5 py-0.5 rounded border border-red-500/30">
                          INPUT DOWN
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Main Preview */}
          <div className={`${UI.card} preview-sticky`}>
            <div className="px-4 sm:px-5 py-4 border-b border-gray-800 flex items-center justify-between bg-gradient-to-r from-gray-900/50 to-transparent mobile-stack">
              <div className="flex items-center gap-3">
                <div className={`h-2.5 w-2.5 rounded-full ${safeBool(st?.internal?.running) ? "bg-emerald-500 animate-pulse" : "bg-blue-500"}`} />
                <span className="font-bold text-sm tracking-wide">{fmtLabel(ch)} MASTER PREVIEW</span>
                {isPlaylistActive && (
                  <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded-full">
                    {activePlaylistName}  |  Clip {Math.min(currentFileIndex + 1, Math.max(activePlaylistCount, 1))}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-2 sm:mt-0">
                {safeBool(st?.internal?.running) ? (
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-emerald-400 animate-ping" />
                    <span className="text-xs font-mono text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-full border border-emerald-500/20">
                      LIVE  |  {st?.internal?.duration || "00:00:00"}
                    </span>
                  </div>
                ) : (
                  <span className="text-xs font-mono text-amber-500 bg-amber-500/10 px-2 py-1 rounded-full border border-amber-500/20">
                    IDLE
                  </span>
                )}
              </div>
            </div>

            {/* Video Area */}
            <div className="aspect-video bg-black relative group">
              {f.mode === "hls" ? (
                <HlsPlayer channel={ch} key={hlsKey[ch] || ch} />
              ) : f.mode === "server" && f.input ? (
                <VodPlayer
                  key={vodKey[ch] || ch + f.input}
                  file={String(f.input).split("/").pop()}
                  refCallback={(el) => { vodRefs.current[ch] = el; }}
                  showControls={true}
                  autoPlay={false}
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-600">
                  <Film className="h-16 sm:h-20 w-16 sm:w-20 mb-3 opacity-20" />
                  <span className="text-sm font-medium">NO SIGNAL SOURCE</span>
                  <span className="text-xs mt-1 text-center px-4">Select a file or URL to begin</span>
                </div>
              )}
              
              {/* Playlist Indicator */}
              {isPlaylistActive && (
                <div className="absolute top-3 left-3 bg-emerald-900/70 backdrop-blur px-3 py-1.5 rounded-lg text-xs text-emerald-300 border border-emerald-500/30 flex items-center gap-2">
                  <List className="h-3 w-3" />
                  <span>{activePlaylistName}</span>
                  <span className="text-emerald-100">Clip {Math.min(currentFileIndex + 1, Math.max(activePlaylistCount, 1))}/{activePlaylistCount}</span>
                </div>
              )}
              
              {/* Overlay Controls */}
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                <button onClick={() => bumpHls(ch)} className="bg-black/60 backdrop-blur hover:bg-blue-600 text-white p-2 rounded-lg border border-white/10 transition">
                  <RefreshCw className="h-4 w-4" />
                </button>
                <button onClick={() => vodRefs.current[ch]?.play()} className="bg-black/60 backdrop-blur hover:bg-emerald-600 text-white p-2 rounded-lg border border-white/10 transition">
                  <Play className="h-4 w-4" />
                </button>
                <button onClick={() => vodRefs.current[ch]?.pause()} className="bg-black/60 backdrop-blur hover:bg-amber-600 text-white p-2 rounded-lg border border-white/10 transition">
                  <Pause className="h-4 w-4" />
                </button>
              </div>
              
              {/* Health Indicator */}
              {getHealthIndicator(ch, 'input') && (
                <div className={`absolute bottom-3 right-3 backdrop-blur px-3 py-1.5 rounded-lg text-xs border ${getHealthIndicator(ch, 'input').healthy ? 'bg-emerald-900/30 text-emerald-300 border-emerald-500/30' : 'bg-red-900/30 text-red-300 border-red-500/30'}`}>
                  {getHealthIndicator(ch, 'input').healthy ? '✓ Input Healthy' : '⚠ Input Issue'}
                </div>
              )}
              
              {/* Video Info Overlay */}
              {f.input && (
                <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur px-3 py-1.5 rounded-lg text-xs text-gray-300 border border-white/10 max-w-[70%] truncate">
                  {f.inputType === "file" ? f.input.split("/").pop() : f.input}
                  {isPlaylistActive && (
                    <span className="ml-2 text-blue-300">
                      Clip {Math.min(currentFileIndex + 1, Math.max(activePlaylistCount, 1))} of {Math.max(activePlaylistCount, 1)}
                    </span>
                  )}
                </div>
              )}
            </div>

            {isRecordedYoutube && (
              <div className="mx-4 sm:mx-5 mb-1 rounded-xl border border-red-500/25 bg-red-500/5 px-4 py-3">
                <div className="flex items-center justify-between gap-3 text-xs mb-2">
                  <span className="font-semibold text-red-100">Recorded YouTube loop anchor</span>
                  <span className="font-mono text-red-200">{formatYoutubeTime(youtubeAnchor)}{youtubeDuration ? ` / ${formatYoutubeTime(youtubeDuration)}` : ""}</span>
                </div>
                {youtubeDuration > 0 ? (
                  <input
                    type="range"
                    min="0"
                    max={youtubeDuration}
                    step="1"
                    value={youtubeAnchor}
                    onChange={(e) => updateField(ch, "youtube_anchor_seconds", Number(e.target.value))}
                    className="w-full accent-red-500"
                    aria-label="Recorded YouTube loop anchor"
                  />
                ) : (
                  <div className="text-xs text-gray-400">Use “Detect link” under Source to load the video duration and enable the seek bar.</div>
                )}
                <div className="text-[11px] text-gray-500 mt-2">Restart encoding after changing the anchor. The recorded video restarts from this point on every loop.</div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="p-4 sm:p-5 flex items-center justify-between gap-3 bg-gradient-to-b from-transparent to-black/30 mobile-stack">
              <button
                onClick={() => startInternal(ch)}
                className={`flex-1 py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-3 shadow-lg transition-all ${safeBool(st?.internal?.running) ? "bg-amber-500/10 text-amber-400 border border-amber-500/40 hover:bg-amber-500/20" : UI.btnPrimary}`}
              >
                {safeBool(st?.internal?.running) ? 
                  <RefreshCw className="h-4 w-4 animate-spin" /> : 
                  <Play className="h-5 w-5 fill-current" />
                }
                {safeBool(st?.internal?.running) ? "Restart Encoder" : "Start Encoding"}
              </button>
              
              <div className="flex gap-2 mt-3 sm:mt-0">
                <button onClick={() => setShowRecordingPanel(true)} className={`p-3.5 rounded-xl ${isRecording ? UI.btnDanger : UI.btnNeutral}`} title="Record">
                  <Circle className="h-5 w-5 fill-current" />
                </button>
                <button onClick={() => forceStop(ch)} className={`p-3.5 rounded-xl ${UI.btnDanger}`} title="Force Stop All">
                  <Square className="h-5 w-5 fill-current" />
                </button>
                <button onClick={() => clearChannel(ch)} className={`p-3.5 rounded-xl ${UI.btnNeutral}`} title="Reset Settings">
                  <RotateCcw className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="px-4 sm:px-5 pb-3">
              <div className="rounded-2xl border border-gray-800 bg-gray-950/45 px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-gray-500">Channel Name</div>
                  {editingChannelName ? (
                    <input
                      autoFocus
                      value={channelNameDraft}
                      onChange={(e) => setChannelNameDraft(e.target.value)}
                      onBlur={handleChannelNameBlur}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveChannelNameEdit();
                        if (e.key === "Escape") {
                          channelNameCancelRef.current = true;
                          setEditingChannelName(false);
                          setChannelNameDraft("");
                        }
                      }}
                      className="mt-1 w-full bg-gray-900 border border-blue-500/40 rounded-xl px-3 py-2 text-sm font-semibold text-white outline-none focus:ring-2 focus:ring-blue-500/20"
                      placeholder={defaultChannelName(ch)}
                    />
                  ) : (
                    <div className="mt-1 flex items-center gap-2 min-w-0">
                      <span className="text-base font-bold text-white truncate">{channelDisplayName}</span>
                      <span className="text-xs text-gray-500">{fmtLabel(ch)}</span>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={beginChannelNameEdit}
                  className="shrink-0 p-2.5 rounded-xl bg-gray-800/80 hover:bg-gray-700 text-gray-300 border border-gray-700 transition"
                  title="Rename channel"
                >
                  <Edit3 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="px-4 sm:px-5 pb-4 sm:pb-5">
              <div className="system-meter-strip dashboard-telemetry-strip">
                {systemMeters.map((meter) => {
                  const Icon = meter.icon;
                  return (
                    <div key={meter.key} className="system-meter-card">
                      <Icon className="h-3.5 w-3.5" style={{ color: meter.tone }} />
                      <div className="min-w-0">
                        <span className="system-meter-label">{meter.label}</span>
                        <span className="system-meter-detail"> {Math.round(meter.percent)}%</span>
                      </div>
                      <span className="system-meter-sub">{meter.detail}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Controls */}
        <div className="space-y-4">
          
          <div className="dashboard-status-strip">
            <button onClick={() => setActiveConfigTab("source")}>INPUT {getHealthIndicator(ch, "input")?.healthy ? "✓" : "○"}</button>
            <button onClick={() => setActiveConfigTab("encoder")}>ENCODER {safeBool(st?.internal?.running) ? `● ${st?.internal?.bitrate || ""}` : "○"}</button>
            <button onClick={() => setActiveConfigTab("destinations")}>FACEBOOK {safeBool(st?.facebook?.running) ? "●" : "○"}</button>
            <button onClick={() => setActiveConfigTab("destinations")}>YOUTUBE {safeBool(st?.youtube?.running) ? "●" : "○"}</button>
            <button onClick={() => setActiveConfigTab("destinations")}>CUSTOM {safeBool(st?.custom?.running) ? `● ${runningProtocolLabel(st?.custom?.protocol)}` : "○"}</button>
            <button onClick={() => setActiveConfigTab("graphics")}>GRAPHICS {overlayLayers.filter((layer) => layer.enabled).length}</button>
            <button onClick={() => setShowRecordingPanel(true)}>REC {isRecording ? "●" : "○"}</button>
          </div>

          <div className="space-y-4">
            <div className="dashboard-mode-commandbar">
              <span className="dashboard-mode-label">MODE</span>
              <div className="dashboard-mode-grid">
                {[
                  { key: "encoder", label: "Encoder", icon: Cpu, sub: "File or local clip ingest, trim, overlays, outputs." },
                  { key: "restreamer", label: "Restreamer", icon: Globe, sub: "RTMP, UDP, SRT and other network feeds." },
                  { key: "playlist", label: "Playout", icon: List, sub: "Playlist rundown, current/next/cue and live clip control." },
                ].map((modeCard) => {
                  const Icon = modeCard.icon;
                  const active = currentDashboardMode === modeCard.key;
                  const disabled = channelModeLocked && currentDashboardMode !== modeCard.key;
                  return (
                    <button
                      key={modeCard.key}
                      onClick={() => setDashboardModeForChannel(ch, modeCard.key)}
                      disabled={disabled}
                      className={`dashboard-mode-card ${active ? "dashboard-mode-card-active" : ""} ${disabled ? "dashboard-mode-card-disabled" : ""}`}
                    >
                      <Icon className="h-3.5 w-3.5" /> {modeCard.label}
                    </button>
                  );
                })}
              </div>
              <span className="dashboard-mode-lock">{channelModeLocked ? <><Lock className="h-3 w-3" /> Locked</> : "Ready"}</span>
            </div>

            {currentDashboardMode === "playlist" && (
              <div className={UI.card}>
                <div className="px-4 sm:px-5 py-4 border-b border-gray-800 flex items-center justify-between mobile-stack gap-3">
                  <div className="flex items-center gap-3">
                    <List className="h-5 w-5 text-emerald-400" />
                    <div>
                      <h3 className="text-sm font-bold text-white">Playout Dashboard</h3>
                      <p className="text-xs text-gray-500">Current, next, cue, clip trims, loop single, and playlist loop control.</p>
                    </div>
                  </div>
                  <button onClick={() => setShowPlaylistManager(true)} className={`px-3 py-2 rounded-xl text-sm ${UI.btnNeutral}`}>
                    Open Playlist Manager
                  </button>
                </div>
                <div className="p-4 sm:p-5 space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[
                      { label: "Current", item: currentPlaylistItem, tone: "border-emerald-500/30 bg-emerald-500/10 text-emerald-100" },
                      { label: "Next", item: nextPlaylistItem, tone: "border-blue-500/20 bg-blue-500/10 text-blue-100" },
                      { label: "Cue", item: cuePlaylistItem, tone: "border-violet-500/20 bg-violet-500/10 text-violet-100" },
                    ].map((slot) => (
                      <div key={slot.label} className={`rounded-2xl border p-4 ${slot.tone}`}>
                        <div className="text-[11px] uppercase tracking-[0.2em] opacity-80">{slot.label}</div>
                        <div className="mt-2 text-sm font-semibold truncate">
                          {slot.item ? playlistItemLabel(slot.item) : "No clip queued"}
                        </div>
                        <div className="mt-2 text-xs opacity-80 flex items-center gap-3 flex-wrap">
                          <span>{slot.item?.duration_str || "--:--:--"}</span>
                          <span>{slot.item ? playlistItemProtocol(slot.item) : "IDLE"}</span>
                          {slot.item?.trim_enabled && <span>Trim Active</span>}
                        </div>
                      </div>
                    ))}
                  </div>

                  {activePlaylistName ? (
                    <div className="rounded-2xl border border-gray-800 bg-black/20 overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between mobile-stack gap-3">
                        <div>
                          <div className="text-sm font-semibold text-white">{activePlaylistName}</div>
                          <div className="text-xs text-gray-500">
                            Playlist Loop: {activePlaylistInfo?.loop_mode ? "On" : "Off"} | Item Loop: {activeLoopItem ? "On" : "Off"} | {activePlaylistCount} clips{selectedPlaylistItem ? ` | Selected: ${playlistItemLabel(selectedPlaylistItem)}` : ""}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap justify-end playout-transport">
                          <button onClick={playSelectedPlaylistItem} disabled={playlistBusy || !activePlaylistItems.length} className="px-3 py-2 rounded-xl border border-emerald-500/20 bg-emerald-600/20 text-emerald-200 hover:bg-emerald-600/30 disabled:opacity-40">
                            <span className="inline-flex items-center gap-2"><Play className="h-4 w-4" /> PLAY</span>
                          </button>
                          <button onClick={() => playPlaylistOffset(1)} disabled={playlistBusy || !activePlaylistItems.length} className="px-3 py-2 rounded-xl border border-blue-500/20 bg-blue-600/20 text-blue-200 hover:bg-blue-600/30 disabled:opacity-40">
                            NEXT
                          </button>
                          <button onClick={cuePlaylistSelection} disabled={playlistBusy || activePlaylistItems.length < 2} className="px-3 py-2 rounded-xl border border-violet-500/20 bg-violet-600/20 text-violet-200 hover:bg-violet-600/30 disabled:opacity-40">
                            CUE
                          </button>
                          <button onClick={jumpToPlaylistItem} disabled={playlistBusy || !activePlaylistItems.length} className="px-3 py-2 rounded-xl border border-cyan-500/20 bg-cyan-600/20 text-cyan-200 hover:bg-cyan-600/30 disabled:opacity-40">
                            JUMP
                          </button>
                          <button onClick={status?.[selected]?.playlist?.paused ? resumePlaylist : pausePlaylist} className="px-3 py-2 rounded-xl bg-amber-600/20 hover:bg-amber-600/30 text-amber-300 border border-amber-500/20 flex items-center gap-2">{status?.[selected]?.playlist?.paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />} {status?.[selected]?.playlist?.paused ? "Resume" : "Pause"}</button>
                        <button onClick={restorePlaylistLoop} disabled={playlistBusy || !activePlaylistItems.length} className="px-3 py-2 rounded-xl border border-amber-500/20 bg-amber-600/20 text-amber-100 hover:bg-amber-600/30 disabled:opacity-40">
                            <span className="inline-flex items-center gap-2"><Repeat className="h-4 w-4" /> Loop Playlist</span>
                          </button>
                          <button onClick={stopPlaylist} className={`px-3 py-2 rounded-xl ${UI.btnDanger}`}>
                            <span className="inline-flex items-center gap-2"><Square className="h-4 w-4" /> Stop</span>
                          </button>
                        </div>
                      </div>
                      <div className="divide-y divide-gray-800 max-h-[30rem] overflow-y-auto playlist-rundown-scroller">
                        {activePlaylistItems.map((item, idx) => {
                          const isCurrent = idx === currentFileIndex;
                          const isNext = nextPlaylistItem?.path === item.path && !isCurrent;
                          const isCue = cuePlaylistItem?.path === item.path && !isCurrent;
                          return (
                            <div
                              key={`playout-${item.path}-${idx}`}
                              onClick={() => selectPlaylistItem(activePlaylistName, idx)}
                              onDoubleClick={() => openTrimModal(item)}
                              className={`p-4 flex items-start justify-between gap-4 cursor-pointer ${
                                selectedPlaylistName === activePlaylistName && selectedPlaylistIndex === idx
                                  ? "bg-cyan-500/10"
                                  : isCurrent
                                    ? "bg-emerald-500/10"
                                    : isNext
                                      ? "bg-blue-500/5"
                                      : isCue
                                        ? "bg-violet-500/5"
                                        : "hover:bg-gray-800/20"
                              }`}
                            >
                              <div className="min-w-0 flex-1 space-y-3">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-[11px] px-2 py-1 rounded-full border border-gray-700 bg-gray-800/70 text-gray-300">
                                    {String(idx + 1).padStart(2, "0")}
                                  </span>
                                  <span className={`text-[11px] px-2 py-1 rounded-full border ${playlistItemTone(item)}`}>{playlistItemProtocol(item)}</span>
                                  <span className={`text-sm font-semibold truncate ${isCurrent ? "text-emerald-100" : "text-white"}`}>{playlistItemLabel(item)}</span>
                                  {selectedPlaylistName === activePlaylistName && selectedPlaylistIndex === idx && <span className="text-[11px] px-2 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/20 text-cyan-200">Selected</span>}
                                  {isCurrent && <span className="text-[11px] px-2 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/20 text-emerald-200">On Air</span>}
                                  {isNext && <span className="text-[11px] px-2 py-1 rounded-full border border-blue-500/30 bg-blue-500/20 text-blue-200">Next</span>}
                                  {isCue && <span className="text-[11px] px-2 py-1 rounded-full border border-violet-500/30 bg-violet-500/20 text-violet-200">Cue</span>}
                                </div>
                                <div className="text-xs text-gray-500 flex items-center gap-3 flex-wrap">
                                  <span className="text-emerald-400/80 font-mono">{activePlaylistStartTimes[idx] || "--:--:--"}</span>
                                  <span>{item.duration_str || "--:--:--"}</span>
                                  <span>{item.resolution || "Unknown"}</span>
                                  <span>{formatBytes(item.size)}</span>
                                  {!item.exists && <span className="text-red-300">Missing source</span>}
                                  {canTrimPlaylistItem(item) && <span className="text-cyan-300">Double-click to trim</span>}
                                  {item.trim_enabled && <span className="text-emerald-300">Trim {item.mark_in || "00:00:00"} - {item.mark_out || "END"}</span>}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap justify-end">
                                <button
                                  onClick={() => (isCurrent ? stopPlaylistItem() : playPlaylistItem(activePlaylistName, idx, false))}
                                  disabled={playlistBusy || !item.exists}
                                  className={`px-3 py-2 rounded-xl border transition disabled:opacity-40 disabled:cursor-not-allowed ${isCurrent ? "bg-red-600/20 text-red-300 border-red-500/20 hover:bg-red-600/30" : "bg-emerald-600/20 text-emerald-300 border-emerald-500/20 hover:bg-emerald-600/30"}`}
                                >
                                  <span className="inline-flex items-center gap-2">{isCurrent ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}{isCurrent ? "Stop" : "Play"}</span>
                                </button>
                                <button
                                  onClick={() => playPlaylistItem(activePlaylistName, idx, true)}
                                  disabled={playlistBusy || !item.exists}
                                  className="px-3 py-2 rounded-xl border border-violet-500/20 bg-violet-600/20 text-violet-300 hover:bg-violet-600/30 transition disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  <span className="inline-flex items-center gap-2"><Repeat className="h-4 w-4" /> Loop Single</span>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-gray-700 bg-gray-900/30 p-6 text-center text-gray-500">
                      No playlist is active on this channel. Choose a playlist below or open Playlist Manager to build one.
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="dashboard-configuration-anchor" />
            {/* Configuration tabs */}
            <div className="dashboard-detail-sections">
            <div className="dashboard-config-tabs" role="tablist" aria-label="Channel configuration">
              {[
                { key: 'source', label: 'Source', icon: HardDrive, visible: showSourceSection },
                { key: 'encoder', label: 'Encoder', icon: Cpu, visible: showEncoderSection },
                { key: 'graphics', label: 'Graphics', icon: Layers, visible: showGraphicsSection },
                { key: 'destinations', label: 'Destinations', icon: Globe, visible: showDestinationsSection }
              ].filter((section) => section.visible).map((section) => {
                const Icon = section.icon;
                const isExpanded = activeConfigTab === section.key;
                return (
                  <button
                    key={section.key}
                    onClick={() => setActiveConfigTab(section.key)}
                    className={`dashboard-config-tab ${
                      isExpanded 
                        ? "bg-blue-500/20 text-blue-300 border border-blue-500/30" 
                        : "bg-gray-800/50 text-gray-500 hover:text-gray-300 border border-gray-700"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span className="mobile-hidden">{section.label}</span>
                  </button>
                );
              })}
            </div>
            
            {/* SOURCE CONTROL - UPDATED WITH PLAYLIST OPTION */}
            {showSourceSection && activeConfigTab === "source" && (
              <div className={UI.card}>
                <div className="px-4 sm:px-5 py-4 border-b border-gray-800 flex items-center justify-between mobile-stack">
                  <div className="flex items-center gap-3">
                    <HardDrive className="h-5 w-5 text-blue-400" />
                    <h3 className="text-sm font-bold text-white">
                      {currentDashboardMode === "playlist" ? "Playout Source & Rundown" : currentDashboardMode === "restreamer" ? "Restream Source Configuration" : "Input Source Configuration"}
                    </h3>
                    {getHealthIndicator(ch, 'input') && (
                      <span className={`text-xs px-2 py-1 rounded-full ${getHealthIndicator(ch, 'input').healthy ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>
                        {getHealthIndicator(ch, 'input').healthy ? '✓' : '⚠'}
                      </span>
                    )}
                  </div>
                  <button 
                    onClick={() => setActiveConfigTab("source")}
                    className="text-gray-500 hover:text-gray-300"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                </div>
                
                <div className="p-4 sm:p-5 space-y-6">
                  {/* State Persistence Toggle */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="saveState"
                      checked={f.save_state !== false}
                      onChange={(e) => updateField(ch, "save_state", e.target.checked)}
                      className="rounded border-gray-600"
                    />
                    <label htmlFor="saveState" className="text-sm text-gray-300">
                      Auto-save and restore channel state
                    </label>
                    <span className="text-xs text-gray-500">(Channel will auto-start on service restart)</span>
                  </div>

                  {/* Source Type Selection */}
                  {currentDashboardMode === "encoder" && (
                    <div>
                      <div className={UI.label}>Source Type</div>
                      <div className="flex bg-gray-900 p-1 rounded-xl border border-gray-800 w-fit flex-wrap">
                        <button 
                          onClick={() => updateField(ch, "inputType", "file")} 
                          className={`px-3 sm:px-4 py-2 rounded-lg text-xs font-medium transition flex items-center gap-2 ${f.inputType === "file" ? "bg-gray-800 text-white shadow" : "text-gray-500 hover:text-gray-300"}`}
                        >
                          <HardDrive className="h-3.5 w-3.5" /> <span className="mobile-hidden">File</span>
                        </button>
                        <button 
                          onClick={() => updateField(ch, "inputType", "url")} 
                          className={`px-3 sm:px-4 py-2 rounded-lg text-xs font-medium transition flex items-center gap-2 ${f.inputType === "url" ? "bg-gray-800 text-white shadow" : "text-gray-500 hover:text-gray-300"}`}
                        >
                          <Globe className="h-3.5 w-3.5" /> <span className="mobile-hidden">URL</span>
                        </button>
                        <button 
                          onClick={() => updateField(ch, "inputType", "playlist")} 
                          className={`px-3 sm:px-4 py-2 rounded-lg text-xs font-medium transition flex items-center gap-2 ${f.inputType === "playlist" ? "bg-gray-800 text-white shadow" : "text-gray-500 hover:text-gray-300"}`}
                        >
                          <List className="h-3.5 w-3.5" /> <span className="mobile-hidden">Playlist</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Source Input */}
                  {sourceInputType === "file" ? (
                    <div className="space-y-4">
                      <div className="flex gap-3 text-sm mb-1">
                        <button 
                          onClick={() => updateField(ch, "mode", "server")} 
                          className={f.mode === "server" ? "text-blue-400 font-bold underline underline-offset-4" : "text-gray-500 hover:text-white"}
                        >
                          Browse Library
                        </button>
                        <span className="text-gray-700">|</span>
                        <button 
                          onClick={() => updateField(ch, "mode", "upload")} 
                          className={f.mode === "upload" ? "text-blue-400 font-bold underline underline-offset-4" : "text-gray-500 hover:text-white"}
                        >
                          Upload New
                        </button>
                      </div>
                      
                      {f.mode === "server" ? (
                        <div className="relative">
                          <select 
                            className={`${UI.input} appearance-none`}
                            onChange={async (e) => {
                              const name = e.target.value;
                              if (!name) return;
                              const abs = `/home/FTP/loop/video/${name}`;
                              updateField(ch, "input", abs);
                              await loadMarksForInput(ch, abs);
                            }}
                            value={f.input ? String(f.input).split("/").pop() : ""}
                          >
                            <option value="">Select a video file...</option>
                            {serverFiles.map((x) => (
                              <option key={x.name} value={x.name}>{x.name} ({x.duration_str})</option>
                            ))}
                          </select>
                          <div className="absolute right-4 top-3.5 pointer-events-none">
                            <Server className="h-4 w-4 text-gray-500" />
                          </div>
                        </div>
                      ) : (
                        <div className="relative group">
                          <input 
                            type="file"
                            accept="video/*"
                            onChange={(e) => uploadFile(e, ch)} 
                            className="w-full text-sm text-gray-400 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-gradient-to-r file:from-blue-600 file:to-indigo-600 file:text-white hover:file:from-blue-500 hover:file:to-indigo-500 cursor-pointer" 
                          />
                          {uploadProgress[ch] > 0 && (
                            <div className="mt-3 w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                              <div 
                                className="bg-gradient-to-r from-blue-500 to-indigo-500 h-full transition-all duration-300" 
                                style={{ width: `${uploadProgress[ch]}%` }} 
                              />
                            </div>
                          )}
                          {uploadProgress[ch] > 0 && (
                            <div className="text-xs text-center mt-1">
                              {uploadProgress[ch] < 100
                                ? <span className="text-blue-400">Uploading: {uploadProgress[ch]}%</span>
                                : <span className="text-emerald-400">✓ Upload complete</span>
                              }
                            </div>
                          )}
                          {f.input && (
                            <div className="mt-2 text-xs text-emerald-400 truncate">
                              Selected: {String(f.input).split("/").pop()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : sourceInputType === "url" ? (
                    <div className="space-y-4">
                      <div className="relative">
                        <input 
                          className={`${UI.input} pl-10 font-mono text-sm`} 
                          placeholder="rtmp://... | http://... | srt://... | udp://..."
                          value={f.input || ""}
                          onChange={(e) => updateField(ch, "input", e.target.value.replace(/\s+/g, ""))}
                        />
                        <Globe className="absolute left-3 top-3.5 h-4 w-4 text-gray-500" />
                      </div>
                      {isYoutubeSource && (
                        <div className="rounded-2xl border border-red-500/25 bg-red-500/5 p-4 space-y-3">
                          <div className="flex items-center justify-between gap-3 mobile-stack">
                            <div>
                              <div className="text-sm font-semibold text-red-100 flex items-center gap-2"><Youtube className="h-4 w-4 text-red-400" /> YouTube source</div>
                              <div className="text-xs text-gray-400 mt-1">Choose Live for a broadcast, or Recorded for an uploaded/archived video that loops.</div>
                            </div>
                            <button type="button" onClick={() => inspectYoutubeSource(ch)} className="px-3 py-2 rounded-xl text-xs border border-red-400/25 bg-red-500/10 hover:bg-red-500/20 text-red-200">Detect link</button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {[{ key: "live", label: "YouTube Live" }, { key: "video", label: "Recorded Video (Loop)" }].map((choice) => (
                              <button
                                type="button"
                                key={choice.key}
                                onClick={() => updateField(ch, "youtube_source_kind", choice.key)}
                                className={`px-3 py-2 rounded-xl text-xs font-semibold border ${f.youtube_source_kind === choice.key ? "border-red-400/50 bg-red-500/20 text-red-100" : "border-gray-700 bg-gray-900/60 text-gray-400 hover:text-gray-200"}`}
                              >{choice.label}</button>
                            ))}
                          </div>
                          {f.youtube_title && <div className="text-xs text-gray-300 truncate">{f.youtube_source_kind === "video" ? "Recorded" : "Live"}: {f.youtube_title}</div>}
                        </div>
                      )}
                      <div className="rounded-2xl border border-dashed border-amber-500/25 bg-amber-500/5 p-4 space-y-4">
                        <div className="flex items-start justify-between gap-3 mobile-stack">
                          <label className="flex items-center gap-3 text-sm text-gray-200">
                            <input
                              type="checkbox"
                              checked={!!f.fallback_enabled}
                              onChange={(e) => updateField(ch, "fallback_enabled", e.target.checked)}
                              className="rounded border-gray-600"
                            />
                            <span className="font-medium">Use fallback media when the source drops</span>
                          </label>
                          <span className="text-xs text-gray-500">Path: /home/FTP/loop/fallback</span>
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                          <div className="relative">
                            <select
                              className={`${UI.input} appearance-none`}
                              value={f.fallback_media || ""}
                              onChange={(e) => updateField(ch, "fallback_media", e.target.value)}
                            >
                              <option value="">Select fallback media...</option>
                              {fallbackOptions.map((item) => (
                                <option key={item.path} value={item.path}>
                                  {fallbackFileLabel(item)}
                                </option>
                              ))}
                            </select>
                            <div className="absolute right-4 top-3.5 pointer-events-none">
                              <Video className="h-4 w-4 text-gray-500" />
                            </div>
                          </div>
                          <div className="relative group">
                            <input 
                              type="file"
                              accept="image/*,video/*"
                              onChange={(e) => uploadFallbackMedia(e, ch)} 
                              className="w-full text-sm text-gray-400 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-gradient-to-r file:from-amber-600 file:to-orange-600 file:text-white hover:file:from-amber-500 hover:file:to-orange-500 cursor-pointer" 
                            />
                            {uploadProgress[`${ch}:fallback`] > 0 && (
                              <div className="mt-3 w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                                <div 
                                  className="bg-gradient-to-r from-amber-500 to-orange-500 h-full transition-all duration-300" 
                                  style={{ width: `${uploadProgress[`${ch}:fallback`]}%` }} 
                                />
                              </div>
                            )}
                            {uploadProgress[`${ch}:fallback`] > 0 && (
                              <div className="text-xs text-center mt-1">
                                {uploadProgress[`${ch}:fallback`] < 100
                                  ? <span className="text-amber-400">Uploading: {uploadProgress[`${ch}:fallback`]}%</span>
                                  : <span className="text-emerald-400">✓ Upload complete</span>
                                }
                              </div>
                            )}
                            {f.fallback_media && (
                              <div className="mt-2 text-xs text-emerald-400 truncate">
                                Selected: {String(f.fallback_media).split("/").pop()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between gap-3 mobile-stack">
                        <div className="text-sm text-gray-400">Select a playlist to play or take over the active rundown:</div>
                        <button onClick={() => setShowPlaylistManager(true)} className="text-xs px-3 py-1.5 rounded-lg border border-gray-700 bg-gray-800/70 hover:bg-gray-700 text-gray-300">
                          Manage Playlists
                        </button>
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        {Object.keys(playlists).map((name) => {
                          const pl = playlists[name];
                          const isActivePlayout = activePlaylistName === name;
                          return (
                            <div key={name} className={`flex items-center justify-between p-3 rounded-xl border mobile-stack ${isActivePlayout ? "border-emerald-500/30 bg-emerald-500/10" : "border-gray-800 bg-gray-900/50"}`}>
                              <div className="flex-1">
                                <div className="font-medium text-gray-200 flex items-center gap-2 flex-wrap">
                                  <span>{name}</span>
                                  {isActivePlayout && <span className="text-[11px] px-2 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/20 text-emerald-200">On Air</span>}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {pl.files?.length || 0} items  |  {pl.loop_mode ? "Playlist Loop" : "Play Once"}  |  {pl.shuffle ? "Shuffle" : "In order"}
                                </div>
                                {pl.now_playing && (
                                  <div className="mt-2 text-xs text-gray-400 space-y-1">
                                    <div>Current: {playlistItemLabel(pl.now_playing)}</div>
                                    {pl.next_item && <div>Next: {playlistItemLabel(pl.next_item)}</div>}
                                    {pl.cue_item && <div>Cue: {playlistItemLabel(pl.cue_item)}</div>}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-2 sm:mt-0">
                                <button 
                                  onClick={() => startPlaylist(name, false)}
                                  className="px-3 py-1.5 rounded-lg bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 text-xs"
                                >
                                  Play
                                </button>
                                <button 
                                  onClick={() => startPlaylist(name, true)}
                                  className="px-3 py-1.5 rounded-lg bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 text-xs"
                                >
                                  Loop All
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        {Object.keys(playlists).length === 0 && (
                          <div className="text-center py-4 text-gray-500 text-sm">
                            No playlists created yet. Go to Playlist Manager to create one.
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Trimming Controls - Only for files */}
                  {currentDashboardMode === "encoder" && sourceInputType === "file" && f.input && (
                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-800 space-y-4">
                      <div className="flex items-center justify-between mobile-stack">
                        <div className={UI.label}>
                          <Clock className="h-4 w-4" /> Segment Trimming
                        </div>
                        <button 
                          onClick={() => saveMarks(ch)}
                          className="text-xs bg-blue-500/10 text-blue-300 px-3 py-1.5 rounded-lg border border-blue-500/20 hover:bg-blue-500/20 mt-2 sm:mt-0"
                        >
                          <Save className="h-3 w-3 mr-1" /> Save Marks
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-xs text-gray-500 mb-1.5">IN POINT (HH:MM:SS)</div>
                          <input 
                            className={`${UI.input} py-2.5 text-center font-mono text-sm`} 
                            value={f.mark_in || ""} 
                            onChange={(e) => updateField(ch, "mark_in", e.target.value)} 
                            placeholder="00:00:00" 
                          />
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 mb-1.5">OUT POINT (HH:MM:SS)</div>
                          <input 
                            className={`${UI.input} py-2.5 text-center font-mono text-sm`} 
                            value={f.mark_out || ""} 
                            onChange={(e) => updateField(ch, "mark_out", e.target.value)} 
                            placeholder="00:00:00" 
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <button 
                          onClick={() => captureMark(ch, "in")} 
                          className="py-2.5 rounded-lg bg-gray-800 hover:bg-blue-500/20 text-xs font-bold text-gray-300 hover:text-blue-300 border border-gray-700 transition"
                        >
                          SET IN
                        </button>
                        <button 
                          onClick={() => captureMark(ch, "out")} 
                          className="py-2.5 rounded-lg bg-gray-800 hover:bg-blue-500/20 text-xs font-bold text-gray-300 hover:text-blue-300 border border-gray-700 transition"
                        >
                          SET OUT
                        </button>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="loopSegment"
                          checked={f.loop_segment || false}
                          onChange={(e) => updateField(ch, "loop_segment", e.target.checked)}
                          className="rounded border-gray-600"
                        />
                        <label htmlFor="loopSegment" className="text-sm text-gray-300">
                          Loop between marks continuously
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ENCODER SETTINGS */}
            {showEncoderSection && activeConfigTab === "encoder" && (
              <div className={UI.card}>
                <div className="px-4 sm:px-5 py-4 border-b border-gray-800 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Cpu className="h-5 w-5 text-gray-400" />
                    <h3 className="text-sm font-bold text-white">Encoder Configuration</h3>
                  </div>
                  <button 
                    onClick={() => setActiveConfigTab("encoder")}
                    className="text-gray-500 hover:text-gray-300"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                </div>
                <div className="p-4 sm:p-5 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className={UI.label}><Cpu className="h-3 w-3" /> GPU</div>
                      <select className={UI.input} value={f.gpu_index} onChange={(e) => updateField(ch, "gpu_index", e.target.value)}>
                        {GPU_INDICES.map(g => <option key={g} value={g}>NVIDIA GPU {g}</option>)}
                      </select>
                    </div>
                    <div>
                      <div className={UI.label}>Video Codec</div>
                      <select className={UI.input} value={f.video_codec} onChange={(e) => updateField(ch, "video_codec", e.target.value)}>
                        {VIDEO_CODECS.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <div className={UI.label}>Video Bitrate</div>
                      <input
                        className={UI.input}
                        list="internal-video-bitrates"
                        placeholder="5000k"
                        value={f.video_bitrate || ""}
                        onChange={(e) => updateField(ch, "video_bitrate", e.target.value.replace(/[^\dkKmM]/g, ""))}
                      />
                    </div>
                    <div>
                      <div className={UI.label}>Audio Bitrate</div>
                      <input
                        className={UI.input}
                        list="internal-audio-bitrates"
                        placeholder="192k"
                        value={f.audio_bitrate || ""}
                        onChange={(e) => updateField(ch, "audio_bitrate", e.target.value.replace(/[^\dkKmM]/g, ""))}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <div className={UI.label}>VBV Buffer</div>
                      <input
                        className={UI.input}
                        placeholder="5000k"
                        value={f.encoder_bufsize || ""}
                        onChange={(e) => updateField(ch, "encoder_bufsize", e.target.value.replace(/[^\dkKmM]/g, ""))}
                      />
                    </div>
                    <div>
                      <div className={UI.label}>GOP / I-Frame</div>
                      <input
                        className={UI.input}
                        placeholder="50"
                        value={f.encoder_gop_size || ""}
                        onChange={(e) => updateField(ch, "encoder_gop_size", e.target.value.replace(/[^\d]/g, ""))}
                      />
                    </div>
                    <div>
                      <div className={UI.label}>Min I-Frame</div>
                      <input
                        className={UI.input}
                        placeholder="50"
                        value={f.encoder_keyint_min || ""}
                        onChange={(e) => updateField(ch, "encoder_keyint_min", e.target.value.replace(/[^\d]/g, ""))}
                      />
                    </div>
                    <div>
                      <div className={UI.label}>B-Frames</div>
                      <input
                        className={UI.input}
                        placeholder="2"
                        value={f.encoder_bframes || ""}
                        onChange={(e) => updateField(ch, "encoder_bframes", e.target.value.replace(/[^\d]/g, ""))}
                      />
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    Standard internal RTMP defaults: CBR NVENC, 5000k video, 5000k VBV, 192k AAC, GOP 50, min I-frame 50, B-frames 2, forced IDR on.
                  </div>
                  <datalist id="internal-video-bitrates">
                    {["4000k", "5000k", "6000k", "8000k", "10000k"].map((value) => <option key={value} value={value} />)}
                  </datalist>
                  <datalist id="internal-audio-bitrates">
                    {["128k", "192k", "256k", "320k"].map((value) => <option key={value} value={value} />)}
                  </datalist>
                </div>
              </div>
            )}

            {/* GRAPHICS / OVERLAY */}
            {showGraphicsSection && activeConfigTab === "graphics" && (
              <div className={UI.card}>
                <div className="px-4 sm:px-5 py-4 border-b border-gray-800 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Layers className="h-5 w-5 text-purple-400" />
                    <h3 className="text-sm font-bold text-white">Graphics Overlay</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => addAdditionalLogo(ch)}
                      className="text-xs bg-gray-800 hover:bg-gray-700 px-3 py-1.5 rounded-lg border border-gray-700 flex items-center gap-1.5"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add Layer
                    </button>
                    <button
                      onClick={() => setOverlayCanvasOpen((prev) => !prev)}
                      className={`text-xs px-3 py-1.5 rounded-lg border flex items-center gap-1.5 ${
                        overlayCanvasOpen
                          ? "bg-blue-500/15 text-blue-300 border-blue-500/30"
                          : "bg-gray-800 hover:bg-gray-700 text-gray-300 border-gray-700"
                      }`}
                    >
                      <Edit3 className="h-3.5 w-3.5" /> {overlayCanvasOpen ? "Hide Canvas" : "Edit Canvas"}
                    </button>
                    <button 
                      onClick={() => setActiveConfigTab("graphics")}
                      className="text-gray-500 hover:text-gray-300"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                <div className="p-4 sm:p-5 space-y-5">
                  {overlayCanvasOpen && (
                    <div className="overlay-canvas-shell">
                      <div className="overlay-canvas-header">
                        <div>
                          <div className="overlay-canvas-title">Overlay Canvas</div>
                          <div className="overlay-canvas-subtitle">Drag or resize logo thumbnails to update master live.</div>
                        </div>
                        <div className="text-xs text-gray-500">1920 x 1080 layout space</div>
                      </div>
                      <div
                        ref={overlayCanvasRef}
                        className="overlay-canvas-stage"
                        style={{ width: overlayCanvasPreviewWidth, height: overlayCanvasPreviewHeight }}
                      >
                        <div className="overlay-canvas-safezone" />
                        {overlayLayers.filter((item) => item.logo && item.enabled).map((item) => {
                          const active = overlayCanvasSelection.type === item.type && overlayCanvasSelection.index === item.index;
                          const displayWidth = Math.max(42, (item.w > 0 ? item.w : 220) * overlayCanvasScale);
                          const displayHeight = Math.max(22, (item.h > 0 ? item.h : 80) * overlayCanvasScale);
                          return (
                            <button
                              key={item.key}
                              type="button"
                              onMouseDown={(event) => startOverlayDrag(event, { type: item.type, index: item.index })}
                              onClick={() => setOverlayCanvasSelection({ type: item.type, index: item.index })}
                              className={`overlay-canvas-object ${active ? "overlay-canvas-object-active" : ""}`}
                              style={{
                                left: item.x * overlayCanvasScale,
                                top: item.y * overlayCanvasScale,
                                width: displayWidth,
                                minHeight: displayHeight,
                              }}
                              title={`${item.label}: ${logoFileLabel(item.logo)}`}
                            >
                              <img
                                src={logoPreviewSrc(item.logo)}
                                alt={logoFileLabel(item.logo)}
                                className="overlay-canvas-image"
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                              />
                              <span className="overlay-canvas-chip">{item.label}</span>
                              <span
                                className="overlay-canvas-resize"
                                onMouseDown={(event) => startOverlayResize(event, { type: item.type, index: item.index })}
                              />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    <div className={UI.label}>Overlay Layers</div>

                    <div className="overlay-layer-row">
                      <div className="overlay-layer-badge">Main</div>
                      <div className="relative overlay-layer-select">
                        <select
                          className={`${UI.input} text-sm appearance-none pr-10`}
                          value={f.overlay_logo || ""}
                          onChange={(e) => {
                            setMainOverlayLogo(ch, e.target.value);
                            setOverlayCanvasSelection({ type: "main", index: null });
                          }}
                        >
                          <option value="">Disabled</option>
                          {logoOptions.map((l) => <option key={l} value={`/home/FTP/loop/logo/${l}`}>{l}</option>)}
                        </select>
                        <div className="absolute right-3 top-3 pointer-events-none">
                          {f.overlay_logo ? (
                            <img
                              src={logoPreviewSrc(f.overlay_logo)}
                              alt="Selected logo"
                              className="h-5 w-5 object-contain"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          ) : (
                            <Film className="h-5 w-5 text-gray-500" />
                          )}
                        </div>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => uploadLogo(e, ch)}
                        className="hidden"
                        id={`logo-upload-${ch}`}
                      />
                      <label
                        htmlFor={`logo-upload-${ch}`}
                        className="overlay-layer-upload"
                        title="Upload custom logo"
                      >
                        <Upload className="h-4 w-4" />
                      </label>
                      <input
                        type="number"
                        className={`${UI.input} overlay-layer-coord`}
                        placeholder="X"
                        value={f.logo_x}
                        onChange={(e) => updateField(ch, "logo_x", e.target.value)}
                        title="X position"
                      />
                      <input
                        type="number"
                        className={`${UI.input} overlay-layer-coord`}
                        placeholder="Y"
                        value={f.logo_y}
                        onChange={(e) => updateField(ch, "logo_y", e.target.value)}
                        title="Y position"
                      />
                      <input
                        type="number"
                        className={`${UI.input} overlay-layer-coord`}
                        placeholder="W"
                        value={f.logo_width}
                        onChange={(e) => setMainLogoSize("w", e.target.value)}
                        title="Width"
                      />
                      <button
                        type="button"
                        onClick={() => toggleOverlaySizeLock("main")}
                        className={`overlay-size-lock ${isOverlaySizeLocked("main") ? "overlay-size-lock-on" : ""}`}
                        title={isOverlaySizeLocked("main") ? "Unlock proportional resize" : "Lock proportional resize"}
                      >
                        <Lock className="h-3.5 w-3.5" />
                      </button>
                      <input
                        type="number"
                        className={`${UI.input} overlay-layer-coord`}
                        placeholder="H"
                        value={f.logo_height}
                        onChange={(e) => setMainLogoSize("h", e.target.value)}
                        title="Height"
                      />
                      <button
                        onClick={() => toggleMainOverlay(ch)}
                        className={f.overlay_logo && f.overlay_logo_enabled !== false ? "overlay-layer-stop" : "overlay-layer-play"}
                      >
                        {f.overlay_logo && f.overlay_logo_enabled !== false ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        {f.overlay_logo && f.overlay_logo_enabled !== false ? "Stop" : "Play"}
                      </button>
                    </div>

                    {(f.additional_logos || []).map((row, idx) => (
                      <div key={idx} className="overlay-layer-row overlay-layer-row-additional">
                        <button
                          type="button"
                          onClick={() => setOverlayCanvasSelection({ type: "layer", index: idx })}
                          className={`overlay-layer-badge ${overlayCanvasSelection.type === "layer" && overlayCanvasSelection.index === idx ? "overlay-layer-badge-active" : ""}`}
                        >
                          L{idx + 1}
                        </button>
                        <div className="relative overlay-layer-select">
                          <select
                            className={`${UI.input} text-sm appearance-none pr-10`}
                            value={row.logo || ""}
                            onChange={(e) => {
                              setAdditionalLogoPath(ch, idx, e.target.value);
                              setOverlayCanvasSelection({ type: "layer", index: idx });
                          }}
                        >
                          <option value="">Disabled</option>
                            {logoOptions.map((l) => <option key={l} value={`/home/FTP/loop/logo/${l}`}>{l}</option>)}
                          </select>
                          <div className="absolute right-3 top-2.5 pointer-events-none">
                            {row.logo ? (
                              <img
                                src={logoPreviewSrc(row.logo)}
                                alt="Selected logo"
                                className="h-5 w-5 object-contain"
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                              />
                            ) : (
                              <Film className="h-5 w-5 text-gray-500" />
                            )}
                          </div>
                        </div>
                        <input
                          type="number"
                          className={`${UI.input} overlay-layer-coord`}
                          placeholder="X"
                          value={row.x}
                          onChange={(e) => updateAdditionalLogo(ch, idx, "x", e.target.value)}
                        />
                        <input
                          type="number"
                          className={`${UI.input} overlay-layer-coord`}
                          placeholder="Y"
                          value={row.y}
                          onChange={(e) => updateAdditionalLogo(ch, idx, "y", e.target.value)}
                        />
                        <input
                          type="number"
                          className={`${UI.input} overlay-layer-coord`}
                          placeholder="W"
                          value={row.w || 0}
                          onChange={(e) => setAdditionalLogoSize(ch, idx, "w", e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => toggleOverlaySizeLock("layer", idx)}
                          className={`overlay-size-lock ${isOverlaySizeLocked("layer", idx) ? "overlay-size-lock-on" : ""}`}
                          title={isOverlaySizeLocked("layer", idx) ? "Unlock proportional resize" : "Lock proportional resize"}
                        >
                          <Lock className="h-3.5 w-3.5" />
                        </button>
                        <input
                          type="number"
                          className={`${UI.input} overlay-layer-coord`}
                          placeholder="H"
                          value={row.h || 0}
                          onChange={(e) => setAdditionalLogoSize(ch, idx, "h", e.target.value)}
                        />
                        <button
                          onClick={() => toggleAdditionalOverlay(ch, idx)}
                          className={row.logo && row.enabled !== false ? "overlay-layer-stop" : "overlay-layer-play"}
                        >
                          {row.logo && row.enabled !== false ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                          {row.logo && row.enabled !== false ? "Stop" : "Play"}
                        </button>
                        <button
                          onClick={() => removeAdditionalLogo(ch, idx)}
                          className="overlay-layer-remove"
                          title="Remove this layer"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}

                    {(f.additional_logos || []).length === 0 && (
                      <div className="text-xs text-gray-600 italic text-center py-3 border border-dashed border-gray-800 rounded-xl">
                        No additional layers configured
                      </div>
                    )}
                  </div>

                  {/* Ticker */}
                  <div className="border-t border-gray-800 pt-4">
                    <div className="flex items-center justify-between gap-3 mb-3 mobile-stack">
                      <div className={UI.label}>Scrolling Ticker</div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => updateScroll(ch)} className="px-3 py-2 rounded-xl bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border border-emerald-500/20 transition">
                          <span className="inline-flex items-center gap-2"><Play className="h-4 w-4" /> Publish</span>
                        </button>
                        <button onClick={() => { updateField(ch, "scroll_text", ""); updateScroll(ch); }} className="px-3 py-2 rounded-xl bg-red-600/20 hover:bg-red-600/30 text-red-300 border border-red-500/20 transition">
                          <span className="inline-flex items-center gap-2"><Square className="h-4 w-4" /> Stop</span>
                        </button>
                      </div>
                    </div>
                    <textarea 
                      rows={3} 
                      className={`${UI.input} resize-none mb-3 text-sm`} 
                      placeholder="Enter scrolling text..." 
                      value={f.scroll_text || ""} 
                      onChange={(e) => updateField(ch, "scroll_text", e.target.value)} 
                    />
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Font Size</div>
                        <input className={UI.input} type="number" value={f.scroll_font_size} onChange={(e) => updateField(ch, "scroll_font_size", e.target.value)} />
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Speed</div>
                        <input className={UI.input} type="number" value={f.scroll_speed} onChange={(e) => updateField(ch, "scroll_speed", e.target.value)} />
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Text Color</div>
                        <input className={UI.input} type="text" value={f.scroll_font_color} onChange={(e) => updateField(ch, "scroll_font_color", e.target.value)} />
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">BG Color</div>
                        <input className={UI.input} type="text" value={f.scroll_bg_color} onChange={(e) => updateField(ch, "scroll_bg_color", e.target.value)} />
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-3 mobile-stack">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="scrollBg"
                          checked={f.scroll_enable_bg}
                          onChange={(e) => updateField(ch, "scroll_enable_bg", e.target.checked)}
                          className="rounded border-gray-600"
                        />
                        <label htmlFor="scrollBg" className="text-sm text-gray-300">Background</label>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Opacity:</span>
                        <input 
                          type="range" 
                          min="0" 
                          max="1" 
                          step="0.05"
                          value={f.scroll_bg_alpha || 0.65}
                          onChange={(e) => updateField(ch, "scroll_bg_alpha", parseFloat(e.target.value))}
                          className="w-24"
                        />
                        <span className="text-xs text-gray-500 w-8">{(f.scroll_bg_alpha || 0.65).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* DESTINATIONS - FIXED OUTPUTS */}
            {showDestinationsSection && activeConfigTab === "destinations" && (
              <div className={UI.card}>
                <div className="px-4 sm:px-5 py-4 border-b border-gray-800 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Globe className="h-5 w-5 text-blue-400" />
                    <h3 className="text-sm font-bold text-white">Broadcast Destinations</h3>
                  </div>
                  <button 
                    onClick={() => setActiveConfigTab("destinations")}
                    className="text-gray-500 hover:text-gray-300"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                </div>
                <div className="p-4 sm:p-5 space-y-6">
                  {/* Facebook */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-blue-400 font-semibold">
                        <Facebook className="h-4 w-4" /> Facebook Live
                      </div>
                      <span className={`text-xs ${safeBool(st?.facebook?.running) ? "text-emerald-400" : "text-gray-600"}`}>
                        {safeBool(st?.facebook?.running) ? "ON AIR" : "OFF"}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <input 
                        type="password" 
                        className={UI.input} 
                        placeholder="Facebook Stream Key" 
                        value={f.facebook_key || ""} 
                        onChange={(e) => updateField(ch, "facebook_key", e.target.value)} 
                      />
                      <button 
                        onClick={() => toggleStream(ch, "facebook")} 
                        className={`p-3 rounded-xl min-w-[60px] flex items-center justify-center ${safeBool(st?.facebook?.running) ? UI.btnDanger : UI.btnNeutral}`}
                      >
                        {safeBool(st?.facebook?.running) ? 
                          <Square className="h-4 w-4 fill-current" /> : 
                          <Play className="h-4 w-4 fill-current" />
                        }
                      </button>
                    </div>
                  </div>

                  {/* YouTube - FIXED */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-red-400 font-semibold">
                        <Youtube className="h-4 w-4" /> YouTube Live
                      </div>
                      <span className={`text-xs ${safeBool(st?.youtube?.running) ? "text-emerald-400" : "text-gray-600"}`}>
                        {safeBool(st?.youtube?.running) ? "ON AIR" : "OFF"}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <input 
                        type="password" 
                        className={UI.input} 
                        placeholder="YouTube Stream Key" 
                        value={f.youtube_key || ""} 
                        onChange={(e) => updateField(ch, "youtube_key", e.target.value)} 
                      />
                      <button 
                        onClick={() => toggleStream(ch, "youtube")} 
                        className={`p-3 rounded-xl min-w-[60px] flex items-center justify-center ${safeBool(st?.youtube?.running) ? UI.btnDanger : UI.btnNeutral}`}
                      >
                        {safeBool(st?.youtube?.running) ? 
                          <Square className="h-4 w-4 fill-current" /> : 
                          <Play className="h-4 w-4 fill-current" />
                        }
                      </button>
                    </div>
                    <div className="text-xs text-gray-500">
                      YouTube RTMP URL: rtmp://a.rtmp.youtube.com/live2/[stream-key]
                    </div>
                  </div>

                  {/* Custom Output - Fixed for RTMP, SRT, UDP */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-purple-400 font-semibold">
                        <Link2 className="h-4 w-4" /> Custom Output
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setCustomDetailOpen((prev) => ({ ...prev, [ch]: !prev[ch] }))}
                          className="text-xs px-2.5 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 inline-flex items-center gap-1.5"
                          title="Edit multicast/cable technical settings"
                        >
                          <Edit3 className="h-3.5 w-3.5" /> Details
                        </button>
                        <span className={`text-xs ${safeBool(st?.custom?.running) ? "text-emerald-400" : "text-gray-600"}`}>
                          {safeBool(st?.custom?.running) ? "ON AIR" : "OFF"}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <input
                          className={`${UI.input} font-mono text-sm`}
                          placeholder="rtmp://... | srt://... | udp://..."
                          value={f.custom_url || ""}
                          onChange={(e) => updateField(ch, "custom_url", e.target.value)}
                        />
                        <button
                          onClick={() => toggleStream(ch, "custom")}
                          className={`p-3 rounded-xl min-w-[60px] flex items-center justify-center ${safeBool(st?.custom?.running) ? UI.btnDanger : UI.btnNeutral}`}
                          title={safeBool(st?.custom?.running) ? "Stop" : "Start"}
                        >
                          {safeBool(st?.custom?.running) ? (
                            <Square className="h-4 w-4 fill-current" />
                          ) : (
                            <Play className="h-4 w-4 fill-current" />
                          )}
                        </button>
                      </div>

                      {/* Protocol-specific settings */}
                      {(String(f.custom_url || "").toLowerCase().startsWith("udp://")) && (
                        <div className="bg-gray-900/30 p-4 rounded-xl border border-gray-800 space-y-3">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <div className="text-sm font-medium text-gray-300">UDP / Multicast Cable Settings</div>
                              <div className="text-xs text-gray-500">MPEG-TS service, PID, mux, packet and table timing controls.</div>
                            </div>
                            <span className="text-[10px] uppercase tracking-[0.2em] text-purple-300">Headend</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
                            <div>
                              <div className="text-xs text-gray-400 mb-1.5">UDP Profile</div>
                              <select
                                className={UI.input}
                                value={f.udp_profile || "mpeg2"}
                                onChange={(e) => applyUdpProfile(ch, e.target.value)}
                              >
                                <option value="mpeg2">MPEG-2 (Cable SD 720x576)</option>
                                <option value="mpeg4">MPEG-4 (HD H.264)</option>
                              </select>
                            </div>
                            <div>
                              <div className="text-xs text-gray-400 mb-1.5">Rate Control</div>
                              <select
                                className={UI.input}
                                value={f.udp_rate_control || "cbr"}
                                onChange={(e) => updateField(ch, "udp_rate_control", e.target.value)}
                              >
                                <option value="cbr">CBR</option>
                                <option value="vbr">VBR</option>
                              </select>
                            </div>
                            <div>
                              <div className="text-xs text-gray-400 mb-1.5">Video Bitrate</div>
                              <input
                                className={UI.input}
                                placeholder="4000k"
                                value={f.udp_video_bitrate || ""}
                                onChange={(e) => updateField(ch, "udp_video_bitrate", e.target.value.replace(/[^\dkKmM]/g, ""))}
                              />
                            </div>
                            <div>
                              <div className="text-xs text-gray-400 mb-1.5">Audio Bitrate</div>
                              <input
                                className={UI.input}
                                placeholder="192k"
                                value={f.udp_audio_bitrate || ""}
                                onChange={(e) => updateField(ch, "udp_audio_bitrate", e.target.value.replace(/[^\dkKmM]/g, ""))}
                              />
                            </div>
                            <div>
                              <div className="text-xs text-gray-400 mb-1.5">TS Muxrate</div>
                              <input
                                className={UI.input}
                                placeholder="e.g. 5500k"
                                value={f.udp_muxrate || ""}
                                onChange={(e) =>
                                  updateField(ch, "udp_muxrate", e.target.value.replace(/[^\dkKmM]/g, ""))
                                }
                              />
                            </div>
                            <div>
                              <div className="text-xs text-gray-400 mb-1.5">Packet Size</div>
                              <input className={UI.input} placeholder="1316" value={f.udp_pkt_size || ""} onChange={(e) => updateField(ch, "udp_pkt_size", e.target.value.replace(/[^\d]/g, ""))} />
                            </div>
                          </div>
                          {customDetailOpen[ch] && (
                            <div className="space-y-4 pt-3 border-t border-gray-800">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[
                                  ["Minrate", "udp_minrate", "4000k"],
                                  ["Maxrate", "udp_maxrate", "4000k"],
                                  ["VBV Buffer", "udp_video_bufsize", "1835k"],
                                  ["B-Frames", "udp_bframes", "2"],
                                  ["GOP / I-Frame", "udp_gop_size", "12"],
                                  ["Min I-Frame", "udp_keyint_min", "12"],
                                ].map(([label, key, placeholder]) => (
                                  <div key={key}>
                                    <div className="text-xs text-gray-400 mb-1.5">{label}</div>
                                    <input
                                      className={UI.input}
                                      placeholder={placeholder}
                                      value={f[key] || ""}
                                      onChange={(e) => updateField(
                                        ch,
                                        key,
                                        key.includes("rate") || key.includes("bufsize")
                                          ? e.target.value.replace(/[^\dkKmM]/g, "")
                                          : e.target.value.replace(/[^\d]/g, "")
                                      )}
                                    />
                                  </div>
                                ))}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <div className="text-xs text-gray-400 mb-1.5">Service Name</div>
                                  <input className={UI.input} placeholder="Channel service name" value={f.udp_service_name || ""} onChange={(e) => updateField(ch, "udp_service_name", e.target.value)} />
                                </div>
                                <div>
                                  <div className="text-xs text-gray-400 mb-1.5">Service Provider</div>
                                  <input className={UI.input} placeholder="Provider name" value={f.udp_service_provider || ""} onChange={(e) => updateField(ch, "udp_service_provider", e.target.value)} />
                                </div>
                              </div>

                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[
                                  ["Service ID", "udp_service_id", "1"],
                                  ["TS ID", "udp_transport_stream_id", "1"],
                                  ["Original Network ID", "udp_original_network_id", "1"],
                                  ["PMT PID", "udp_pmt_pid", "4096"],
                                  ["Start PID", "udp_start_pid", "256"],
                                  ["Video PID", "udp_video_pid", "256"],
                                  ["Audio PID", "udp_audio_pid", "257"],
                                  ["TTL", "udp_ttl", "255"],
                                ].map(([label, key, placeholder]) => (
                                  <div key={key}>
                                    <div className="text-xs text-gray-400 mb-1.5">{label}</div>
                                    <input className={UI.input} placeholder={placeholder} value={f[key] || ""} onChange={(e) => updateField(ch, key, e.target.value.replace(/[^\d]/g, ""))} />
                                  </div>
                                ))}
                              </div>

                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[
                                  ["PCR Period ms", "udp_pcr_period", "40"],
                                  ["PAT Period sec", "udp_pat_period", "0.1"],
                                  ["SDT Period sec", "udp_sdt_period", "0.5"],
                                  ["Buffer Size", "udp_buffer_size", "9388608"],
                                  ["FIFO Size", "udp_fifo_size", "1000000"],
                                ].map(([label, key, placeholder]) => (
                                  <div key={key}>
                                    <div className="text-xs text-gray-400 mb-1.5">{label}</div>
                                    <input className={UI.input} placeholder={placeholder} value={f[key] || ""} onChange={(e) => updateField(ch, key, key.includes("period") ? e.target.value.replace(/[^\d.]/g, "") : e.target.value.replace(/[^\d]/g, ""))} />
                                  </div>
                                ))}
                                <label className="flex items-center gap-2 rounded-xl border border-gray-800 bg-gray-950/40 px-3 py-2 text-xs text-gray-300">
                                  <input type="checkbox" checked={!!f.udp_resend_headers} onChange={(e) => updateField(ch, "udp_resend_headers", e.target.checked)} />
                                  Resend Headers
                                </label>
                                <label className="flex items-center gap-2 rounded-xl border border-gray-800 bg-gray-950/40 px-3 py-2 text-xs text-gray-300">
                                  <input type="checkbox" checked={!!f.udp_system_b} onChange={(e) => updateField(ch, "udp_system_b", e.target.checked)} />
                                  DVB System B
                                </label>
                              </div>
                            </div>
                          )}
                          <div className="text-xs text-gray-500">
                            UDP output to: {f.custom_url}
                          </div>
                          <div className="text-xs text-amber-300/90">
                            Cable note: MPEG-2 default is 720x576p25, 16:9 SAR, 4:2:0, MP2 audio, GOP 12, B-frames 2, VBV 1835k, and 4 Mbps video with 5500k TS muxrate for headend ingest.
                          </div>
                          <div className="text-xs text-gray-500">
                            Live UDP/MPEG-2 now prefers the direct GST TCP handoff when available, bypassing the RTMP middle hop to reduce cable-headend jitter while still using the long 20-second probe/analyze window.
                          </div>
                        </div>
                      )}

                      {/* SRT-specific indicator */}
                      {(String(f.custom_url || "").toLowerCase().startsWith("srt://")) && (
                        <div className="bg-blue-900/10 p-4 rounded-xl border border-blue-800/30">
                          <div className="text-sm font-medium text-blue-300">SRT Output Active</div>
                          <div className="text-xs text-blue-400 mt-1">
                            SRT streaming to: {f.custom_url}
                          </div>
                          <div className="text-xs text-gray-500 mt-2">
                            SRT mode: Listen  |  Latency: 2000ms
                          </div>
                        </div>
                      )}

                      {/* RTMP-specific indicator */}
                      {(String(f.custom_url || "").toLowerCase().startsWith("rtmp://") || 
                        String(f.custom_url || "").toLowerCase().startsWith("rtmps://")) && (
                        <div className="bg-emerald-900/10 p-4 rounded-xl border border-emerald-800/30">
                          <div className="text-sm font-medium text-emerald-300">RTMP Output Active</div>
                          <div className="text-xs text-emerald-400 mt-1">
                            RTMP streaming to: {f.custom_url}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800 bg-gray-900/50 py-4 px-4 sm:px-6">
        <div className="max-w-[1920px] mx-auto flex flex-col md:flex-row items-center justify-between text-xs text-gray-500 gap-3">
          <div className="flex items-center gap-4 flex-wrap">
            <span>© 2026 ProStream Transcoder v5.1</span>
            <span className="h-1 w-1 rounded-full bg-gray-600" />
            <span>Auto-Restore Enabled</span>
            <span className="mobile-hidden">Health Monitoring Active</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5">
              <Globe className="h-3 w-3" />
              Status: 
              <span className={`font-mono ${healthStatus.status === "ok" ? "text-emerald-400" : "text-amber-400"}`}>
                {healthStatus.status === "ok" ? "Operational" : "Warning"}
              </span>
            </span>
            <span className="h-3 w-[1px] bg-gray-700" />
            <span className="text-gray-600">Active: {activeCount} streams</span>
            <span className="h-3 w-[1px] bg-gray-700" />
            <span className="text-gray-600">Channels: {Object.keys(channelHealth).length}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
